import * as THREE from './three.module.js';

// Feature flag: rain puddles that make the player dizzy (slide to another
// lane, slippery steering, or inverted left/right). Set to false to turn the
// whole feature off -- nothing spawns, builds or updates.
const PUDDLES_ENABLED = true;

// TESTING switch: true makes every character playable regardless of the "Unlock rule" blocks, so
// they can all be tried, except the ones listed in KEEP_LOCKED_WHILE_TESTING, which keep their rule.
// Set to false to enforce all unlock rules again.
const UNLOCK_ALL_CHARACTERS_FOR_TESTING = false;
const KEEP_LOCKED_WHILE_TESTING = ['topgaard', 'baloo'];

const LANE_WIDTH = 2.2;
const LANE_X = [-LANE_WIDTH, 0, LANE_WIDTH];
// Cobbled carriageway (the three lanes plus a margin), flanked by a
// pavement on each side before the building fronts (see BUILDING_* below).
const ROAD_WIDTH = LANE_WIDTH * 3 + 1.5;
const SIDEWALK_WIDTH = 1.5;
const GROUND_WIDTH = ROAD_WIDTH + SIDEWALK_WIDTH * 2;
const GROUND_LENGTH = 400;
// World length of one repeat of the ground texture.
const GROUND_TILE_LENGTH = 10;
const GROUND_PIXELS_PER_UNIT = 16;

// Scene lights (see `buildScene`). Directions point from the light toward
// the scene origin. The back light sits behind and above the camera, so it
// softly lights everything the player is looking at -- the character's back,
// the near faces of obstacles, the road -- without adding anything to the
// sides of things (so there's still shape from the sun).
let AMBIENT_LIGHT_INTENSITY = 0.55;
let SUN_LIGHT_INTENSITY = 0.45;
let SUN_LIGHT_POSITION = [-4, 8, 4];
let BACK_LIGHT_INTENSITY = 1.1;
let BACK_LIGHT_POSITION = [0, 6, 8];
let BACK_LIGHT_COLOR = 0xfff3e0;

/**
 * Fraction of a texture's color a Lambert surface facing `normal` ends up
 * showing under the lights above. This three.js build uses physically-based
 * light units, where Lambert divides each light's intensity by PI -- so the
 * nominal intensities above deliver far less than they look like they would.
 */
function lambertLightLevel(normal) {
  const n = new THREE.Vector3(...normal).normalize();
  const directional = (position, intensity) =>
    intensity * Math.max(0, n.dot(new THREE.Vector3(...position).normalize()));
  return (
    (AMBIENT_LIGHT_INTENSITY +
      directional(SUN_LIGHT_POSITION, SUN_LIGHT_INTENSITY) +
      directional(BACK_LIGHT_POSITION, BACK_LIGHT_INTENSITY)) /
    Math.PI
  );
}

// --- Theme ------------------------------------------------------------------
// Everything a season can restyle. The values here are the everyday look and
// are also the fallback for anything a theme leaves out; `applyTheme` swaps
// them in one go before the game builds anything (see
// `assets/runner-game-themes.js` for the themes themselves).
let AMBIENT_LIGHT_COLOR = 0xffffff;
let SUN_LIGHT_COLOR = 0xffffff;
let SKY_CLOUD_COLOR = '#ffffff';
let SKY_STARS = null;
let SKY_MOON = null;
let SKY_BATS = null;
let GROUND_PALETTE = {
  mortar: '#8f887b',
  stones: ['#d9d3c5', '#cfc8b9', '#dfd9cc', '#c8c1b1', '#d4cdbf'],
  stoneHighlight: '#e7e2d7',
  stoneShade: '#b7b0a1',
  pavementBase: '#a8a296',
  slabs: ['#e2ded4', '#d9d4c8', '#e6e2d9', '#d4cfc2'],
  slabHighlight: '#f0ede6',
  slabShade: '#c6c0b3',
  curb: ['#8e897f', '#a7a298', '#77726a'],
  gutter: ['#9d9689', '#b3ac9f', '#86807a'],
  laneSett: ['#7f786d', '#958e82', '#68625a'],
};
let BUILDING_GLASS_COLOR = '#2d3a48';
let BUILDING_GLINT_COLOR = '#56687c';
let BUILDING_FRAME_COLOR = '#f4f1ea';
let BUILDING_LIT_WINDOW_CHANCE = 0;
let BUILDING_LIT_WINDOW_COLORS = ['#ffd98a'];
// Street props, in the order they repeat along both pavements. Filled in by
// `applyTheme`; the fallback is the everyday tree/lamp pair.
let STREET_PROPS = null;
let COBWEBS = null;
let GROUND_MIST = null;
let FOG_BANKS = null;
let FLYING_BATS = null;
// Tall obstacles are a cross-billboard by default (two planes crossed, so a
// cactus has bulk from any angle). A flat object like a headstone sets this
// false and renders as the single plane it looks like.
let TALL_OBSTACLE_CROSS = true;
let ACTIVE_THEME_ID = 'default';

/** Keeps a theme's gameplay overrides inside values the game is known to stay fair at. */
const clampTheme = (value, min, max, fallback) =>
  typeof value === 'number' && Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback;

/**
 * Applies a theme: visuals wholesale, gameplay within bounds. Called once,
 * before the game is constructed, so everything built afterwards (textures,
 * geometry, baked light levels) comes out in that theme. `assets` is the
 * section's asset-url map, which theme asset keys are looked up in.
 */
function applyTheme(theme, assets = {}) {
  if (!theme) return;
  ACTIVE_THEME_ID = theme.id ?? ACTIVE_THEME_ID;

  const lights = theme.lights ?? {};
  AMBIENT_LIGHT_INTENSITY = lights.ambient ?? AMBIENT_LIGHT_INTENSITY;
  AMBIENT_LIGHT_COLOR = lights.ambientColor ?? AMBIENT_LIGHT_COLOR;
  SUN_LIGHT_INTENSITY = lights.sunIntensity ?? SUN_LIGHT_INTENSITY;
  SUN_LIGHT_COLOR = lights.sunColor ?? SUN_LIGHT_COLOR;
  SUN_LIGHT_POSITION = lights.sunPosition ?? SUN_LIGHT_POSITION;
  BACK_LIGHT_INTENSITY = lights.backIntensity ?? BACK_LIGHT_INTENSITY;
  BACK_LIGHT_COLOR = lights.backColor ?? BACK_LIGHT_COLOR;
  BACK_LIGHT_POSITION = lights.backPosition ?? BACK_LIGHT_POSITION;
  // Brightness multipliers are derived from the lights, so they have to be
  // recomputed here rather than at import -- otherwise a night theme would
  // light its buses and houses as if it were still daytime.
  BUS_NEAR_END_BRIGHTNESS = BUS_TARGET_LIGHT_LEVEL / lambertLightLevel([0, 0, 1]);
  BUS_FAR_END_BRIGHTNESS = BUS_TARGET_LIGHT_LEVEL / lambertLightLevel([0, 0, -1]);
  BUS_SIDE_BRIGHTNESS = BUS_TARGET_LIGHT_LEVEL / lambertLightLevel([-1, 0, 0]);
  BUS_ROOF_BRIGHTNESS = BUS_TARGET_LIGHT_LEVEL / lambertLightLevel([0, 1, 0]);

  const sky = theme.sky ?? {};
  SKY_HORIZON_COLOR = sky.horizon ?? SKY_HORIZON_COLOR;
  SKY_ZENITH_COLOR = sky.zenith ?? SKY_ZENITH_COLOR;
  SKY_CLOUDS = sky.clouds ?? SKY_CLOUDS;
  SKY_CLOUD_COLOR = sky.cloudColor ?? SKY_CLOUD_COLOR;
  SKY_CLOUD_DRIFT = sky.drift ?? SKY_CLOUD_DRIFT;
  SKY_STARS = sky.stars ?? null;
  SKY_MOON = sky.moon ?? null;
  SKY_BATS = sky.bats ?? null;

  const fog = theme.fog ?? {};
  FOG_NEAR = fog.near ?? FOG_NEAR;
  FOG_FAR = fog.far ?? FOG_FAR;
  // The building rows run out to the fog's far edge, so they follow it.
  BUILDING_ROW_FAR_Z = -FOG_FAR;

  const storm = theme.storm ?? {};
  STORM_SKY_TINT = storm.skyTint ?? STORM_SKY_TINT;
  STORM_FOG_COLOR = storm.fogColor ?? STORM_FOG_COLOR;

  if (theme.ground) GROUND_PALETTE = { ...GROUND_PALETTE, ...theme.ground };

  const buildings = theme.buildings ?? {};
  BUILDING_COLORFUL_SHARE = buildings.colorfulShare ?? BUILDING_COLORFUL_SHARE;
  BUILDING_COLORFUL_COUNT = buildings.colorfulCount ?? BUILDING_COLORFUL_COUNT;
  BUILDING_WALL_COLORS = buildings.wallColors ?? BUILDING_WALL_COLORS;
  BUILDING_ROOF_COLORS = buildings.roofColors ?? BUILDING_ROOF_COLORS;
  BUILDING_DOOR_COLORS = buildings.doorColors ?? BUILDING_DOOR_COLORS;
  BUILDING_CHIMNEY_COLOR = buildings.chimneyColor ?? BUILDING_CHIMNEY_COLOR;
  BUILDING_GLASS_COLOR = buildings.glassColor ?? BUILDING_GLASS_COLOR;
  BUILDING_GLINT_COLOR = buildings.glintColor ?? BUILDING_GLINT_COLOR;
  BUILDING_FRAME_COLOR = buildings.frameColor ?? BUILDING_FRAME_COLOR;
  BUILDING_LIT_WINDOW_CHANCE = buildings.litWindowChance ?? BUILDING_LIT_WINDOW_CHANCE;
  BUILDING_LIT_WINDOW_COLORS = buildings.litWindowColors ?? BUILDING_LIT_WINDOW_COLORS;

  if (theme.props) STREET_PROPS = theme.props;
  COBWEBS = theme.cobwebs ?? null;
  GROUND_MIST = theme.groundMist ?? null;
  FOG_BANKS = theme.fogBanks ?? null;
  FLYING_BATS = theme.flyingBats ?? null;
  if (FLYING_BATS?.sheetAsset && assets[FLYING_BATS.sheetAsset]) {
    FLYING_BATS = { ...FLYING_BATS, sheet: assets[FLYING_BATS.sheetAsset] };
  }

  // Obstacle re-skins. The art swaps, the gameplay shape follows it: a wider
  // sprite is wider to hit. Anything a theme doesn't state is recomputed from
  // the art's own aspect, so a re-skin can't quietly keep the old hitbox.
  const tall = theme.obstacles?.tall;
  if (tall) {
    if (tall.asset && assets[tall.asset]) TALL_OBSTACLE_URL = assets[tall.asset];
    if (tall.aspect) TALL_OBSTACLE_ASPECT = tall.aspect;
    TALL_OBSTACLE_CROSS = tall.crossBillboard ?? TALL_OBSTACLE_CROSS;
    const width = TALL_OBSTACLE_HEIGHT * TALL_OBSTACLE_ASPECT;
    TALL_OBSTACLE_HIT_HALF_WIDTH = tall.hitHalfWidth ?? width / 2 + 0.2;
    // A flat slab is shallow whatever its width, so themes can say so.
    TALL_OBSTACLE_HIT_HALF_DEPTH = tall.hitHalfDepth ?? width / 2 + 0.1;
    TALL_OBSTACLE_CLEAR_HEIGHT = tall.clearHeight ?? TALL_OBSTACLE_CLEAR_HEIGHT;
    Object.assign(OBSTACLE_DEFS.tall, {
      clearHeight: TALL_OBSTACLE_CLEAR_HEIGHT,
      hitHalfWidth: TALL_OBSTACLE_HIT_HALF_WIDTH,
      hitHalfDepth: TALL_OBSTACLE_HIT_HALF_DEPTH,
    });
  }

  if (theme.assets?.music && assets[theme.assets.music]) MUSIC_SOUND_URL = assets[theme.assets.music];

  // Gameplay. Clamped to ranges the game has been play-tested at: fast enough
  // to hurt, not so fast that obstacles stop being jumpable (see the collision
  // and jump-window checks).
  const play = theme.gameplay ?? {};
  MAX_SPEED = clampTheme(play.maxSpeed, BASE_SPEED, 22, MAX_SPEED);
  SPAWN_INTERVAL_SECONDS_MIN = clampTheme(play.spawnIntervalMin, 0.2, 1, SPAWN_INTERVAL_SECONDS_MIN);
  DOUBLE_PILLAR_CHANCE_MAX = clampTheme(play.doublePillarChanceMax, 0, 0.85, DOUBLE_PILLAR_CHANCE_MAX);
  TARGETED_LANE_CHANCE_MAX = clampTheme(play.targetedLaneChanceMax, 0, 0.9, TARGETED_LANE_CHANCE_MAX);
  WALL_MIN_DIFFICULTY = clampTheme(play.wallMinDifficulty, 0.2, 1, WALL_MIN_DIFFICULTY);
  WALL_CHANCE_MAX = clampTheme(play.wallChanceMax, 0, 0.45, WALL_CHANCE_MAX);
  BUS_WAVE_CHANCE_MAX = clampTheme(play.busWaveChanceMax, 0, 0.4, BUS_WAVE_CHANCE_MAX);
  RIDER_WAVE_CHANCE_MAX = clampTheme(play.riderWaveChanceMax, 0, 0.4, RIDER_WAVE_CHANCE_MAX);
  DIFFICULTY_DISTANCE_SCALE = clampTheme(play.difficultyDistanceScale, 300, 1200, DIFFICULTY_DISTANCE_SCALE);
}

// Character: a single lit plane whose texture is picked each frame from a
// real production voxel-sprite crop. Two selectable characters (Mika, Jas)
// share this same rig; they differ in which angles/frame-counts they have.
// Mika has real BACK-LEFT / BACK / BACK-RIGHT lean art (3 angles x 4
// strides); Jas currently only has back-view art (1 angle x 5 strides) --
// `updateRunAnimation` has to pick angle/stride counts per-character rather
// than assuming Mika's shape. Jumping and getting hit also swap to real
// dedicated pose art instead of faking those with math.
// Read from the cache-busted URLs the section's inline script builds via
// Liquid's `asset_url` (see sections/game-threejs.liquid) when available,
// falling back to self-constructed (un-versioned) URLs so this file still
// works if that data island is ever missing.
const assetUrls = typeof window !== 'undefined' ? window.__runnerAssetUrls : null;
// Lazily-imported helper modules (leaderboard, analytics, city list). Uses the
// section's versioned asset URLs so each loads at the same version as the theme
// that rendered the page; the relative path is only a fallback for local use.
const moduleUrl = (name) => assetUrls?.modules?.[name] ?? new URL(`./runner-${name}.js`, import.meta.url).href;
const characterAssetUrls = assetUrls?.characters;
const fallbackAssetUrl = (name) => new URL(`./${name}.png`, import.meta.url).href;
const fallbackSoundUrl = (name) => new URL(`./${name}.mp3`, import.meta.url).href;

const MIKA_VOICE_FILES = [
  'MIKA_Badabum_1',
  'MIKA_Baloo',
  'MIKA_FilmeEnVideo',
  'MIKA_IkkeMereMaelk',
  'MIKA_Like',
  'MIKA_MikaHer',
  'MIKA_Prut_2',
  'MIKA_SesPaaSondag',
  'MIKA_SetJasmin',
  'MIKA_Velkommen',
];
const JAS_VOICE_FILES = [
  'Jasmin_Danseee',
  'Jasmin_FilmeEnVideo',
  'Jasmin_GodDagIDag',
  'JASMIN_IkTrykDer_1',
  'JASMIN_JasHer_2',
  'JASMIN_Like_2',
  'JASMIN_LystTilAtLave',
  'JASMIN_Prut',
  'JASMIN_SesPaaSondag_2',
  'Jasmin_SetBaloo',
];
const ALEX_VOICE_FILES = [
  'HUSUM_10k_1',
  'HUSUM_BBBB_1',
  'HUSUM_Gemmeleg',
  'HUSUM_Huzumz_1',
  'HUSUM_Kram_1',
  'HUSUM_Pommes',
  'HUSUM_Praaaank_2',
  'HUSUM_PrankMaskine_Kort_2',
  'HUSUM_PrankerDig',
  'HUSUM_Sulten_2',
];
const NELLIE_VOICE_FILES = [
  'NELLIE_Eventyr_2',
  'NELLIE_Hemmelighed',
  'NELLIE_KastMig',
  'NELLIE_KlarSammen',
  'NELLIE_Prut',
  'NELLIE_SesIgen',
  'NELLIE_Smiler_1',
  'NELLIE_Stjerne_2',
  'NELLIE_Vatpind',
];
const MONKEY_VOICE_FILES = [
  'monkey-1',
  'monkey-2',
  'monkey-3',
  'MONKEY_Like',
  'MONKEY_MonkeyTime',
  'MONKEY_SetBananer_2',
  'MONKEY_SetMinKrone',
  'MONKEY_SmackMig',
  'MONKEY_StikMigEnBanan',
  'MONKEY_StraekMinArm',
];
const BALOO_VOICE_FILES = [
  'BALOO_1',
  'BALOO_2',
  'BALOO_3',
  'BALOO_5',
  'BALOO_9',
  'BALOO_11',
  'BALOO_12',
  'BALOO_13',
  'BALOO_14',
  'BALOO_15',
];
const COCO_VOICE_FILES = [
  'COCO_3',
  'COCO_4',
  'COCO_5',
  'COCO_6',
  'COCO_7',
  'COCO_9',
  'COCO_11',
  'COCO_13',
  'COCO_14',
  'COCO_15',
];
const NIKI_VOICE_FILES = [
  'NIKI_Batteri',
  'NIKI_Boovs',
  'NIKI_EnGed',
  'NIKI_EnSang',
  'NIKI_HurtigsteAal',
  'NIKI_IkkeSaaHaardt',
  'NIKI_SlaaGraes',
  'NIKI_SoveEnTime',
  'NIKI_TaleneDukke',
  'NIKI_Tisser',
];
const CHARACTER_DEFS = {
  mika: {
    label: 'Mika',
    nativeScale: true, // same shaded-art handling as Jas (see below)
    run: characterAssetUrls?.mika?.run ?? {
      left: [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-run-back-left-${n}`)),
      center: [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-run-back-${n}`)),
      right: [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-run-back-right-${n}`)),
    },
    jump: characterAssetUrls?.mika?.jump ?? [1, 2].map((n) => fallbackAssetUrl(`runner-game-character-jump-${n}`)),
    injured:
      characterAssetUrls?.mika?.injured ??
      [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-injured-${n}`)),
    // Milestone voice lines (see `checkMilestone`): reuses the same buddy
    // sound bank the bendy-buddies slider uses, rather than recording new
    // dedicated game lines.
    voice: characterAssetUrls?.mika?.voice ?? MIKA_VOICE_FILES.map((name) => fallbackSoundUrl(`buddy.sound.${name}`)),
  },
  jas: {
    label: 'Jas',
    // Her frames share one pixel scale (run frames on a common canvas, jump
    // and injured at the same pixels-per-unit), so each pose sizes the player
    // plane from its own texture instead of stretching into the run box.
    nativeScale: true,
    run: characterAssetUrls?.jas?.run ?? {
      left: [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-jas-run-left-${n}`)),
      center: [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-jas-run-${n}`)),
      right: [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-jas-run-right-${n}`)),
    },
    jump:
      characterAssetUrls?.jas?.jump ?? [1, 2].map((n) => fallbackAssetUrl(`runner-game-character-jas-jump-${n}`)),
    injured:
      characterAssetUrls?.jas?.injured ??
      [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-jas-injured-${n}`)),
    voice: characterAssetUrls?.jas?.voice ?? JAS_VOICE_FILES.map((name) => fallbackSoundUrl(`buddy.sound.${name}`)),
  },
  monkey: {
    label: 'Monkey',
    nativeScale: true,
    run: characterAssetUrls?.monkey?.run ?? {
      left: [1, 2, 3, 4, 5].map((n) => fallbackAssetUrl(`runner-game-character-monkey-run-left-${n}`)),
      center: [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-monkey-run-${n}`)),
      right: [1, 2, 3, 4, 5].map((n) => fallbackAssetUrl(`runner-game-character-monkey-run-right-${n}`)),
    },
    jump:
      characterAssetUrls?.monkey?.jump ??
      [1, 2].map((n) => fallbackAssetUrl(`runner-game-character-monkey-jump-${n}`)),
    injured:
      characterAssetUrls?.monkey?.injured ??
      [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-monkey-injured-${n}`)),
    voice: characterAssetUrls?.monkey?.voice ?? MONKEY_VOICE_FILES.map((name) => fallbackSoundUrl(`buddy.sound.${name}`)),
  },
  nellie: {
    label: 'Nellie',
    nativeScale: true,
    run: characterAssetUrls?.nellie?.run ?? {
      left: [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-nellie-run-left-${n}`)),
      center: [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-nellie-run-${n}`)),
      right: [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-nellie-run-right-${n}`)),
    },
    jump:
      characterAssetUrls?.nellie?.jump ??
      [1, 2].map((n) => fallbackAssetUrl(`runner-game-character-nellie-jump-${n}`)),
    injured:
      characterAssetUrls?.nellie?.injured ??
      [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-nellie-injured-${n}`)),
    voice: characterAssetUrls?.nellie?.voice ?? NELLIE_VOICE_FILES.map((name) => fallbackSoundUrl(`buddy.sound.${name}`)),
  },
  alex: {
    label: 'Alexander',
    nativeScale: true,
    run: characterAssetUrls?.alex?.run ?? {
      left: [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-alex-run-left-${n}`)),
      center: [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-alex-run-${n}`)),
      right: [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-alex-run-right-${n}`)),
    },
    jump:
      characterAssetUrls?.alex?.jump ??
      [1, 2].map((n) => fallbackAssetUrl(`runner-game-character-alex-jump-${n}`)),
    injured:
      characterAssetUrls?.alex?.injured ??
      [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-alex-injured-${n}`)),
    voice: characterAssetUrls?.alex?.voice ?? ALEX_VOICE_FILES.map((name) => fallbackSoundUrl(`buddy.sound.${name}`)),
  },
  coco: {
    label: 'Coco',
    nativeScale: true,
    run: characterAssetUrls?.coco?.run ?? {
      left: [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-coco-run-left-${n}`)),
      center: [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-coco-run-${n}`)),
      right: [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-coco-run-right-${n}`)),
    },
    jump:
      characterAssetUrls?.coco?.jump ?? [1, 2].map((n) => fallbackAssetUrl(`runner-game-character-coco-jump-${n}`)),
    injured:
      characterAssetUrls?.coco?.injured ??
      [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-coco-injured-${n}`)),
    voice: characterAssetUrls?.coco?.voice ?? COCO_VOICE_FILES.map((name) => fallbackSoundUrl(`buddy.sound.${name}`)),
  },
  baloo: {
    label: 'Baloo',
    nativeScale: true,
    run: characterAssetUrls?.baloo?.run ?? {
      left: [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-baloo-run-left-${n}`)),
      center: [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-baloo-run-${n}`)),
      right: [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-baloo-run-right-${n}`)),
    },
    jump:
      characterAssetUrls?.baloo?.jump ?? [1, 2].map((n) => fallbackAssetUrl(`runner-game-character-baloo-jump-${n}`)),
    injured:
      characterAssetUrls?.baloo?.injured ??
      [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-baloo-injured-${n}`)),
    voice: characterAssetUrls?.baloo?.voice ?? BALOO_VOICE_FILES.map((name) => fallbackSoundUrl(`buddy.sound.${name}`)),
  },
  topgaard: {
    label: 'Topgaard',
    nativeScale: true,
    run: characterAssetUrls?.topgaard?.run ?? {
      left: [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-topgaard-run-left-${n}`)),
      center: [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-topgaard-run-${n}`)),
      right: [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-topgaard-run-right-${n}`)),
    },
    jump:
      characterAssetUrls?.topgaard?.jump ??
      [1, 2].map((n) => fallbackAssetUrl(`runner-game-character-topgaard-jump-${n}`)),
    injured:
      characterAssetUrls?.topgaard?.injured ??
      [1, 2, 3, 4].map((n) => fallbackAssetUrl(`runner-game-character-topgaard-injured-${n}`)),
    voice: characterAssetUrls?.topgaard?.voice ?? NIKI_VOICE_FILES.map((name) => fallbackSoundUrl(`buddy.sound.${name}`)),
  },
};
const CHARACTER_ORDER = ['mika', 'jas', 'alex', 'nellie', 'monkey', 'topgaard', 'coco', 'baloo'];
const DEFAULT_CHARACTER = 'mika';
const CHARACTER_STORAGE_KEY = 'fg-runner-game-character';

// Obstacles and pickups are real art too now -- a cactus, a crate, and (in
// place of generic coins) a randomly-picked collectible per spawn, all
// textured planes rather than solid-color primitives.
let TALL_OBSTACLE_URL = assetUrls?.obstacleTall ?? new URL('./runner-game-obstacle-tall.png', import.meta.url).href;
const LOW_OBSTACLE_URL = assetUrls?.obstacleLow ?? new URL('./runner-game-obstacle-low.png', import.meta.url).href;
// Straight-on front/rear crops from the bus sprite sheet (pixel-bus.png),
// downscaled to BUS_PIXELS_PER_UNIT so they share a texel density with the
// procedurally-drawn sides/roof. The sheet has no orthographic side or top
// view (only perspective/3-4 views), and mapping perspective art onto flat
// box faces gives the same double-perspective problem the crate comment in
// `buildScene` describes -- so sides/roof are drawn in code from the
// sheet's palette instead (see `BUS_COLORS`).
const BUS_FRONT_URL = assetUrls?.busFront ?? new URL('./runner-game-bus-front.png', import.meta.url).href;
const BUS_REAR_URL = assetUrls?.busRear ?? new URL('./runner-game-bus-rear.png', import.meta.url).href;
// Same crops with the yellow bodywork recolored to the blue livery.
const BUS_FRONT_BLUE_URL =
  assetUrls?.busFrontBlue ?? new URL('./runner-game-bus-front-blue.png', import.meta.url).href;
const BUS_REAR_BLUE_URL = assetUrls?.busRearBlue ?? new URL('./runner-game-bus-rear-blue.png', import.meta.url).href;
// Cycling riders: one atlas strip per character, cut from the peepziez
// sprite sheets -- side-view riding frames, then back-view riding frames,
// then the crying crash pose, every cell bottom-aligned so the wheels (or
// seat, when crashed) sit on the ground line. The sheets' baked drop
// shadows are stripped during extraction; the game draws its own.
const RIDER_URLS = {
  panda: assetUrls?.riders?.panda ?? new URL('./runner-game-rider-panda.png', import.meta.url).href,
  koala: assetUrls?.riders?.koala ?? new URL('./runner-game-rider-koala.png', import.meta.url).href,
  reindeer: assetUrls?.riders?.reindeer ?? new URL('./runner-game-rider-reindeer.png', import.meta.url).href,
};
const PICKUP_URLS =
  assetUrls?.pickups ?? [1, 2, 3].map((n) => new URL(`./runner-game-pickup-${n}.png`, import.meta.url).href);
// Index of the banana within PICKUP_URLS/PICKUP_ASPECTS/etc -- it gets its
// own extruded-silhouette geometry instead of the plain box the other
// variants use (see the `buildScene` pickup-pool comment).
const BANANA_VARIANT_INDEX = 2;
// Item-tracking keys, one per pickup variant (same order as PICKUP_URLS). They
// must exist in the Supabase `runner_items` table (supabase/runner-items-
// analytics.sql); the HUD tracker and leaderboard read their labels/icons from
// the section's "Tracked item" blocks. To track a new collectible: add its key
// here (at its variant's index), insert it into `runner_items`, add a block.
const ITEM_KEY_BY_VARIANT = ['jojos5p', 'jojos3p', 'bananer'];
const POWERUP_ITEM_KEY = 'goldJasmin';
const COLLECT_SOUND_URL = assetUrls?.collectSound ?? new URL('./runner-game-sound-collect.mp3', import.meta.url).href;
// How loud the coin-pickup sound is (0..1). Quick coin trails layer several of these, so keep it modest.
const COIN_COLLECT_VOLUME = 0.4;
const JUMP_SOUND_URL = assetUrls?.jumpSound ?? new URL('./runner-game-sound-jump.mp3', import.meta.url).href;
const GAME_OVER_SOUND_URL =
  assetUrls?.gameOverSound ?? new URL('./runner-game-sound-gameover.mp3', import.meta.url).href;
let MUSIC_SOUND_URL = assetUrls?.musicSound ?? new URL('./runner-game-sound-bendy-buddies.mp3', import.meta.url).href;

// How often (in score points) a random character voice line plays.
// (Score is only earned from collected items -- COIN_VALUE each -- so this is
// every 10 items.)
const VOICE_MILESTONE_INTERVAL = 150;

const TALL_OBSTACLE_HEIGHT = 1.65;
let TALL_OBSTACLE_ASPECT = 329 / 672;
// Hitbox measured from the art rather than the whole lane: the trunk is
// ~38% of the sprite's width, the arms span nearly all of it (between ~45%
// and ~75% of its height). Half-widths include the player's own body
// half-width, so a player edging past visibly clear of the arms isn't hit.
// Same half-depth along Z, since it's a cross-billboard (as deep as wide).
let TALL_OBSTACLE_HIT_HALF_WIDTH = (TALL_OBSTACLE_HEIGHT * TALL_OBSTACLE_ASPECT) / 2 + 0.2;
let TALL_OBSTACLE_HIT_HALF_DEPTH = (TALL_OBSTACLE_HEIGHT * TALL_OBSTACLE_ASPECT) / 2 + 0.1;
// Jumpable once the player's feet are up around the top of the arms -- a
// ground jump peaks at ~1.44, so this leaves a real but tighter timing
// window than a crate's (see JUMP_APEX_HEIGHT). The narrow top of the trunk
// is let off, cartoon-style.
let TALL_OBSTACLE_CLEAR_HEIGHT = TALL_OBSTACLE_HEIGHT * 0.58;
// Was 1.1 -- nearly as tall as a bus roof, which made crates read as
// oversized next to buses. Now ~half a bus's height.
const LOW_OBSTACLE_HEIGHT = 0.825;
const LOW_OBSTACLE_ASPECT = 255 / 246;
// Each pickup variant has a very different real aspect ratio (a snack pack
// vs. a banana lying on its side) and its own target world height (the
// snack packs read as small at the banana's size, so they're bigger), so
// each pooled pickup is individually sized whenever it's (re)assigned a
// random variant, rather than forcing every variant into one footprint.
const PICKUP_ASPECTS = [529 / 898, 510 / 809, 576 / 324];
const PICKUP_HEIGHTS = [0.75, 0.75, 0.5];
// A sliver of depth: just enough edge to catch the light and stop the art
// reading as paper, well short of the chunky block these used to be.
// Edge color per
// variant (sampled by eye from each PNG) for the 4 side faces -- a cross-
// billboard (2 crossed planes) was tried first, but for a curved/directional
// shape like the banana it shows both planes' silhouettes at once from most
// angles, reading as multiple overlapping bananas instead of one 3D object.
// A real box always shows exactly one instance of the art per face.
const PICKUP_DEPTH_FRACTION = 0.05;
const PICKUP_SIDE_COLORS = [0x5b3a8e, 0x2f6fb0, 0xd9a520];

// The banana (variant index 2) doesn't fill its own rectangular bounding
// box -- it's a diagonal curved shape -- so a plain box's side faces showed
// as a visible rectangular "frame" floating around the art. Traced from the
// PNG's actual alpha silhouette (contour-detected + simplified to 20 points)
// and extruded instead, so the depth hugs the real banana outline with no
// frame. Local unit space, matching BoxGeometry(1,1,1)'s -0.5..0.5 range.
const BANANA_SHAPE_POINTS = [
  [0.4983, 0.4228],
  [0.4601, 0.5],
  [0.3681, 0.463],
  [0.3681, 0.3056],
  [0.309, 0.2068],
  [0.059, -0.0309],
  [-0.1163, -0.034],
  [-0.4132, 0.216],
  [-0.4913, 0.1728],
  [-0.5, 0.034],
  [-0.4201, -0.2099],
  [-0.2969, -0.3889],
  [-0.1094, -0.4969],
  [0.1198, -0.4938],
  [0.3003, -0.3735],
  [0.4601, -0.0926],
  [0.4601, -0.0062],
  [0.4861, 0.0],
  [0.4688, 0.321],
  [0.4965, 0.3272],
];
const BANANA_DEPTH_FRACTION = 0.1;
function bananaFaceUV(geometry, vertices, indexA, indexB, indexC) {
  const toUV = (i) => new THREE.Vector2(vertices[3 * i] + 0.5, vertices[3 * i + 1] + 0.5);
  return [toUV(indexA), toUV(indexB), toUV(indexC)];
}
const BANANA_UV_GENERATOR = {
  generateTopUV: bananaFaceUV,
  generateBottomUV: bananaFaceUV,
  generateSideWallUV: () => [new THREE.Vector2(0, 0), new THREE.Vector2(1, 0), new THREE.Vector2(1, 1), new THREE.Vector2(0, 1)],
};

const PLAYER_HEIGHT = 1.7;
// Characters with `nativeScale` all draw at one shared size per source pixel (their sheets are
// rendered at the same scale), so a short figure like Nellie stays smaller than a tall-haired one
// instead of every figure being stretched to the same height. This is the frame height that comes
// out at exactly PLAYER_HEIGHT.
const NATIVE_REFERENCE_HEIGHT_PX = 262;
// Corrected once each texture's real image dimensions are known; only used
// before then, and as a sane fallback if a load ever fails. Every frame's
// aspect ratio varies slightly (independently-cropped poses, not a uniform
// sprite export), so this is necessarily an approximation, not exact for
// every frame -- close enough that the difference isn't visible in play.
const PLAYER_FALLBACK_ASPECT = 112 / 227;

const JUMP_VELOCITY = 7.2;
const GRAVITY = 18;
// Half the crate's height, as it was at the original 1.1 size (0.55).
const LOW_OBSTACLE_CLEAR_HEIGHT = LOW_OBSTACLE_HEIGHT / 2;
// Switches from the low (crouched takeoff) to the high (arms-up, peak) jump
// frame once ascent has climbed past this fraction of the clear height a low
// obstacle needs -- roughly "on the way up" vs "near the top of the arc".
const JUMP_HIGH_POSE_HEIGHT = 0.35;
// Physical apex height of a jump (v^2 / 2g), used to spread a jump's real
// pose frames evenly across the arc for characters with more than the 2
// (low/high) frames Mika's explicit threshold above was tuned for.
const JUMP_APEX_HEIGHT = (JUMP_VELOCITY * JUMP_VELOCITY) / (2 * GRAVITY);

// How far a lane change has to have progressed (world units of remaining
// distance to the target lane) before swapping to the turned angle art;
// settles back to the center frames once a lane change finishes.
const LEAN_ANGLE_THRESHOLD = 0.15;

// Procedural animation layered on top of the run frames: a bob synced to
// distance traveled, which also drives which of the 4 real stride frames is
// showing. Kept small now that real alternating-leg art exists -- this was
// carrying the entire "running" illusion back when the character was a
// static shape, and left at that size it now competes with the real stride
// frames rather than supporting them, reading as a hop instead of a run.
const BOB_AMPLITUDE = 0.025;
const BOB_CYCLES_PER_WORLD_UNIT = 1.7;
const SHADOW_WORLD_SIZE = 0.62;
const SHADOW_MAX_HEIGHT_FOR_FADE = 2.2;

// Speed follows the difficulty curve (see DIFFICULTY_DISTANCE_SCALE):
// BASE_SPEED at the start, approaching MAX_SPEED over a long run. (An early
// version reached 2.4x BASE_SPEED inside a minute, which was too much; the
// ceiling stays well under that.)
const BASE_SPEED = 9;
let MAX_SPEED = 20;

// Time-based, not distance-based: spacing waves by distance meant the real-time
// gap between them shrank as speed ramped up (distance accrues faster at
// higher speed), so runs got relentlessly dense well before the player had
// adjusted to the current speed. A fixed time window keeps the reaction gap
// consistent regardless of how fast the world is scrolling.
// The interval itself still shrinks, but on a slow, distance-driven ramp
// (see `getDifficulty`) rather than reacting to the moment-to-moment speed --
// a deliberate difficulty curve over the course of a run, not a side effect
// of the speed ramp.
// The interval range and jitter were originally close enough in size
// (jitter +-0.3s against a 0.65s total range) that the systematic shrink was
// easy to lose in the noise -- widened the range and cut the jitter so the
// trend actually reads as "getting faster" rather than just "randomly
// varying" over a short run.
const SPAWN_INTERVAL_SECONDS_START = 1.0;
let SPAWN_INTERVAL_SECONDS_MIN = 0.26;
const SPAWN_INTERVAL_JITTER_SECONDS = 0.2;

// Difficulty (0..1) as a function of distance run:
// 1 - exp(-distance / DIFFICULTY_DISTANCE_SCALE). It rises fastest early on
// and then keeps creeping up for the whole run, never plateauing -- the old
// linear ramp hit its ceiling ~15s in, after which nothing changed however
// long you survived. Roughly: 40% of the way after ~30s, 65% after ~1 min,
// 85% after ~2 min, 95% after ~3 min. It drives speed, spawn cadence, and
// the mix of harder patterns (double pillars, buses, riders, roof crates).
// (Background music was originally tied to this too, speeding up alongside
// the obstacles, but writing its `playbackRate` every frame turned out to
// be expensive enough on mobile Safari/WebKit to cause visible jank, so
// music now just plays at a constant rate.)
// (Was 650. Slowed a little so younger players get longer on each step.)
let DIFFICULTY_DISTANCE_SCALE = 700;
const DOUBLE_PILLAR_CHANCE_START = 0.2;
let DOUBLE_PILLAR_CHANCE_MAX = 0.7;
// Chance a hazard is aimed at the lane the player is currently in, rather
// than a random one. This is the main thing that turns density into
// pressure: an obstacle in one of the other lanes asks nothing of the
// player, so at low targeting most of a busy screen can be ignored.
const TARGETED_LANE_CHANCE_START = 0.15;
let TARGETED_LANE_CHANCE_MAX = 0.75;
// A wall of crates across all three lanes: the only pattern that can't be
// sidestepped, so it has to be jumped. Held back until the run has had time
// to teach jumping, and never spawned into a landing zone that isn't clear
// (see `spawnWallWave`).
let WALL_MIN_DIFFICULTY = 0.45;
let WALL_CHANCE_MAX = 0.3;

// --- Progressive patterns ----------------------------------------------------
// Each pattern below fades in over a difficulty range (see `rampBetween`):
// nothing before `from`, full strength at `to`, so a run meets them one at a
// time instead of all at once.
//
// Fairness: every wave is checked by `isWaveFair` -- a wave that would leave
// no way through, given how long a lane change actually takes, is dropped.
// FAIR_LANE_STEP_SECONDS is deliberately roomier than the ~0.1s the lane
// ease needs, to leave room for a young player's reaction time.
const FAIR_LANE_STEP_SECONDS = 0.3;
const FAIR_TIME_STEP = 0.05;
const FAIR_MAX_HORIZON_SECONDS = 9;
const FAIR_SPAWN_ATTEMPTS = 3;
// Pickups with a catch: a trail of four leading straight into a crate in the
// same lane. Jump early (pickups are collected in the air too) or swerve
// after them -- either works, it just has to be noticed.
const BAIT_FROM = 0.15;
const BAIT_TO = 0.8;
const BAIT_CHANCE_MAX = 0.18;
const BAIT_COIN_COUNT = 4;
const BAIT_CRATE_GAP = 2.6;
// A crate then a cactus in the same lane, close enough that jumping the crate
// lands you on the cactus -- so the answer is to swerve, not hop. The gap is
// in seconds at the current speed.
const COMBO_FROM = 0.3;
const COMBO_TO = 0.7;
const COMBO_CHANCE_MAX = 0.16;
const COMBO_GAP_SECONDS_START = 1.05;
const COMBO_GAP_SECONDS_END = 0.8;
// Slalom: a run of cactus pairs whose open lane keeps changing. Two steps
// first, three later; each gap grows with how far the open lane moves.
const SLALOM_FROM = 0.45;
const SLALOM_TO = 0.9;
const SLALOM_CHANCE_MAX = 0.16;
const SLALOM_THREE_STEP_FROM = 0.7;
const SLALOM_BASE_GAP_SECONDS = 0.75;
const SLALOM_GAP_PER_LANE_SECONDS = 0.3;
// Jump then swerve: a wall to jump, and just after landing a cactus pair.
const JUMP_SWERVE_FROM = 0.6;
const JUMP_SWERVE_TO = 1;
const JUMP_SWERVE_CHANCE_MAX = 0.14;
const JUMP_SWERVE_LANDING_SECONDS = 0.45;
// Storms: strikes come less often early (gentler than before), more often
// late, and late in a run one can follow another in a different lane. The
// warning shrinks a little but never below LIGHTNING_WARNING_FLOOR_SECONDS.
const STORM_PRESSURE_FROM = 0.25;
const STORM_PRESSURE_TO = 0.9;
const LIGHTNING_INTERVAL_SCALE_EASY = 1.35;
const LIGHTNING_INTERVAL_SCALE_HARD = 0.85;
const LIGHTNING_WARNING_FLOOR_SECONDS = 0.95;
const LIGHTNING_CHAIN_FROM = 0.7;
const LIGHTNING_CHAIN_CHANCE_MAX = 0.35;
const LIGHTNING_CHAIN_DELAY_SECONDS = 1.7;

// Riders who steer into the next lane: some riders heading away from the
// player move over once, early (RIDER_SWERVE_TRIGGER_Z is far up the road),
// so there's time to read it. Until it has finished, both lanes count as
// blocked for the fairness check and for new spawns.
const RIDER_SWERVE_FROM = 0.3;
const RIDER_SWERVE_TO = 0.85;
const RIDER_SWERVE_CHANCE_MAX = 0.5;
const RIDER_SWERVE_SECONDS = 0.9;
const RIDER_SWERVE_TRIGGER_Z = -34;

/** 0 until `from`, 1 from `to`, linear in between. */
function rampBetween(value, from, to) {
  return Math.min(1, Math.max(0, (value - from) / (to - from)));
}

const SPAWN_Z = -60;
// How close the nearest obstacle already is when a run starts (see
// `prefillTrack`) -- ~2.5s away at BASE_SPEED.
const FIRST_OBSTACLE_Z = -22;
const DESPAWN_Z = 4;
const COLLISION_Z_MIN = -0.6;
const COLLISION_Z_MAX = 0.6;

// Sized with headroom for the tightened min spawn interval above: at
// SPAWN_INTERVAL_SECONDS_MIN and MAX_SPEED, more obstacles/pickups can be
// in flight (spawned but not yet past the collision line) at once than the
// old pool sizes allowed -- without this headroom, spawning would start
// silently failing to find a free pooled instance right when the run is
// supposed to be getting harder, not just failing to actually spawn.
// Raised again, and sized per type, for the longer difficulty curve: late in
// a run the two-cactus pattern dominates, so cacti need far more than crates
// (the old even 8/8 split ran out of cacti in stress-tested runs; peaks seen
// were ~7 crates, 22 cacti, 23 pickups).
// (Raised again for the multi-step patterns, which spawn up to 6 cacti at once.)
const OBSTACLE_POOL_SIZES = { low: 16, tall: 36 };
const COIN_POOL_SIZE = 32;
const COIN_VALUE = 15;

// Weather: randomized storms. Clear and stormy spells alternate; while it
// storms, lightning strikes a lane at the player's row. The lane glows for
// LIGHTNING_WARNING_SECONDS first, so a player who moves out is safe. A hit
// wipes the run's points (the run itself carries on).
const RAIN_DROP_COUNT = 650;
const RAIN_AREA_HALF_WIDTH = 9;
const RAIN_AREA_HEIGHT = 14;
// How far up and down the street the ground mist drifts.
// However thick a fog bank gets, at least this much road stays visible --
// enough to see and clear anything standing in it.
const FOG_BANK_MIN_VISIBILITY = 30;
// The drifting fog banks run from well behind the camera to the far end of
// the street, so they sweep through the near field rather than popping in.
const FOG_VOLUME_NEAR_Z = 12;
// Kept to the near half of the street: further out they're indistinguishable
// from the distance fog, and it's passing close that sells the effect.
const FOG_VOLUME_FAR_Z = -48;
// Bats fly in from the far end and are recycled once they pass the camera.
const BAT_NEAR_Z = 10;
const BAT_FAR_Z = -85;
const MIST_NEAR_Z = 8;
const MIST_FAR_Z = -95;
const RAIN_NEAR_Z = 6;
const RAIN_FAR_Z = -45;
const RAIN_FALL_SPEED = 26;
const RAIN_SLANT = 3;
const RAIN_DROP_LENGTH = 0.7;
const STORM_FADE_SECONDS = 2.5;
const STORM_CLEAR_RANGE = [7, 16];
const STORM_RAIN_RANGE = [12, 22];
const STORM_FIRST_CLEAR_RANGE = [5, 10];
const LIGHTNING_INTERVAL_RANGE = [2.2, 5];
const LIGHTNING_WARNING_SECONDS = 1.1;
// Fraction of the run's pickups lost per hit, and how many spill out as
// bouncing pickups (visual only, capped).
const LIGHTNING_LOSS_FRACTION = 0.5;
const PUDDLE_POOL_SIZE = 4;
const PUDDLE_INTERVAL_RANGE = [5, 10];
const PUDDLE_FIRST_RANGE = [6, 10];
const PUDDLE_LENGTH = 1.7;
// Ground-level breathing room kept between a puddle and any obstacle in its
// lane (either side), so a puddle is never hidden behind or trapped by one.
const PUDDLE_CLEARANCE = 14;
const PUDDLE_MAX_JUMP_HEIGHT = 0.4; // higher than this = you're over it
const PUDDLE_EFFECTS = [
  { type: 'slide', seconds: 1.0 },
  { type: 'slippery', seconds: 3.2 },
  { type: 'inverted', seconds: 3.5 },
];
const LANE_EASE_SLIPPERY = 3;
const SPILL_POOL_SIZE = 14;
const SPILL_GRAVITY = 26;
const SPILL_BOUNCE = 0.55;
const SPILL_LIFETIME = 2.6;
const SPILL_SCALE = 1.6;
const SPILL_ROAD_DRIFT = 0.3;
const LIGHTNING_BOLT_SECONDS = 0.5;
const BOLT_POINTS = 24;
const BOLT_BRANCH_POINTS = 6;
const BOLT_HALF_WIDTH = 0.4;
const BOLT_TOP_Y = 24;
const BOLT_Z = -2;
const LIGHTNING_HIT_HALF_WIDTH = LANE_WIDTH * 0.5;
const LIGHTNING_TARGET_PLAYER_CHANCE = 0.55;
let STORM_SKY_TINT = '#5d6672';
let STORM_FOG_COLOR = '#7d8792';


// --- Buses: elevated running surfaces ---------------------------------------
// Yellow Danish-style city buses whose roofs are traversable. Every size is
// derived from existing gameplay scale rather than picked in world units:
// - Roof height: above a jump's apex (109%), so a bus reads as taller than
//   the character can simply hop onto. Every ground-jump mount ends in the
//   lift-up described at SURFACE_MOUNT_TOLERANCE rather than a drop onto
//   the roof -- that tolerance is what makes this height reachable, so tune
//   the two together (the mount window must stay open: roof minus tolerance
//   has to sit comfortably below the jump apex).
// - Width: fits inside one lane with visible road either side.
// - Length: 3x the cactus's original 2.2 height (the largest obstacle when
//   buses were sized; fixed here so resizing cacti doesn't resize buses), and
//   the articulated bus 1.5x that -- also roughly a real 12m/18m bus against its
//   width, and comfortably longer than the ~4.5 units a player can travel
//   between clearing the front and touching down, so a landing always ends
//   on the roof with room left to actually run on it.
const BUS_ROOF_HEIGHT = JUMP_APEX_HEIGHT * 1.09;
const BUS_BODY_BOTTOM = 0.12;
const BUS_BODY_HEIGHT = BUS_ROOF_HEIGHT - BUS_BODY_BOTTOM;
const BUS_WIDTH = LANE_WIDTH * 0.55;
const BUS_STANDARD_LENGTH = 2.2 * 3;
const BUS_ARTICULATED_LENGTH = BUS_STANDARD_LENGTH * 1.5;
const BUS_JOINT_LENGTH = 0.6;
const BUS_WHEEL_RADIUS = 0.24;
const BUS_WHEEL_WIDTH = 0.16;
// A jump that's below roof height as the bus arrives, but with feet within
// this distance of the roof, climbs onto it instead of counting as a crash
// into the end -- same spirit as LOW_OBSTACLE_CLEAR_HEIGHT only needing half
// the crate's real height. Visually eased as a quick climb (see
// `landingSnapOffset`). Also the lower edge of the band a falling player can
// land from, so a fast fall can't tunnel through a roof.
const SURFACE_MOUNT_TOLERANCE = 0.45;
// Half-depth of the player's feet along Z, for anything with real length
// (surfaces). Used both for "is the roof under me" and "did I hit the bus
// body", so the two can never disagree at an edge.
const PLAYER_FOOT_HALF_DEPTH = 0.3;
// Spacing rules at the spawn line for lanes involving a bus. Derived from a
// jump's ground travel at base speed: room ahead of a bus for a run-up
// (half a jump), a full jump's worth behind one so a player dropping off the
// back isn't landed straight onto a hazard, and a short roof-to-roof gap so
// buses can chain in one lane as a bus-to-bus jump.
const JUMP_AIR_TIME = (2 * JUMP_VELOCITY) / GRAVITY;
const BUS_RUNUP_CLEARANCE = JUMP_AIR_TIME * BASE_SPEED * 0.5;
const BUS_EXIT_CLEARANCE = JUMP_AIR_TIME * BASE_SPEED;
const BUS_CHAIN_GAP = LANE_WIDTH;
const BUS_WAVE_CHANCE_START = 0.15;
let BUS_WAVE_CHANCE_MAX = 0.25;
const BUS_ARTICULATED_SHARE = 0.4;
const BUS_ROOF_COIN_CHANCE = 0.55;
const BUS_ROOF_CRATE_CHANCE = 0.5;
const BUS_ROOF_CRATE_MIN_DIFFICULTY = 0.4;
// Per variant per livery (so 4 pools); stress-tested peak is ~10 buses in
// flight across all of them at max speed/difficulty -- 4 per pool
// occasionally ran one pool dry and broke the livery alternation.
const BUS_POOL_SIZE = 5;
const BUS_PIXELS_PER_UNIT = 40;
// Material color multipliers per bus face, derived from the actual lights
// (see `lambertLightLevel`) so each face lands at roughly the sprite's own
// colors, just under 1.0 so nothing clips -- the lights alone turned the
// sprite's saturated yellow mustard/olive. Faces are named by where they
// point in the world (buses are turned to face away from the player). The
// sides are both boosted by the sunlit side's level, so the side facing
// away from the sun stays naturally darker for shape.
const BUS_TARGET_LIGHT_LEVEL = 0.92;
let BUS_NEAR_END_BRIGHTNESS = BUS_TARGET_LIGHT_LEVEL / lambertLightLevel([0, 0, 1]);
let BUS_FAR_END_BRIGHTNESS = BUS_TARGET_LIGHT_LEVEL / lambertLightLevel([0, 0, -1]);
let BUS_SIDE_BRIGHTNESS = BUS_TARGET_LIGHT_LEVEL / lambertLightLevel([-1, 0, 0]);
let BUS_ROOF_BRIGHTNESS = BUS_TARGET_LIGHT_LEVEL / lambertLightLevel([0, 1, 0]);
// Sampled from pixel-bus.png. Body colors come from a livery (below).
const BUS_COLORS = {
  glass: '#2b3849',
  glassDark: '#1f222c',
  glassGlint: '#4a5d73',
  skirt: '#303549',
  arch: '#1b1d25',
  doorEdge: '#8c93a3',
  tailLight: '#f43c0b',
  bellowsDark: '#2c303c',
  bellowsLight: '#555b6c',
  wheel: '#22242e',
  hub: '#6b7184',
};
// Spawned buses alternate between these, in this order (see `spawnBusWave`).
// Each livery overrides BUS_COLORS for its procedurally-drawn panels and
// has its own baked front/rear sprite faces. The blue livery also darkens
// the glass -- the yellow bus's navy windows would otherwise nearly vanish
// against a dark blue body.
const BUS_LIVERIES = {
  yellow: {
    body: '#fcbc12',
    bodyLight: '#fdd645',
    bodyShade: '#f29b02',
    frontUrl: BUS_FRONT_URL,
    rearUrl: BUS_REAR_URL,
  },
  blue: {
    body: '#1f3d7a',
    bodyLight: '#3a64ac',
    bodyShade: '#162c5c',
    glass: '#11151d',
    glassDark: '#0a0c11',
    glassGlint: '#3b4b61',
    frontUrl: BUS_FRONT_BLUE_URL,
    rearUrl: BUS_REAR_BLUE_URL,
  },
};
const BUS_LIVERY_ORDER = ['yellow', 'blue'];

// --- Cycling riders --------------------------------------------------------------
// Moving obstacles: kids in animal onesies on bikes. Some cross the road
// (side view) -- they wait on the pavement, then set off timed to be partway
// across when they reach the player -- and some ride away down a lane ahead of
// the player (back view), slower than the player runs, so they're caught up
// with. Anything that runs into them (or that they run into -- a crate, a
// cactus, a bus, another rider) makes them crash: they switch to their
// crying pose and sit where they fell, as a small jumpable obstacle. The
// player hitting one is a game over, same as any other obstacle.
const RIDER_KINDS = ['panda', 'koala', 'reindeer'];
// Cell sizes in atlas pixels; every atlas is scaled so its side-view frames
// are RIDER_SIDE_PIXELS tall, i.e. one shared world scale for all riders.
const RIDER_DEFS = {
  panda: { cellWidth: 211, cellHeight: 227, sideFrames: 4, backFrames: 4 },
  koala: { cellWidth: 226, cellHeight: 239, sideFrames: 4, backFrames: 4 },
  reindeer: { cellWidth: 190, cellHeight: 239, sideFrames: 4, backFrames: 4 },
};
const RIDER_SIDE_PIXELS = 216;
// A kid on a bike, a little shorter than the standing player character.
const RIDER_HEIGHT = 1.5;
const RIDER_PIXELS_PER_UNIT = RIDER_SIDE_PIXELS / RIDER_HEIGHT;
const RIDER_FRAMES_PER_SECOND = 10;
// Riding away from the player: well under BASE_SPEED, so they're overtaken.
const RIDER_ALONG_SPEED = 4;
const RIDER_CROSS_SPEED = 2.2;
// Crossing riders wait just off the curb and stop once across, in front of
// the far houses.
const RIDER_CROSS_START_X = ROAD_WIDTH / 2 + 0.3;
const RIDER_CROSS_END_X = GROUND_WIDTH / 2 - 0.45;
// Riders collide by actual x distance, not lane -- crossing riders are
// usually between lanes. Wide enough for a side-on bike plus the player.
const RIDER_HIT_HALF_WIDTH = 0.8;
const RIDER_BUMP_HALF_DEPTH = 0.6;
// Jumpable, crate-style: half their height while riding, lower once sitting.
const RIDER_CLEAR_HEIGHT = RIDER_HEIGHT / 2;
// The crying pose is drawn at this fraction of the riding scale -- at full
// scale the sitting kid (plus the stars over its head) read as bigger than
// the rider it came off.
const RIDER_CRASHED_SCALE = 0.75;
const RIDER_CRASHED_CLEAR_HEIGHT = 0.45;
const RIDER_WAVE_CHANCE_START = 0.12;
let RIDER_WAVE_CHANCE_MAX = 0.2;
const RIDER_CROSSING_SHARE = 0.5;
// Anything spawned behind a rider going the player's way catches up with it
// (the rider is slower than the world), so left alone nearly every one
// crashed before reaching the player. This share of them keep their lane
// clear of new spawns for as long as a new spawn could still catch them (see
// `getFreeLanes`); the rest can still be run into, for the odd pile-up.
const RIDER_ALONG_PROTECTED_CHANCE = 0.75;
// Per character.
const RIDER_POOL_SIZE = 3;

/**
 * Per-type obstacle data the collision/spawn code reads instead of branching
 * on type names. `length` is the obstacle's extent along Z: 0 for the
 * original point-like obstacles (collision stays exactly the old
 * COLLISION_Z window test), real length for buses. `topSurfaceHeight` marks
 * a traversable top the player can land on and run along; obstacles without
 * one are purely something to avoid. Bus `sections` are fractions of the
 * non-joint length; door/axle positions are fractions of their own section,
 * measured from that section's front end -- the articulated bus is purely a
 * different configuration of the same bus builder.
 */
const OBSTACLE_DEFS = {
  low: { length: 0, height: LOW_OBSTACLE_HEIGHT, clearHeight: LOW_OBSTACLE_CLEAR_HEIGHT },
  // Taller than a ground jump's apex, so still never jumpable from the
  // ground -- but a player leaping across lanes from a bus roof who's
  // visibly above its top clears it rather than crashing into thin air.
  // `hitHalfWidth`/`hitHalfDepth`: collide by actual distance to the player
  // (see TALL_OBSTACLE_HIT_*) instead of by lane and the shared Z window.
  tall: {
    length: 0,
    height: TALL_OBSTACLE_HEIGHT,
    clearHeight: TALL_OBSTACLE_CLEAR_HEIGHT,
    hitHalfWidth: TALL_OBSTACLE_HIT_HALF_WIDTH,
    hitHalfDepth: TALL_OBSTACLE_HIT_HALF_DEPTH,
  },
  // Origin at ground level (bottom of the sprite); clear height varies with
  // pose, so it's kept on each rider (see `crashRider`).
  rider: { length: 0, clearHeight: RIDER_CLEAR_HEIGHT },
  bus: {
    length: BUS_STANDARD_LENGTH,
    topSurfaceHeight: BUS_ROOF_HEIGHT,
    sections: [
      { fraction: 1, doors: [[0.04, 0.15], [0.45, 0.57]], axles: [0.24, 0.75] },
    ],
  },
  busArticulated: {
    length: BUS_ARTICULATED_LENGTH,
    topSurfaceHeight: BUS_ROOF_HEIGHT,
    sections: [
      { fraction: 0.53, doors: [[0.05, 0.19], [0.5, 0.65]], axles: [0.3, 0.86] },
      { fraction: 0.47, doors: [[0.12, 0.28]], axles: [0.66] },
    ],
  },
};
const BUS_TYPES = ['bus', 'busArticulated'];

const CAMERA_HEIGHT = 2.5;
const CAMERA_DISTANCE = 4.4;
const CAMERA_LOOK_AHEAD = -8;
const CAMERA_LOOK_HEIGHT = 0.9;
// How quickly the camera's look target follows the player up onto / down
// off a roof, and how quickly a lenient-landing snap is absorbed (per second).
const CAMERA_SURFACE_EASE = 4;
const CAMERA_SNAP_EASE = 12;

// Sky: a gradient dome from a pale, hazy horizon up to a deeper blue
// overhead, with a few chunky voxel clouds (see `buildSky`). The distance fog
// is the horizon color, so the far end of the street (buildings being
// recycled in, obstacles appearing at SPAWN_Z) melts into the haze rather
// than popping in against the sky.
let SKY_HORIZON_COLOR = '#d6e9f5';
let SKY_ZENITH_COLOR = '#3f8fdb';
// Inside the camera's far plane (200), and following the camera, so the sky
// always surrounds the view and never gets closer.
const SKY_RADIUS = 180;
// Each cloud: a cluster of boxes ([w, h, d, x, y, z] offsets) around a
// center relative to the camera, all ahead of the player (-z). Kept low (the camera looks slightly down, so only ~18 degrees of sky above
// the horizon is ever on screen) and mostly central, so a narrow portrait
// view still gets some.
let SKY_CLOUDS = [
  { center: [-40, 24, -140], boxes: [[26, 6, 12, 0, 0, 0], [14, 5, 10, -8, 4, 1], [12, 4, 9, 9, 3, -1]] },
  { center: [12, 32, -150], boxes: [[34, 7, 14, 0, 0, 0], [16, 6, 11, 6, 5, 0], [12, 4, 8, -11, 3, 2]] },
  { center: [45, 20, -130], boxes: [[22, 5, 10, 0, 0, 0], [12, 4, 8, 4, 4, 0]] },
  { center: [-12, 16, -155], boxes: [[18, 4, 9, 0, 0, 0], [9, 3, 7, -3, 3, 1]] },
  { center: [75, 34, -120], boxes: [[20, 5, 10, 0, 0, 0], [12, 4, 8, -5, 4, 0], [8, 3, 6, 7, 3, 1]] },
  { center: [-85, 30, -100], boxes: [[24, 6, 12, 0, 0, 0], [12, 5, 9, 6, 4, 0]] },
];
// Slow sideways drift of the whole cloud layer, in radians per second.
let SKY_CLOUD_DRIFT = 0.004;

// Crates, cacti and pickups: brightened through their own materials so the
// camera-facing side shows ~this share of the art's color, the same way the
// buses are (a stronger scene light would also push the already-compensated
// buses and houses into clipping). Pickups additionally glow a little --
// their art is added on top of the lighting at this strength -- so they stay
// bright from every angle as they spin and read as collectible.
const OBSTACLE_TARGET_LIGHT_LEVEL = 0.95;
const PICKUP_GLOW_INTENSITY = 0.3;
let FOG_NEAR = 45;
let FOG_FAR = 125;

// --- Street buildings --------------------------------------------------------
// Continuous rows of old Copenhagen / Nyhavn-style townhouses along both
// pavements: a plastered facade per house in the city's classic palette,
// white-framed windows, a ground floor of doors and shopfronts, and pitched
// roofs (red tile, slate or copper green), some with the gable turned to the
// street. Houses scroll at world speed and are recycled from behind the
// camera to the far end of their row, re-dressed with a new random look.
const BUILDING_DEPTH = 5;
const BUILDING_WIDTHS = [2.6, 3.2, 3.8];
const BUILDING_GROUND_FLOOR_HEIGHT = 1.5;
const BUILDING_FLOOR_HEIGHT = 1.15;
const BUILDING_UPPER_FLOORS = [3, 4];
const BUILDING_PIXELS_PER_UNIT = 16;
// Rows run from just behind the camera out past the fog's far distance, so
// the recycled far end is always fully fogged when it appears.
const BUILDING_ROW_NEAR_Z = CAMERA_DISTANCE + 1;
let BUILDING_ROW_FAR_Z = -FOG_FAR;
const BUILDING_GABLE_TO_STREET_CHANCE = 0.35;
const BUILDING_CHIMNEY_CHANCE = 0.5;
// Mostly the city's quieter plasters and brick, with the famous colorful
// Nyhavn-style houses mixed in (BUILDING_COLORFUL_SHARE of houses) rather
// than every house competing for attention. The first
// BUILDING_COLORFUL_COUNT entries are the colorful ones.
let BUILDING_COLORFUL_SHARE = 0.35;
let BUILDING_COLORFUL_COUNT = 9;
let BUILDING_WALL_COLORS = [
  '#e3a73b', // ochre
  '#b8412f', // oxblood red
  '#d0643a', // terracotta
  '#efd27a', // pale yellow
  '#8fb3cf', // powder blue
  '#9fc7a8', // mint
  '#e2a3a0', // old pink
  '#efe6d2', // cream
  '#d9823b', // burnt orange
  '#e6e0d3', // off-white plaster
  '#d5cdbd', // pale stone
  '#c7bca8', // sandstone
  '#b8b2a7', // grey plaster
  '#a8664e', // old brick
  '#9c8f7e', // taupe
  '#d9c9a8', // faded ochre
];
let BUILDING_ROOF_COLORS = ['#a3432e', '#565c66', '#6fa08a'];
let BUILDING_DOOR_COLORS = ['#2f4a3a', '#5a3a28', '#27405e', '#7a2e2a'];
let BUILDING_CHIMNEY_COLOR = '#8f3b2b';
// --- Pavement trees and street lamps ------------------------------------------
// Alternating trees and lamps along both pavements, near the curb, as two
// instanced meshes (one draw call each). They scroll at world speed and wrap
// from behind the camera to the far end of their row. Built from chunky
// flat-shaded boxes to match the voxel look.
const STREET_PROP_SPACING = 5;
const STREET_PROP_X = ROAD_WIDTH / 2 + 0.55;
// Pavement trees: a tree-pit grate, trunk, and a clustered voxel canopy
// (kept within ~0.75 of the trunk, so it never reaches over the lanes or
// into the facades). Positions are [x, y, z] of each box's center.
let TREE_BOXES = [
  { size: [0.7, 0.03, 0.7], position: [0, 0.015, 0], color: '#5a554c' },
  { size: [0.2, 2.1, 0.2], position: [0, 1.05, 0], color: '#6b4a2f' },
  { size: [1.3, 1.0, 1.3], position: [0, 2.5, 0], color: '#4f8a3c' },
  { size: [1.0, 0.7, 1.0], position: [0.05, 3.2, -0.05], color: '#5e9a45' },
  { size: [0.6, 0.6, 0.6], position: [0.45, 2.2, 0.3], color: '#467d35' },
  { size: [0.6, 0.5, 0.6], position: [-0.3, 2.95, -0.45], color: '#56913f' },
  { size: [0.5, 0.4, 0.5], position: [0, 3.7, 0], color: '#6aa84f' },
];
// Dark green cast-iron lamp post with an arm reaching out over the road
// (-x; left-row lamps are turned round) and a warm lantern. `glow` parts
// ignore the scene lighting and show at full color, so the lantern reads as
// lit.
let LAMP_BOXES = [
  { size: [0.2, 0.4, 0.2], position: [0, 0.2, 0], color: '#26312b' },
  { size: [0.08, 3.0, 0.08], position: [0, 1.5, 0], color: '#2e3b33' },
  { size: [0.55, 0.06, 0.06], position: [-0.27, 2.97, 0], color: '#2e3b33' },
  { size: [0.28, 0.08, 0.28], position: [-0.52, 2.93, 0], color: '#26312b' },
  { size: [0.2, 0.26, 0.2], position: [-0.52, 2.76, 0], color: '#ffe7a3', glow: true },
  { size: [0.14, 0.04, 0.14], position: [-0.52, 2.61, 0], color: '#26312b' },
];

// A little dimmer than the buses so the street stays background and the
// obstacles in front of it pop. Brightness multipliers are derived from the
// real lights the same way as the buses' (see BUS_TARGET_LIGHT_LEVEL).
const BUILDING_TARGET_LIGHT_LEVEL = 0.8;

const LANE_EASE = 10;
const MAX_DELTA_TIME = 0.05;

// Versioned: scores used to include distance run, so bests saved under the
// old key are on a different scale and would be near-unbeatable now that
// only collected items score.
const HIGH_SCORE_KEY = 'fg-runner-game-best-v2';
// The theme this device chose with the switcher ("default" = the original look, "halloween", ...).
const THEME_CHOICE_KEY = 'fg-runner-theme-v1';
// Older "too scary" flag, still honoured: it means the original theme was chosen.
const SCARY_OFF_KEY = 'fg-runner-scary-off-v1';

function readThemeChoice() {
  try {
    const stored = window.localStorage.getItem(THEME_CHOICE_KEY);
    if (stored) return stored;
    return window.localStorage.getItem(SCARY_OFF_KEY) === '1' ? 'default' : '';
  } catch {
    return '';
  }
}

/** Remembers the player's theme and reloads: the theme is built into the game when the page loads. */
function chooseTheme(id) {
  try {
    window.localStorage.setItem(THEME_CHOICE_KEY, id);
    window.localStorage.removeItem(SCARY_OFF_KEY);
  } catch {
    // storage blocked: the choice only lasts for this load
  }
  window.location.reload();
}

// Global randomized power-up (see tools/powerup-worker). The Worker decides
// when one is live and how many players may claim it; the game just asks once
// per run and, if live, drops a single golden orb a few seconds in.
const POWERUP_API_URL = 'https://throbbing-breeze-720a.moe-f89.workers.dev';
const POWERUP_PLAYER_ID_KEY = 'fg-runner-player-id';
const POWERUP_SPAWN_DELAY_MIN = 8; // seconds into the run
const POWERUP_SPAWN_DELAY_MAX = 20;
const POWERUP_URL = assetUrls?.powerup ?? new URL('./runner-game-powerup.png', import.meta.url).href;
const POWERUP_ASPECT = 256 / 245;
const POWERUP_HEIGHT = 1.3;
// What the Worker can grant (it picks per spawn): how long each lasts and the
// label shown in the HUD. `banana` is the "campaign" benefit -- every pickup
// that spawns is a banana for the duration.
const POWERUP_EFFECTS = {
  magnet: { seconds: 30 },
  invincible: { seconds: 20 },
  banana: { seconds: 30 },
};
// Prebuilt so the per-frame invincibility blink doesn't parse a color string.
const PLAYER_TINT_NONE = new THREE.Color('#ffffff');
const PLAYER_TINT_INVINCIBLE = new THREE.Color('#ffe45c');
const MAGNET_RANGE_Z = -14; // coins closer than this get pulled toward the player
const MAGNET_PULL_RATE = 9;
// Retired: an older build carried an unsubmitted best score between runs. Cleaned up on load.
const LEGACY_PENDING_SCORE_KEY = 'fg-runner-pending-score';

const SWIPE_DISTANCE_THRESHOLD = 30;

// How long the character wheel takes to spin a tapped character to the middle.
const WHEEL_SCROLL_DURATION_MS = 240;
// The wheel's characters ride an arc rather than a flat row: the further one
// sits from the middle, the lower it drops, the more it tilts away, and the
// smaller and fainter it gets -- so the middle slot reads as the selection and
// its neighbours as peeking round the curve. Distances are measured in card
// widths from the middle.
const WHEEL_ARC_DROP_PX = 30;
const WHEEL_ARC_TILT_DEG = 10;
const WHEEL_ARC_SCALE_FALLOFF = 0.16;
const WHEEL_ARC_FADE = 0.32;

/**
 * Small canvas texture drawn in whole pixels and filtered the same way as
 * the loaded sprite art (see `loadTexture`), so procedurally-drawn bus
 * panels read as the same pixel-art style as the cropped sprite faces.
 * `draw` receives a `rect(x, y, w, h, color)` helper plus the canvas size.
 */
function createPixelTexture(width, height, draw) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  const rect = (x, y, w, h, color) => {
    ctx.fillStyle = color;
    ctx.fillRect(Math.round(x), Math.round(y), Math.max(1, Math.round(w)), Math.max(1, Math.round(h)));
  };
  draw(rect, width, height);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  return texture;
}

/**
 * One side panel of one bus section: window band with pillars, doors (on
 * the door side only), wheel arches and skirt. Drawn in "distance from the
 * section's front end" coordinates; `mirrored` flips it for the -x face,
 * whose U runs rear-to-front (BoxGeometry's +x face runs front-to-rear).
 * Buses are built facing local +z, so the right-hand, curb/door side --
 * Danish buses board on the right -- is local -x.
 */
function createBusSideTexture(section, C, { withDoors, mirrored }) {
  const W = Math.round(section.length * BUS_PIXELS_PER_UNIT);
  const H = Math.round(BUS_BODY_HEIGHT * BUS_PIXELS_PER_UNIT);
  return createPixelTexture(W, H, (fill) => {
    const rect = (x, y, w, h, color) => fill(mirrored ? W - x - w : x, y, w, h, color);
    const winTop = Math.round(H * 0.1);
    const winBottom = Math.round(H * 0.55);
    const skirtTop = H - Math.round(H * 0.09);

    rect(0, 0, W, H, C.body);
    rect(0, 0, W, 2, C.bodyLight);

    // Continuous dark window band, split into panes by body-colored pillars.
    // The front section's windscreen wraps round the corner (taller glass at
    // the very front); the rear section ends in a solid corner with a tail
    // light, so front and rear read from the side too.
    const winStart = section.isFront ? 0 : 3;
    const winEnd = section.isRear ? W - 7 : W - 3;
    rect(winStart, winTop, winEnd - winStart, winBottom - winTop, C.glass);
    rect(winStart, winBottom - 2, winEnd - winStart, 2, C.glassDark);
    if (section.isFront) rect(0, winTop, 6, winBottom - winTop + 6, C.glass);
    const paneWidth = BUS_PIXELS_PER_UNIT * 1.1;
    for (let x = winStart + 6; x < winEnd - 12; x += paneWidth) {
      for (let i = 0; i < 3; i++) rect(x + 8 - i * 2, winTop + 3 + i * 2, 2, 2, C.glassGlint);
    }
    for (let x = winStart + paneWidth; x < winEnd - 8; x += paneWidth) {
      rect(x, winTop, 3, winBottom - winTop, C.body);
    }
    if (section.isRear) rect(W - 3, winBottom + 4, 3, 8, C.tailLight);

    if (withDoors) {
      for (const [start, end] of section.doors) {
        const x0 = start * W;
        const x1 = end * W;
        rect(x0 - 2, winTop - 2, x1 - x0 + 4, skirtTop - winTop + 2, C.bodyShade);
        rect(x0, winTop, x1 - x0, skirtTop - winTop, C.glass);
        rect((x0 + x1) / 2 - 1, winTop, 2, skirtTop - winTop, C.doorEdge);
        rect(x0, winBottom + 3, x1 - x0, 1, C.doorEdge);
      }
    }

    rect(0, skirtTop - 2, W, 2, C.bodyShade);
    rect(0, skirtTop, W, H - skirtTop, C.skirt);

    // Arches sit behind the real 3D wheels (see `buildBusTemplate`) so the
    // body reads as cut out around them rather than wheels pasted on.
    const archCenterY = ((BUS_ROOF_HEIGHT - BUS_WHEEL_RADIUS) / BUS_BODY_HEIGHT) * H;
    const archRadius = (BUS_WHEEL_RADIUS + 0.05) * BUS_PIXELS_PER_UNIT;
    for (const axle of section.axles) {
      const cx = axle * W;
      for (let y = Math.floor(archCenterY - archRadius); y < H; y++) {
        const dy = y + 0.5 - archCenterY;
        const half = dy >= 0 ? archRadius : Math.sqrt(Math.max(0, archRadius * archRadius - dy * dy));
        rect(cx - half, y, half * 2, 1, C.arch);
      }
    }
  });
}

/**
 * Roof panel for one bus section: flat body color with edge shading and
 * cross seams every ~1 unit. Deliberately flat -- no AC pods or vents
 * modelled on top -- so it reads unambiguously as something to run on, and
 * the seams streaming past underfoot sell the speed while riding it.
 * BoxGeometry's +y face maps canvas top to the section's rear.
 */
function createBusRoofTexture(section, C) {
  const W = Math.round(BUS_WIDTH * BUS_PIXELS_PER_UNIT);
  const H = Math.round(section.length * BUS_PIXELS_PER_UNIT);
  return createPixelTexture(W, H, (rect) => {
    rect(0, 0, W, H, C.body);
    rect(0, 0, 2, H, C.bodyShade);
    rect(W - 2, 0, 2, H, C.bodyShade);
    rect(2, 0, 2, H, C.bodyLight);
    for (let y = BUS_PIXELS_PER_UNIT; y < H - 4; y += BUS_PIXELS_PER_UNIT * 1.1) {
      rect(4, y, W - 8, 1, C.bodyShade);
    }
    if (section.isFront) rect(0, H - 3, W, 3, C.bodyLight);
    if (section.isRear) rect(0, 0, W, 3, C.bodyShade);
  });
}

/**
 * The accordion bellows between articulated sections: high-contrast dark
 * ribs so the articulation stays obvious against the yellow at gameplay
 * distance. Ribs run across the bus, so they're vertical stripes on the
 * sides (U = along the bus) and horizontal ones on top (V = along the bus).
 */
function createBellowsTexture(alongU) {
  const ribCount = 6;
  const ribPx = 4;
  const along = ribCount * ribPx;
  const across = Math.round((alongU ? BUS_BODY_HEIGHT : BUS_WIDTH) * BUS_PIXELS_PER_UNIT);
  const W = alongU ? along : across;
  const H = alongU ? across : along;
  return createPixelTexture(W, H, (rect) => {
    rect(0, 0, W, H, BUS_COLORS.bellowsDark);
    for (let i = 0; i < ribCount; i++) {
      const offset = i * ribPx + 1;
      if (alongU) rect(offset, 0, 2, H, BUS_COLORS.bellowsLight);
      else rect(0, offset, W, 2, BUS_COLORS.bellowsLight);
    }
  });
}

function createBusFaceMaterial(map, brightness) {
  return new THREE.MeshLambertMaterial({ map, color: new THREE.Color(brightness, brightness, brightness) });
}

/**
 * Materials shared by both bus variants and every pooled instance of one
 * livery. `palette` is BUS_COLORS merged with that livery's overrides.
 * The windscreen end ends up facing away from the player and the rear end
 * toward them (see the end of `buildBusTemplate`), hence the brightnesses.
 */
function createBusMaterials(palette, frontTexture, rearTexture, shadowTexture) {
  const lambert = (options) => new THREE.MeshLambertMaterial(options);
  return {
    front: createBusFaceMaterial(frontTexture, BUS_FAR_END_BRIGHTNESS),
    rear: createBusFaceMaterial(rearTexture, BUS_NEAR_END_BRIGHTNESS),
    endCap: lambert({ color: new THREE.Color(palette.body).multiplyScalar(BUS_SIDE_BRIGHTNESS) }),
    underside: lambert({ color: BUS_COLORS.arch }),
    bellowsSide: lambert({ map: createBellowsTexture(true) }),
    bellowsTop: lambert({ map: createBellowsTexture(false) }),
    wheel: lambert({ color: BUS_COLORS.wheel }),
    hub: lambert({ color: BUS_COLORS.hub }),
    mirror: lambert({ color: BUS_COLORS.skirt }),
    shadow: new THREE.MeshBasicMaterial({ map: shadowTexture, transparent: true, depthWrite: false, opacity: 0.8 }),
  };
}

/**
 * Builds one bus variant from its OBSTACLE_DEFS entry: one textured box per
 * body section, a bellows box between consecutive sections, chunky 8-sided
 * wheels at each axle, front mirrors, and a soft ground shadow. Built with
 * its front at local +z, then turned round (see the end of this function)
 * so it faces away from the player. Local origin is ground level at the
 * bus's lengthwise center, so the turn doesn't move its collision extent.
 * Visual only -- collision treats the whole bus as one box with one continuous roof (see `getSupportHeight`), so the bellows
 * (sitting a hair below the roof) can never snag the player.
 */
function buildBusTemplate(def, shared, palette) {
  const group = new THREE.Group();
  const bodyLength = def.length - BUS_JOINT_LENGTH * (def.sections.length - 1);
  const sections = def.sections.map((section, i) => ({
    ...section,
    length: section.fraction * bodyLength,
    isFront: i === 0,
    isRear: i === def.sections.length - 1,
  }));

  const wheelGeometry = new THREE.CylinderGeometry(BUS_WHEEL_RADIUS, BUS_WHEEL_RADIUS, BUS_WHEEL_WIDTH, 8);
  wheelGeometry.rotateZ(Math.PI / 2);
  const hubGeometry = new THREE.CylinderGeometry(BUS_WHEEL_RADIUS * 0.45, BUS_WHEEL_RADIUS * 0.45, BUS_WHEEL_WIDTH + 0.02, 8);
  hubGeometry.rotateZ(Math.PI / 2);
  const wheelX = BUS_WIDTH / 2 - BUS_WHEEL_WIDTH / 2 + 0.03;

  let frontZ = def.length / 2;
  sections.forEach((section, i) => {
    const materials = [
      createBusFaceMaterial(createBusSideTexture(section, palette, { withDoors: false, mirrored: false }), BUS_SIDE_BRIGHTNESS),
      createBusFaceMaterial(createBusSideTexture(section, palette, { withDoors: true, mirrored: true }), BUS_SIDE_BRIGHTNESS),
      createBusFaceMaterial(createBusRoofTexture(section, palette), BUS_ROOF_BRIGHTNESS),
      shared.underside,
      section.isFront ? shared.front : shared.endCap,
      section.isRear ? shared.rear : shared.endCap,
    ];
    const body = new THREE.Mesh(new THREE.BoxGeometry(BUS_WIDTH, BUS_BODY_HEIGHT, section.length), materials);
    body.position.set(0, BUS_BODY_BOTTOM + BUS_BODY_HEIGHT / 2, frontZ - section.length / 2);
    group.add(body);

    for (const axle of section.axles) {
      const z = frontZ - axle * section.length;
      for (const side of [-1, 1]) {
        const wheel = new THREE.Mesh(wheelGeometry, shared.wheel);
        const hub = new THREE.Mesh(hubGeometry, shared.hub);
        wheel.position.set(side * wheelX, BUS_WHEEL_RADIUS, z);
        hub.position.copy(wheel.position);
        group.add(wheel, hub);
      }
    }

    frontZ -= section.length;
    if (i < sections.length - 1) {
      const jointHeight = BUS_BODY_HEIGHT - 0.02;
      const joint = new THREE.Mesh(new THREE.BoxGeometry(BUS_WIDTH * 0.97, jointHeight, BUS_JOINT_LENGTH), [
        shared.bellowsSide,
        shared.bellowsSide,
        shared.bellowsTop,
        shared.underside,
        shared.underside,
        shared.underside,
      ]);
      joint.position.set(0, BUS_BODY_BOTTOM + jointHeight / 2, frontZ - BUS_JOINT_LENGTH / 2);
      group.add(joint);
      frontZ -= BUS_JOINT_LENGTH;
    }
  });

  // "Rabbit ear" mirrors reaching forward from the top front corners -- a
  // strong city-bus silhouette cue, poking out past the far end.
  const armGeometry = new THREE.BoxGeometry(0.05, 0.05, 0.16);
  const mirrorGeometry = new THREE.BoxGeometry(0.06, 0.24, 0.06);
  for (const side of [-1, 1]) {
    const arm = new THREE.Mesh(armGeometry, shared.mirror);
    arm.position.set(side * (BUS_WIDTH / 2 + 0.02), BUS_ROOF_HEIGHT - 0.12, def.length / 2 + 0.06);
    const mirror = new THREE.Mesh(mirrorGeometry, shared.mirror);
    mirror.position.set(side * (BUS_WIDTH / 2 + 0.04), BUS_ROOF_HEIGHT - 0.3, def.length / 2 + 0.14);
    group.add(arm, mirror);
  }

  const shadow = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), shared.shadow);
  shadow.rotation.x = -Math.PI / 2;
  shadow.scale.set(BUS_WIDTH * 1.5, def.length * 1.1, 1);
  shadow.position.y = 0.015;
  group.add(shadow);

  // Buses face away from the player, as if driving the same way down the
  // street: the rear (tail lights, rear window) is the end that approaches
  // and that the player jumps up onto, and the front/windscreen is the end
  // they run off. Also puts the door (right-hand) side on world +x.
  group.rotation.y = Math.PI;

  return group;
}

/** Lightens (amount > 0) or darkens (amount < 0) a '#rrggbb' color. */
function adjustColor(hex, amount) {
  const color = new THREE.Color(hex);
  const target = amount > 0 ? new THREE.Color(1, 1, 1) : new THREE.Color(0, 0, 0);
  return `#${color.lerp(target, Math.abs(amount)).getHexString()}`;
}

/** Deterministic PRNG, so every house facade drawn from the same inputs is identical. */
function randomBetween(min, max) {
  return min + Math.random() * (max - min);
}

function createSeededRandom(seed) {
  let state = seed % 2147483647 || 1;
  return () => {
    state = (state * 16807) % 2147483647;
    return (state - 1) / 2147483646;
  };
}

/**
 * Street facade of one townhouse, as a pixel texture at
 * BUILDING_PIXELS_PER_UNIT: cornice at the top, a grid of white-framed
 * windows (the classic Danish cross-barred casement) on each upper floor,
 * a string course, then a darker ground floor with one door and shopfront
 * windows in the remaining bays.
 *
 * The canvas has one extra pure-white row on top that the facade itself
 * never shows: every other surface of the house (plain walls, roof,
 * chimney) samples that texel and gets its real color from vertex colors,
 * so a whole house draws with one material in one draw call (see
 * `buildHouseGeometry`). `userData.facadeFraction` is the share of the
 * texture's height the facade occupies.
 */
function createFacadeTexture(width, upperFloors, wallColor, seed) {
  const unit = BUILDING_PIXELS_PER_UNIT;
  const W = Math.round(width * unit);
  const floorPx = BUILDING_FLOOR_HEIGHT * unit;
  const groundPx = Math.round(BUILDING_GROUND_FLOOR_HEIGHT * unit);
  const H = Math.round(upperFloors * floorPx) + groundPx;
  const random = createSeededRandom(seed);
  const wallLight = adjustColor(wallColor, 0.18);
  const wallShade = adjustColor(wallColor, -0.14);
  const frame = BUILDING_FRAME_COLOR;
  const glass = BUILDING_GLASS_COLOR;
  const glint = BUILDING_GLINT_COLOR;
  const texture = createPixelTexture(W, H + 1, (fill) => {
    fill(0, 0, W, 1, '#ffffff');
    const rect = (x, y, w, h, color) => fill(x, y + 1, w, h, color);
    rect(0, 0, W, H, wallColor);
    rect(0, 0, W, 3, wallLight);
    rect(0, 3, W, 1, wallShade);

    const bays = Math.max(2, Math.round(width / 0.95));
    const bayWidth = W / bays;
    const windowWidth = Math.max(4, Math.round(bayWidth * 0.5));
    const windowHeight = Math.round(floorPx * 0.62);
    for (let floor = 0; floor < upperFloors; floor++) {
      const y = Math.round(4 + floor * floorPx + floorPx * 0.16);
      for (let bay = 0; bay < bays; bay++) {
        const x = Math.round(bay * bayWidth + (bayWidth - windowWidth) / 2);
        // A scattering of windows with the light on (night themes only --
        // BUILDING_LIT_WINDOW_CHANCE is 0 by day). Seeded with the rest of the
        // facade, so a given house always lights the same windows.
        const lit = random() < BUILDING_LIT_WINDOW_CHANCE;
        const pane = lit
          ? BUILDING_LIT_WINDOW_COLORS[Math.floor(random() * BUILDING_LIT_WINDOW_COLORS.length)]
          : glass;
        rect(x - 1, y - 1, windowWidth + 2, windowHeight + 2, frame);
        rect(x, y, windowWidth, windowHeight, pane);
        rect(x + 1, y + 1, 1, 2, lit ? adjustColor(pane, 0.25) : glint);
        rect(x + Math.floor(windowWidth / 2), y, 1, windowHeight, frame);
        rect(x, y + Math.round(windowHeight * 0.36), windowWidth, 1, frame);
        rect(x - 1, y + windowHeight + 1, windowWidth + 2, 1, lit ? adjustColor(pane, -0.45) : wallShade);
      }
    }

    const groundTop = H - groundPx;
    rect(0, groundTop - 2, W, 2, wallLight);
    rect(0, groundTop, W, groundPx, wallShade);
    const doorBay = Math.floor(random() * bays);
    const doorColor = BUILDING_DOOR_COLORS[Math.floor(random() * BUILDING_DOOR_COLORS.length)];
    for (let bay = 0; bay < bays; bay++) {
      if (bay === doorBay) {
        const doorWidth = Math.round(bayWidth * 0.55);
        const doorHeight = Math.round(groundPx * 0.72);
        const x = Math.round(bay * bayWidth + (bayWidth - doorWidth) / 2);
        rect(x - 1, H - doorHeight - 1, doorWidth + 2, doorHeight + 1, frame);
        rect(x, H - doorHeight, doorWidth, doorHeight, doorColor);
        rect(x + Math.floor(doorWidth / 2), H - doorHeight, 1, doorHeight, adjustColor(doorColor, -0.3));
      } else {
        const shopWidth = Math.round(bayWidth * 0.72);
        const shopHeight = Math.round(groundPx * 0.5);
        const x = Math.round(bay * bayWidth + (bayWidth - shopWidth) / 2);
        const y = groundTop + Math.round(groundPx * 0.18);
        const litShop = random() < BUILDING_LIT_WINDOW_CHANCE * 1.5;
        const shopPane = litShop
          ? BUILDING_LIT_WINDOW_COLORS[Math.floor(random() * BUILDING_LIT_WINDOW_COLORS.length)]
          : glass;
        rect(x - 1, y - 1, shopWidth + 2, shopHeight + 2, frame);
        rect(x, y, shopWidth, shopHeight, shopPane);
        rect(x + 1, y + 1, 2, 1, litShop ? adjustColor(shopPane, 0.25) : glint);
      }
    }
    rect(0, H - 1, W, 1, adjustColor(wallColor, -0.35));
  });
  texture.userData.facadeFraction = H / (H + 1);
  return texture;
}

/**
 * One house as a single non-indexed geometry: body box, pitched roof prism
 * and optional chimney merged together, in the house's local space (origin
 * at ground level, centered on its footprint). Only the street face maps
 * the facade texture; everything else samples its reserved white texel and
 * takes its color from vertex colors. Every vertex color is also scaled by
 * the light level its normal receives (see `lambertLightLevel`), so each
 * surface lands at BUILDING_TARGET_LIGHT_LEVEL of its intended color.
 */
function buildHouseGeometry({ side, width, wallHeight, gableToStreet, roofHeight, chimney, wallColor, roofColor, facadeFraction }) {
  const positions = [];
  const normals = [];
  const uvs = [];
  const colors = [];
  const white = new THREE.Color(1, 1, 1);
  const normal = new THREE.Vector3();
  // Top-left texel of the reserved white row.
  const plainUV = [0.001, 1 - (1 - facadeFraction) / 2];

  const add = (geometry, surfaceAt) => {
    const source = geometry.index ? geometry.toNonIndexed() : geometry;
    const position = source.getAttribute('position');
    const sourceNormal = source.getAttribute('normal');
    const uv = source.getAttribute('uv');
    for (let i = 0; i < position.count; i++) {
      const { color, facade } = surfaceAt(i, source);
      positions.push(position.getX(i), position.getY(i), position.getZ(i));
      normal.fromBufferAttribute(sourceNormal, i);
      normals.push(normal.x, normal.y, normal.z);
      if (facade) uvs.push(uv.getX(i), uv.getY(i) * facadeFraction);
      else uvs.push(...plainUV);
      const brightness = BUILDING_TARGET_LIGHT_LEVEL / lambertLightLevel([normal.x, normal.y, normal.z]);
      colors.push(color.r * brightness, color.g * brightness, color.b * brightness);
    }
    geometry.dispose();
    if (source !== geometry) source.dispose();
  };

  // BoxGeometry face order is [+x, -x, +y, -y, +z, -z], 6 vertices each once
  // non-indexed; the street face is +x on the left row, -x on the right.
  const wall = new THREE.Color(wallColor);
  const streetFace = side < 0 ? 0 : 1;
  const body = new THREE.BoxGeometry(BUILDING_DEPTH, wallHeight, width).translate(0, wallHeight / 2, 0);
  add(body, (i) => (Math.floor(i / 6) === streetFace ? { color: white, facade: true } : { color: wall }));

  // Triangular prism; ExtrudeGeometry's first group is the two triangular
  // end caps (plastered wall -- a street-facing gable), the rest the slopes.
  const roofSpan = gableToStreet ? width : BUILDING_DEPTH;
  const roofLength = gableToStreet ? BUILDING_DEPTH : width;
  const shape = new THREE.Shape([
    new THREE.Vector2(-roofSpan / 2, 0),
    new THREE.Vector2(roofSpan / 2, 0),
    new THREE.Vector2(0, roofHeight),
  ]);
  const roof = new THREE.ExtrudeGeometry(shape, { depth: roofLength, bevelEnabled: false });
  roof.translate(0, 0, -roofLength / 2);
  if (gableToStreet) roof.rotateY(Math.PI / 2);
  roof.translate(0, wallHeight, 0);
  const capCount = roof.groups[0].count;
  const roofTiles = new THREE.Color(roofColor);
  add(roof, (i) => ({ color: i < capCount ? wall : roofTiles }));

  if (chimney) {
    const chimneyGeometry = new THREE.BoxGeometry(0.32, 0.9, 0.32).translate(chimney.x, chimney.y, chimney.z);
    const brick = new THREE.Color(BUILDING_CHIMNEY_COLOR);
    add(chimneyGeometry, () => ({ color: brick }));
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  return geometry;
}

/**
 * One soft puff of fog. Wisps are built up from blurred blobs, then the
 * whole thing is multiplied by a radial falloff so the alpha reaches zero
 * well before the edge of the canvas -- otherwise every patch shows the
 * square edge of its own quad, which is exactly what a wisp of fog must not
 * do. Drawn once and shared by every patch; they differ by size, turn and
 * where they sit.
 */
function createMistTexture(seed = 5) {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const random = createSeededRandom(seed);
  const centre = size / 2;

  // Wisps: blobs clustered toward the middle, none close to the border.
  for (let i = 0; i < 26; i++) {
    const angle = random() * Math.PI * 2;
    const distance = random() * size * 0.22;
    const x = centre + Math.cos(angle) * distance;
    const y = centre + Math.sin(angle) * distance * 0.7;
    const radius = size * (0.09 + random() * 0.15);
    const alpha = 0.1 + random() * 0.22;
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
    gradient.addColorStop(0, `rgba(255,255,255,${alpha})`);
    gradient.addColorStop(0.5, `rgba(255,255,255,${alpha * 0.4})`);
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  // Radial falloff: keeps the middle, fades everything toward the border to
  // nothing, so a patch has no edge of its own.
  const image = ctx.getImageData(0, 0, size, size);
  const data = image.data;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (x - centre) / centre;
      const dy = (y - centre) / centre;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const falloff = Math.max(0, 1 - distance);
      const index = (y * size + x) * 4 + 3;
      data[index] = Math.round(data[index] * falloff * falloff);
    }
  }
  ctx.putImageData(image, 0, 0);

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  return texture;
}

/**
 * A corner cobweb: spokes fanning out of one corner with threads strung
 * between them, drawn transparent so only the silk shows.
 */
function createBatTexture(color, eyeColor) {
  // Drawn as chunky pixels on a small canvas, like the rest of the game's
  // procedural art: a body with spread wings, read as a silhouette against
  // the night. Rows are the sprite top-down, each string a row of pixels.
  // Wings spread either side of a small body, ears on top, eyes picked out
  // in `eyeColor`. 'X' is the bat, 'e' an eye, '.' transparent.
  const rows = [
    '...X.......X...',
    '...XXXXXXXXX...',
    '.XXXXXeXeXXXXX.',
    'XXXXXXXXXXXXXXX',
    'XXXXXXXXXXXXXXX',
    'XXX.XXXXXXX.XXX',
    '.X...XXXXX...X.',
    '......XXX......',
  ];
  const scale = 4;
  const width = rows[0].length * scale;
  const height = rows.length * scale;
  return createPixelTexture(width, height, (rect) => {
    rows.forEach((row, y) => {
      [...row].forEach((cell, x) => {
        if (cell === '.') return;
        rect(x * scale, y * scale, scale, scale, cell === 'e' ? eyeColor : color);
      });
    });
  });
}

function createCobwebTexture(color) {
  const size = 48;
  return createPixelTexture(size, size, (rect) => {
    const spokes = 5;
    for (let i = 0; i <= spokes; i++) {
      const angle = (i / spokes) * (Math.PI / 2);
      for (let r = 2; r < size; r++) {
        const x = Math.round(Math.cos(angle) * r);
        const y = Math.round(Math.sin(angle) * r);
        if (x < size && y < size) rect(x, y, 1, 1, color);
      }
    }
    // Threads: an arc between each pair of spokes, at a few radii.
    for (const radius of [10, 18, 26, 34, 42]) {
      for (let step = 0; step <= 40; step++) {
        const angle = (step / 40) * (Math.PI / 2);
        // Sag between spokes, like real silk.
        const sag = 1 + 0.08 * Math.sin((step / 40) * Math.PI * spokes);
        const x = Math.round(Math.cos(angle) * radius * sag);
        const y = Math.round(Math.sin(angle) * radius * sag);
        if (x >= 0 && y >= 0 && x < size && y < size) rect(x, y, 1, 1, color);
      }
    }
  });
}

/**
 * Merges colored boxes (see TREE_BOXES / LAMP_BOXES) into one flat-shaded,
 * vertex-colored geometry, with each face's color pre-scaled by the light it
 * receives -- same idea as `buildHouseGeometry` -- so the street furniture
 * sits at BUILDING_TARGET_LIGHT_LEVEL alongside the houses. `glow` boxes are
 * scaled to show at full color regardless of lighting. With `unlit` (for an
 * unlit material, e.g. the clouds) colors aren't light-compensated; instead
 * undersides are shaded a little grey and sides slightly, for volume.
 */
function buildColoredBoxesGeometry(boxes, { unlit = false } = {}) {
  const positions = [];
  const normals = [];
  const colors = [];
  const normal = new THREE.Vector3();
  for (const { size, position, color, glow } of boxes) {
    const box = new THREE.BoxGeometry(...size).translate(...position).toNonIndexed();
    const boxPositions = box.getAttribute('position');
    const boxNormals = box.getAttribute('normal');
    const base = new THREE.Color(color);
    for (let i = 0; i < boxPositions.count; i++) {
      positions.push(boxPositions.getX(i), boxPositions.getY(i), boxPositions.getZ(i));
      normal.fromBufferAttribute(boxNormals, i);
      normals.push(normal.x, normal.y, normal.z);
      let brightness;
      if (unlit) brightness = normal.y > 0.5 ? 1 : normal.y < -0.5 ? 0.84 : 0.94;
      else brightness = (glow ? 1 : BUILDING_TARGET_LIGHT_LEVEL) / lambertLightLevel([normal.x, normal.y, normal.z]);
      colors.push(base.r * brightness, base.g * brightness, base.b * brightness);
    }
    box.dispose();
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  return geometry;
}

/**
 * A low-poly 3-lane endless runner rendered with Three.js. Owns the scene,
 * game loop, input handling and object pooling. Knows nothing about the
 * surrounding DOM beyond the canvas it's given and two callbacks.
 */
class RunnerGame {
  /**
   * @param {HTMLCanvasElement} canvas
   * @param {{ onScoreChange: (score: number) => void, onGameOver: (score: number, best: number, isNewHigh: boolean) => void, onPenalty?: (points: number) => void }} callbacks
   * @param {string} [characterId] initial selected character (defaults to DEFAULT_CHARACTER)
   */
  constructor(canvas, callbacks, characterId) {
    this.canvas = canvas;
    this.callbacks = callbacks;
    this.state = 'idle';
    this.active = false;
    this.rafId = null;
    this.lastFrameTime = 0;
    this.pendingCharacterId = characterId in CHARACTER_DEFS ? characterId : DEFAULT_CHARACTER;

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

    // One-shot sound effects (jump, coin collect, game over, voice lines)
    // play through the Web Audio API rather than plain `<audio>` elements.
    // The original `new Audio(url).cloneNode().play()` approach starts and
    // stops a fresh HTMLMediaElement -- and with it, on iOS, a fresh audio
    // session activation -- on every single trigger, which is exactly the
    // kind of "stopping and starting" that was causing audible lag on iOS.
    // A single AudioContext stays open and running for the whole game once
    // resumed (see `start()`); each effect is decoded once into an
    // AudioBuffer up front, and every trigger afterwards just schedules a
    // new AudioBufferSourceNode on the already-running context -- no
    // session start/stop per play, so no per-play latency.
    this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    this.soundBuffers = {}; // url -> AudioBuffer, populated by `loadSoundBuffer`
    this.collectSoundBuffer = this.loadSoundBuffer(COLLECT_SOUND_URL);
    this.jumpSoundBuffer = this.loadSoundBuffer(JUMP_SOUND_URL);
    this.gameOverSoundBuffer = this.loadSoundBuffer(GAME_OVER_SOUND_URL);
    // Background gameplay music: one shared looping element, played at a
    // constant rate (see the `DIFFICULTY_DISTANCE_SCALE` comment for why it
    // no longer speeds up with difficulty). Left as a plain `<audio>`
    // element rather than moved to the AudioBuffer system above -- it's
    // only started/stopped once or twice per run (not the frequent
    // per-trigger case the lag was about), and at ~1.3MB decoding it
    // eagerly into a raw PCM AudioBuffer would cost far more memory than
    // just streaming it.
    // preload: 'none' -- fetching it immediately on page load (before the
    // player has even tapped Play) competed with everything else the page
    // was loading and was heavy enough to visibly hurt mobile input
    // responsiveness. `start()` kicks off the actual load via `.play()`.
    this.musicSound = new Audio(MUSIC_SOUND_URL);
    this.musicSound.preload = 'none';
    this.musicSound.loop = true;
    // Character voice lines are per-character (see `buildCharacter`), not
    // created here.
    this.voiceUrls = [];

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(SKY_HORIZON_COLOR);
    this.scene.fog = new THREE.Fog(SKY_HORIZON_COLOR, FOG_NEAR, FOG_FAR);

    this.camera = new THREE.PerspectiveCamera(60, 1, 0.1, 200);

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(canvas.parentElement || canvas);

    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.tick = this.tick.bind(this);

    canvas.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    canvas.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    window.addEventListener('keydown', this.handleKeyDown);

    this.buildScene();
    this.handleResize();
    this.resetPlayer();
    this.setUpStats();
    this.compileShaders();
    this.warmUpTextures();
    this.render();
  }

  /**
   * Compiles every shader program up front. The pooled obstacles are hidden
   * until first spawned, and `renderer.compile` only visits visible objects,
   * so the first bus/rider/crate to appear mid-run would otherwise stall the
   * frame compiling its program.
   */
  /** Creates the ?game-stats=1 readout, or nothing at all when it isn't asked for. */
  setUpStats() {
    this.stats = null;
    try {
      if (new URLSearchParams(window.location.search).get('game-stats') !== '1') return;
      const el = document.createElement('div');
      el.style.cssText =
        'position:absolute;top:0;left:0;z-index:20;background:rgba(0,0,0,.72);color:#9f9;' +
        'font:11px/1.5 ui-monospace,monospace;padding:3px 6px;pointer-events:none;white-space:nowrap';
      (this.canvas.parentElement ?? document.body).appendChild(el);
      this.stats = { el, frames: 0, total: 0, worst: 0, dropped: 0, since: performance.now() };
    } catch {
      // Diagnostics must never stop the game running.
    }
  }

  compileShaders() {
    const hidden = [];
    this.scene.traverse((object) => {
      if (!object.visible) {
        hidden.push(object);
        object.visible = true;
      }
    });
    this.renderer.compile(this.scene, this.camera);
    for (const object of hidden) object.visible = false;
  }

  /**
   * Uploads every texture to the GPU ahead of time. Three.js otherwise
   * uploads a texture the first time a visible material draws it -- so the
   * first rider (a ~2000px atlas), each new house facade, and each new
   * pickup or character frame cost a several-millisecond hitch mid-run.
   * Textures whose image hasn't arrived yet are skipped; `loadTexture`
   * schedules another pass as each one loads.
   */
  warmUpTextures() {
    this.warmUpTimer = null;
    for (let widthIndex = 0; widthIndex < BUILDING_WIDTHS.length; widthIndex++) {
      for (const upperFloors of BUILDING_UPPER_FLOORS) {
        for (let colorIndex = 0; colorIndex < BUILDING_WALL_COLORS.length; colorIndex++) {
          this.getFacadeMaterial(widthIndex, upperFloors, colorIndex);
        }
      }
    }
    const textures = new Set();
    const addMaterial = (material) => {
      if (material?.map) textures.add(material.map);
    };
    this.scene.traverse((object) => {
      if (Array.isArray(object.material)) object.material.forEach(addMaterial);
      else addMaterial(object.material);
    });
    for (const material of this.buildingMaterials.values()) addMaterial(material);
    for (const frames of Object.values(this.runFrameTextures)) frames.forEach((texture) => textures.add(texture));
    for (const texture of [...this.jumpFrameTextures, ...this.injuredFrameTextures, ...this.pickupTextures]) {
      textures.add(texture);
    }
    for (const texture of textures) {
      if (texture.image?.width > 0) this.renderer.initTexture(texture);
    }
  }

  scheduleTextureWarmUp() {
    if (this.destroyed || this.warmUpTimer) return;
    this.warmUpTimer = setTimeout(() => this.warmUpTextures(), 50);
  }

  /**
   * Builds everything that only needs to exist once: lights, ground, player
   * mesh, and the pooled obstacle/coin meshes (created hidden, reused for
   * the whole lifetime of the component rather than allocated per spawn).
   */
  buildScene() {
    // Combined intensity kept close to 1.0 (for a surface facing the light
    // fairly directly, roughly ambient + directional): with no tone mapping
    // set on the renderer, WebGL hard-clips any color over 1.0 straight to
    // white instead of rolling it off softly. At the old 0.65 + 0.9 = 1.55,
    // most of each texture's midtones and highlights were getting clipped
    // together, crushing out the shading/shadow detail the character art
    // has painted into it and reading as "washed out" -- especially
    // noticeable on Jas's lighter purple palette.
    //
    // (Note: this three.js build's physically-based light units divide those
    // intensities by PI for Lambert surfaces, so in practice the ambient+sun
    // pair alone left the scene quite dark -- see `lambertLightLevel`. The
    // back light below brings camera-facing surfaces up without pushing any
    // of them anywhere near clipping.)
    const ambient = new THREE.AmbientLight(AMBIENT_LIGHT_COLOR, AMBIENT_LIGHT_INTENSITY);
    const sun = new THREE.DirectionalLight(SUN_LIGHT_COLOR, SUN_LIGHT_INTENSITY);
    sun.position.set(...SUN_LIGHT_POSITION);
    const backLight = new THREE.DirectionalLight(BACK_LIGHT_COLOR, BACK_LIGHT_INTENSITY);
    backLight.position.set(...BACK_LIGHT_POSITION);
    this.scene.add(ambient, sun, backLight);

    const groundTexture = this.createGroundTexture();
    this.groundTexture = groundTexture;

    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(GROUND_WIDTH, GROUND_LENGTH),
      new THREE.MeshLambertMaterial({ map: groundTexture })
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.z = -150;
    this.scene.add(ground);

    this.buildSky();
    if (GROUND_MIST) this.buildGroundMist();
    if (FLYING_BATS) this.buildFlyingBats();
    if (FOG_BANKS?.volume) this.buildFogVolume();
    if (FOG_BANKS) {
      // Starts clear, so a run never opens inside a bank.
      this.fogBank = { inside: false, intensity: 0, timer: randomBetween(...(FOG_BANKS.firstClearRange ?? [8, 16])) };
    }
    this.buildWeather();
    this.buildPuddles();
    this.buildStreet();
    this.buildStreetProps();

    this.player = this.buildCharacter(this.pendingCharacterId);
    this.scene.add(this.player);

    // A soft blob shadow decal under the character's feet -- without any
    // ground-contact cue a blocky character reads as floating rather than
    // standing on the track, especially once it's airborne.
    this.shadow = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({ map: this.createShadowTexture(), transparent: true, depthWrite: false })
    );
    this.shadow.rotation.x = -Math.PI / 2;
    this.shadow.position.y = 0.02;
    this.scene.add(this.shadow);

    // Low/box obstacles are real 3D boxes with the same flat crate art
    // applied to all 6 faces -- not a true 6-sided unwrap, but real box
    // geometry means the obstacle still has an actual silhouette/edge
    // instead of a paper-flat cutout. The source crop matters here: it has
    // to be a genuinely flat, straight-on face render. An earlier version
    // used a crop from the sheet's 3D-angled row instead -- an image that
    // already has its own fake perspective/depth baked in -- and mapping
    // that onto real box geometry produced a mismatched double-perspective
    // that looked like the crate was twisting independently of the box as
    // the camera moved.
    const obstacleBrightness = OBSTACLE_TARGET_LIGHT_LEVEL / lambertLightLevel([0, 0, 1]);
    const obstacleColor = new THREE.Color(obstacleBrightness, obstacleBrightness, obstacleBrightness);
    const lowMaterial = new THREE.MeshLambertMaterial({
      map: this.loadTexture(LOW_OBSTACLE_URL),
      color: obstacleColor,
      transparent: true,
      alphaTest: 0.1,
    });
    const lowWidth = LOW_OBSTACLE_HEIGHT * LOW_OBSTACLE_ASPECT;
    const lowGeometry = new THREE.BoxGeometry(lowWidth, LOW_OBSTACLE_HEIGHT, lowWidth);

    // Tall/cactus obstacles are a cross-billboard: two copies of the same
    // flat art crossed at 90 degrees, rather than one flat plane. Real
    // (if minimal) geometry and a sense of thickness from any angle, without
    // needing art that properly unwraps around a cylinder.
    const tallMaterial = new THREE.MeshLambertMaterial({
      map: this.loadTexture(TALL_OBSTACLE_URL),
      color: obstacleColor,
      transparent: true,
      alphaTest: 0.1,
      side: THREE.DoubleSide,
    });
    const tallGeometry = new THREE.PlaneGeometry(TALL_OBSTACLE_HEIGHT * TALL_OBSTACLE_ASPECT, TALL_OBSTACLE_HEIGHT);

    this.obstacles = [];
    const pointObstacleTypes = Object.entries(OBSTACLE_POOL_SIZES).flatMap(([type, count]) => Array(count).fill(type));
    for (const type of pointObstacleTypes) {
      let mesh;
      if (type === 'low') {
        mesh = new THREE.Mesh(lowGeometry, lowMaterial);
      } else {
        mesh = new THREE.Group();
        mesh.add(new THREE.Mesh(tallGeometry, tallMaterial));
        if (TALL_OBSTACLE_CROSS) {
          const crossed = new THREE.Mesh(tallGeometry, tallMaterial);
          crossed.rotation.y = Math.PI / 2;
          mesh.add(crossed);
        }
      }
      mesh.visible = false;
      this.scene.add(mesh);
      this.obstacles.push({ mesh, type, lane: 0, z: SPAWN_Z, baseY: 0, active: false });
    }

    // Buses share the obstacle pool (and with it spawning, movement and
    // despawn), distinguished only by their OBSTACLE_DEFS entry plus a
    // livery. One template per variant per livery, cloned for the rest of
    // its pool -- clones share the template's geometries/materials/textures
    // rather than redrawing them per instance.
    const busShadowTexture = this.createShadowTexture();
    for (const livery of BUS_LIVERY_ORDER) {
      const { frontUrl, rearUrl, ...colors } = BUS_LIVERIES[livery];
      const palette = { ...BUS_COLORS, ...colors };
      const materials = createBusMaterials(
        palette,
        this.loadTexture(frontUrl),
        this.loadTexture(rearUrl),
        busShadowTexture
      );
      for (const type of BUS_TYPES) {
        const template = buildBusTemplate(OBSTACLE_DEFS[type], materials, palette);
        for (let i = 0; i < BUS_POOL_SIZE; i++) {
          const mesh = i === 0 ? template : template.clone();
          mesh.visible = false;
          this.scene.add(mesh);
          this.obstacles.push({ mesh, type, livery, lane: 0, z: SPAWN_Z, baseY: 0, active: false });
        }
      }
    }

    // Riders: one textured plane each (plus a blob shadow), all of one
    // character sharing a geometry and the atlas image. Each rider gets its
    // own clone of the atlas texture so it can show its own frame (clones
    // share the uploaded image, only offset/repeat differ).
    const riderShadowMaterial = new THREE.MeshBasicMaterial({
      map: busShadowTexture,
      transparent: true,
      depthWrite: false,
      opacity: 0.7,
    });
    const riderShadowGeometry = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    this.riderBaseTextures = [];
    for (const kind of RIDER_KINDS) {
      const def = RIDER_DEFS[kind];
      const cells = def.sideFrames + def.backFrames + 1;
      const clones = [];
      // Clones made before the image arrives don't get `loadTexture`'s
      // settings or upload trigger, so apply both to them once it loads.
      const baseTexture = this.loadTexture(RIDER_URLS[kind], (loaded) => {
        for (const texture of clones) {
          texture.colorSpace = loaded.colorSpace;
          texture.magFilter = loaded.magFilter;
          texture.minFilter = loaded.minFilter;
          texture.needsUpdate = true;
        }
      });
      this.riderBaseTextures.push(baseTexture);
      const width = def.cellWidth / RIDER_PIXELS_PER_UNIT;
      const height = def.cellHeight / RIDER_PIXELS_PER_UNIT;
      const geometry = new THREE.PlaneGeometry(width, height).translate(0, height / 2, 0);
      for (let i = 0; i < RIDER_POOL_SIZE; i++) {
        const texture = baseTexture.clone();
        texture.repeat.set(1 / cells, 1);
        clones.push(texture);
        // A stricter alpha cutoff than the other sprites: the atlases are
        // resampled from larger art, so edges carry soft semi-transparent
        // pixels that would otherwise fringe the silhouette.
        const sprite = new THREE.Mesh(
          geometry,
          new THREE.MeshLambertMaterial({
            map: texture,
            color: obstacleColor,
            transparent: true,
            alphaTest: 0.5,
            side: THREE.DoubleSide,
          })
        );
        const shadow = new THREE.Mesh(riderShadowGeometry, riderShadowMaterial);
        shadow.position.y = 0.012;
        const mesh = new THREE.Group();
        mesh.add(shadow, sprite);
        mesh.visible = false;
        this.scene.add(mesh);
        this.obstacles.push({
          mesh,
          sprite,
          shadow,
          texture,
          cells,
          type: 'rider',
          riderKind: kind,
          lane: 0,
          x: 0,
          z: SPAWN_Z,
          baseY: 0,
          active: false,
        });
      }
    }

    // Pickups: a randomly-chosen collectible per spawn (grouped per trail --
    // see `spawnCoinTrail`) rather than a single repeated coin, and each is
    // Flat art with a sliver of edge: the packs are a shallow box, the banana
    // a shallow extrusion of its own traced outline (a box would frame the
    // curve with visible corners -- see `BANANA_SHAPE_POINTS`). Deep versions
    // of these read as solid blocks rather than the flat art they are.
    // `assignPickupVariant` picks the geometry and sizes it to each variant.
    this.coins = [];
    this.pickupSideMaterials = PICKUP_URLS.map(
      (_, i) =>
        new THREE.MeshLambertMaterial({
          color: new THREE.Color(PICKUP_SIDE_COLORS[i]).multiplyScalar(obstacleBrightness),
          emissive: new THREE.Color(PICKUP_SIDE_COLORS[i]).multiplyScalar(PICKUP_GLOW_INTENSITY),
        })
    );
    this.pickupTextures = PICKUP_URLS.map((url) => this.loadTexture(url));
    this.pickupFaceMaterials = this.pickupTextures.map(
      (texture) =>
        new THREE.MeshLambertMaterial({
          map: texture,
          color: obstacleColor,
          emissive: 0xffffff,
          emissiveMap: texture,
          emissiveIntensity: PICKUP_GLOW_INTENSITY,
          transparent: true,
          alphaTest: 0.1,
        })
    );
    // Two material slots each, so a pickup draws in two calls rather than one
    // per face: slot 0 the thin edges, slot 1 the art. For the box that means
    // regrouping it -- BoxGeometry ships a group per face ([+x,-x,+y,-y,+z,-z],
    // six indices each), and the four edge faces come first, so they merge
    // into one range. ExtrudeGeometry already has exactly two groups: the caps
    // (the banana's flat faces, which carry the art) then the walls.
    this.pickupMaterialSets = PICKUP_URLS.map((_, i) =>
      i === BANANA_VARIANT_INDEX
        ? [this.pickupFaceMaterials[i], this.pickupSideMaterials[i]]
        : [this.pickupSideMaterials[i], this.pickupFaceMaterials[i]]
    );
    this.pickupBoxGeometry = new THREE.BoxGeometry(1, 1, 1);
    this.pickupBoxGeometry.clearGroups();
    this.pickupBoxGeometry.addGroup(0, 24, 0); // the four thin edges
    this.pickupBoxGeometry.addGroup(24, 12, 1); // front and back art
    this.pickupBananaGeometry = this.buildBananaGeometry();

    for (let i = 0; i < COIN_POOL_SIZE; i++) {
      const mesh = new THREE.Mesh(this.pickupBoxGeometry, this.pickupMaterialSets[0]);
      mesh.visible = false;
      this.scene.add(mesh);
      this.coins.push({ mesh, lane: 0, z: SPAWN_Z, active: false });
    }

    // Pickups knocked out of the player by lightning (Sonic-style scatter).
    this.spill = [];
    for (let i = 0; i < SPILL_POOL_SIZE; i++) {
      const mesh = new THREE.Mesh(this.pickupBoxGeometry, this.pickupMaterialSets[0]);
      mesh.visible = false;
      this.scene.add(mesh);
      this.spill.push({ mesh, active: false, vx: 0, vy: 0, vz: 0, age: 0, halfHeight: 0.3 });
    }

    this.powerup = {
      // Flat gold-Jasmin sprite that spins like the pickups do.
      mesh: new THREE.Mesh(
        new THREE.PlaneGeometry(POWERUP_HEIGHT * POWERUP_ASPECT, POWERUP_HEIGHT),
        new THREE.MeshBasicMaterial({
          map: this.loadTexture(POWERUP_URL),
          transparent: true,
          alphaTest: 0.5,
          side: THREE.DoubleSide,
        }),
      ),
      active: false,
      lane: 1,
      z: SPAWN_Z,
      status: null, // { seq } when the Worker says one is live for this run
      spawnIn: Infinity, // seconds of run time until the orb appears
    };
    this.powerup.mesh.visible = false;
    this.scene.add(this.powerup.mesh);
  }

  getPlayerId() {
    try {
      let id = window.localStorage.getItem(POWERUP_PLAYER_ID_KEY);
      if (!id) {
        id = crypto.randomUUID();
        window.localStorage.setItem(POWERUP_PLAYER_ID_KEY, id);
      }
      return id;
    } catch {
      return null;
    }
  }

  /** Asks the Worker whether a power-up is live; any failure just means "no power-up this run". */
  async checkPowerUp() {
    const pu = this.powerup;
    pu.status = null;
    pu.spawnIn = Infinity;
    try {
      const playerId = this.getPlayerId();
      const query = playerId ? `?playerId=${encodeURIComponent(playerId)}` : '';
      const response = await fetch(`${POWERUP_API_URL}/powerup${query}`, { cache: 'no-store' });
      if (!response.ok) return;
      const data = await response.json();
      if (!data.active || this.state !== 'playing') return;
      pu.status = { seq: data.seq, benefit: data.benefit };
      pu.spawnIn =
        POWERUP_SPAWN_DELAY_MIN + Math.random() * (POWERUP_SPAWN_DELAY_MAX - POWERUP_SPAWN_DELAY_MIN);
    } catch {
      // Offline / Worker down: play on without it.
    }
  }

  async claimPowerUp(seq, benefit) {
    const playerId = this.getPlayerId();
    if (!playerId) return;
    try {
      const response = await fetch(`${POWERUP_API_URL}/powerup/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId, seq, benefit }),
      });
      const result = await response.json();
      if (result.granted && this.state === 'playing') this.applyPowerUp(result.benefit ?? benefit);
    } catch {
      // Claim failed: no reward, no harm.
    }
  }

  /** Starts (or refreshes) the benefit the Worker granted. Unknown benefits are ignored. */
  applyPowerUp(type) {
    const def = POWERUP_EFFECTS[type];
    if (!def) return;
    this.effect = { type, remaining: def.seconds, total: def.seconds, shownSecond: -1, blinking: null };
    this.playBuffer(this.collectSoundBuffer, 0.8);
    this.callbacks.onPowerUp?.(type, def.seconds);
  }

  clearEffect() {
    this.effect = null;
    this.player.material.color.copy(this.playerRestTint);
    this.callbacks.onPowerUp?.(null, 0);
  }

  hasEffect(type) {
    return this.effect?.type === type;
  }

  /** Ticks the active benefit. Invincibility waits to end until the player is clear of any roof/body it is inside. */
  updateEffect(dt) {
    const fx = this.effect;
    if (!fx) return;
    fx.remaining = Math.max(0, fx.remaining - dt);
    // The HUD only ever shows whole seconds, so only tell it when that
    // number changes. Rewriting it every frame re-shapes the text and keeps
    // the layer the HUD sits on busy -- cheap on a desktop, not on a phone,
    // and it ran for the whole life of every power-up.
    const second = Math.ceil(fx.remaining);
    if (second !== fx.shownSecond) {
      fx.shownSecond = second;
      this.callbacks.onPowerUp?.(fx.type, fx.remaining);
    }
    if (fx.type === 'invincible') {
      const blink = fx.remaining < 2 && Math.floor(fx.remaining * 8) % 2 === 0;
      if (blink !== fx.blinking) {
        fx.blinking = blink;
        // Prebuilt colors: `Color.set('#rrggbb')` parses the string each call.
        this.player.material.color.copy(blink ? this.playerRestTint : PLAYER_TINT_INVINCIBLE);
      }
    }
    if (fx.remaining > 0) return;
    if (fx.type === 'invincible' && this.isInsideSolidObstacle()) return;
    this.clearEffect();
  }

  /** True while the player's inside a traversable obstacle's body (e.g. a bus), where dropping invincibility would be instant death. */
  isInsideSolidObstacle() {
    for (const obstacle of this.obstacles) {
      if (!obstacle.active) continue;
      const def = OBSTACLE_DEFS[obstacle.type];
      if (def.topSurfaceHeight === undefined) continue;
      const bodyTopY = obstacle.baseY + def.topSurfaceHeight - SURFACE_MOUNT_TOLERANCE;
      if (obstacle.lane === this.playerLane && this.isUnderPlayerFeet(obstacle, def) && this.playerY < bodyTopY) {
        return true;
      }
    }
    return false;
  }

  updatePowerUp(dt) {
    const pu = this.powerup;
    if (pu.status && !pu.active) {
      pu.spawnIn -= dt;
      if (pu.spawnIn <= 0) {
        pu.active = true;
        pu.lane = Math.floor(Math.random() * LANE_X.length);
        pu.z = SPAWN_Z;
        pu.mesh.visible = true;
        pu.mesh.position.set(LANE_X[pu.lane], 1, pu.z);
      }
    }
    if (!pu.active) return;

    pu.z += this.speed * dt;
    pu.mesh.position.z = pu.z;
    pu.mesh.position.y = 1.1 + Math.sin(performance.now() / 200) * 0.15;
    pu.mesh.rotation.y += dt * 3;

    const hit = pu.lane === this.playerLane && pu.z > COLLISION_Z_MIN && pu.z < COLLISION_Z_MAX;
    if (hit || pu.z > DESPAWN_Z) {
      pu.active = false;
      pu.mesh.visible = false;
      const { seq, benefit } = pu.status;
      pu.status = null; // one orb per run, hit or miss
      if (hit) {
        // Touching the orb counts as collecting it, whether or not the Worker grants a benefit.
        this.countItem(POWERUP_ITEM_KEY);
        this.claimPowerUp(seq, benefit);
      }
    }
  }


  /**
   * Shared texture loader: sRGB, used for every character/obstacle/pickup
   * image. `magFilter` stays nearest-neighbor so the pixel art reads crisp
   * (not blurry) at native size or zoomed in. `minFilter` is linear, not
   * nearest -- these characters render noticeably smaller than their native
   * texture resolution on real devices, and nearest-neighbor minification
   * does raw point-sampling with no averaging, which can skip right over
   * thin 1px shading/shadow detail (like the gaps between hair spikes)
   * depending on exactly which texel each screen pixel happens to land on --
   * reads as colors and shading being randomly "stripped out". Linear
   * minification blends the actual footprint instead, so that detail
   * darkens the average color rather than disappearing.
   */
  loadTexture(url, onLoad) {
    if (!this.textureLoader) this.textureLoader = new THREE.TextureLoader();
    return this.textureLoader.load(url, (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.magFilter = THREE.NearestFilter;
      texture.minFilter = THREE.LinearFilter;
      onLoad?.(texture);
      this.scheduleTextureWarmUp();
      this.requestIdleRender();
    });
  }

  /**
   * Fetches and decodes a sound effect into an AudioBuffer once, cached by
   * URL so switching characters back and forth doesn't re-decode a voice
   * line it's already loaded. Returns a promise; a still-pending or
   * ultimately-failed decode just means `playBuffer` has nothing to play
   * yet and silently no-ops -- same "best effort, not worth surfacing"
   * philosophy as the old `.catch(() => {})` on direct playback.
   * @param {string} url
   * @returns {Promise<AudioBuffer|null>}
   */
  async loadSoundBuffer(url) {
    if (this.soundBuffers[url]) return this.soundBuffers[url];
    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      this.soundBuffers[url] = audioBuffer;
      return audioBuffer;
    } catch {
      return null;
    }
  }

  /**
   * Plays a decoded sound effect immediately via a fresh AudioBufferSourceNode
   * on the already-running AudioContext -- no HTMLMediaElement start/stop
   * involved, so no per-play session-activation latency on iOS. Overlapping
   * triggers (e.g. jumping again while the last jump sound is still
   * playing, or a 3-item coin trail collected in quick succession) layer
   * naturally since each is an independent node.
   * @param {Promise<AudioBuffer|null>} bufferPromise
   * @param {number} volume
   */
  async playBuffer(bufferPromise, volume) {
    const buffer = await bufferPromise;
    if (!buffer || this.audioContext.state !== 'running') return;
    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;
    const gain = this.audioContext.createGain();
    gain.gain.value = volume;
    source.connect(gain);
    gain.connect(this.audioContext.destination);
    source.start(0);
  }

  /**
   * Extrudes the banana's traced silhouette into a real 3D shape (see the
   * `BANANA_SHAPE_POINTS` comment for why a box doesn't work here). Built
   * once and shared across every pooled pickup showing the banana variant,
   * same as the shared box geometry. Unit depth (0..1 along Z, then
   * recentered to -0.5..0.5) so it scales the same way `assignPickupVariant`
   * already scales the box.
   */
  buildBananaGeometry() {
    const shape = new THREE.Shape();
    BANANA_SHAPE_POINTS.forEach(([x, y], i) => {
      if (i === 0) shape.moveTo(x, y);
      else shape.lineTo(x, y);
    });
    shape.closePath();
    const geometry = new THREE.ExtrudeGeometry(shape, {
      depth: 1,
      bevelEnabled: false,
      UVGenerator: BANANA_UV_GENERATOR,
    });
    geometry.translate(0, 0, -0.5);
    return geometry;
  }

  /** Assigns a specific pickup variant to a pooled coin, picks the right geometry, and sizes it to that variant's own art. */
  assignPickupVariant(coin, variant) {
    coin.variant = variant;
    const isBanana = variant === BANANA_VARIANT_INDEX;
    coin.mesh.geometry = isBanana ? this.pickupBananaGeometry : this.pickupBoxGeometry;
    coin.mesh.material = this.pickupMaterialSets[variant];
    const height = PICKUP_HEIGHTS[variant];
    const depthFraction = isBanana ? BANANA_DEPTH_FRACTION : PICKUP_DEPTH_FRACTION;
    coin.mesh.scale.set(height * PICKUP_ASPECTS[variant], height, height * depthFraction);
  }

  /**
   * Builds the character mesh: a single plane (its geometry pre-translated
   * so the local origin is at the bottom-center, i.e. the character's feet
   * -- moving the mesh is all lane/jump/bob positioning needs) whose texture
   * is picked each frame from `runFrameTextures[angle][stride]` by
   * `updateRunAnimation`. `NearestFilter` keeps the low-res pixel art crisp
   * instead of going blurry when scaled up. Angle set and stride count are
   * read from the chosen character's own definition -- not every character
   * has left/right lean art yet, so `updateRunAnimation` has to check
   * `runAngles`/`runFrameCount` rather than assuming Mika's 3x4 shape.
   */
  buildCharacter(characterId) {
    const def = CHARACTER_DEFS[characterId] ?? CHARACTER_DEFS[DEFAULT_CHARACTER];
    this.characterId = CHARACTER_DEFS[characterId] ? characterId : DEFAULT_CHARACTER;

    const correctBaseWidth = (texture) => {
      this.runCellHeightPx = texture.image.height;
      if (this.nativeScale) {
        const unitsPerPixel = (PLAYER_HEIGHT / NATIVE_REFERENCE_HEIGHT_PX) * this.nativeScaleFactor;
        this.playerBaseWidth = texture.image.width * unitsPerPixel;
        this.playerBaseHeight = texture.image.height * unitsPerPixel;
      } else {
        this.playerBaseWidth = PLAYER_HEIGHT * (texture.image.width / texture.image.height);
      }
    };

    this.nativeScale = Boolean(def.nativeScale);
    this.nativeScaleFactor = typeof def.nativeScale === 'number' ? def.nativeScale : 1;
    // Resting colour multiplier; below white darkens the unlit Jas art a little.
    this.playerRestTint = new THREE.Color(def.nativeScale ? 0xbebebe : 0xffffff);
    this.runCellHeightPx = 0;
    this.runAngles = Object.keys(def.run);
    this.runFrameTextures = {};
    for (const angle of this.runAngles) {
      this.runFrameTextures[angle] = def.run[angle].map((url, strideIndex) =>
        this.loadTexture(url, angle === 'center' && strideIndex === 0 ? correctBaseWidth : undefined)
      );
    }
    this.runFrameCount = def.run.center.length;
    this.jumpFrameTextures = def.jump.map((url) => this.loadTexture(url));
    this.injuredFrameTextures = def.injured.map((url) => this.loadTexture(url));
    // Voice line URLs only -- unlike the always-used sound effects above,
    // these 10 lines are rarely played (one random pick every 500 points),
    // so eagerly fetching and decoding all 10 at character-build time would
    // be pure waste, and on mobile, competing with everything else the game
    // is already loading, heavy enough to visibly hurt input responsiveness.
    // `checkVoiceMilestone` decodes (and `loadSoundBuffer` caches) each one
    // lazily, only the first time that particular line actually gets
    // picked.
    this.voiceUrls = def.voice;

    this.playerBaseWidth = PLAYER_HEIGHT * PLAYER_FALLBACK_ASPECT;
    this.playerBaseHeight = PLAYER_HEIGHT;

    const geometry = new THREE.PlaneGeometry(1, 1);
    geometry.translate(0, 0.5, 0); // local origin at the bottom edge (feet), not the center

    // Characters with shaded (non-flat) art are drawn unlit: scene lighting is
    // tuned for flat pixel art and would darken their baked-in shading.
    const MaterialClass = this.nativeScale ? THREE.MeshBasicMaterial : THREE.MeshLambertMaterial;
    const mesh = new THREE.Mesh(
      geometry,
      new MaterialClass({
        color: this.playerRestTint,
        map: this.runFrameTextures.center[1 % this.runFrameCount],
        transparent: true,
        alphaTest: 0.1,
        side: THREE.DoubleSide,
      })
    );
    return mesh;
  }

  /** Disposes every loaded texture/sound for the currently-built character (see the `destroy()` comment for why this can't rely on scene traversal alone). */
  disposeCharacterTextures() {
    for (const angle of this.runAngles) {
      for (const texture of this.runFrameTextures[angle]) texture.dispose();
    }
    for (const texture of this.jumpFrameTextures) texture.dispose();
    for (const texture of this.injuredFrameTextures) texture.dispose();
    // Voice lines are decoded AudioBuffers cached by URL in `this.soundBuffers`
    // (see `loadSoundBuffer`), not HTMLMediaElements -- nothing to
    // pause/release here, and leaving them cached is desirable if the
    // player switches back to this character later in the same session.
  }

  /**
   * Swaps the active character while idle (start screen, before a run):
   * disposes the old character's textures/mesh and rebuilds fresh ones from
   * the new definition. Safe to call repeatedly as the player browses the
   * selector; a no-op if the requested character is already active.
   * @param {string} characterId
   */
  setCharacter(characterId) {
    if (characterId === this.characterId) return;

    this.disposeCharacterTextures();
    const oldPlayer = this.player;
    this.scene.remove(oldPlayer);
    oldPlayer.geometry.dispose();
    oldPlayer.material.dispose();

    this.player = this.buildCharacter(characterId);
    this.scene.add(this.player);
    this.resetPlayer();
    this.requestIdleRender();
  }

  /**
   * Pixel-art cobblestone street, tiled along Z and scrolled via `offset`
   * each frame to sell motion without recycling ground geometry. Offset
   * brick-bond rows of setts with a little per-stone color variation, a
   * row of dark granite setts marking each lane boundary (in place of the old
   * painted dashes), darker gutter stones along both road edges, then a
   * granite curb and a pavement of large pale granite slabs out to the
   * building fronts -- the typical Copenhagen street section. Drawn at
   * square world-space pixels (GROUND_PIXELS_PER_UNIT on both axes) so
   * stones don't stretch, and with a fixed seed so every tile repeat and
   * every run looks identical.
   */
  createGroundTexture() {
    const unit = GROUND_PIXELS_PER_UNIT;
    const width = Math.round(GROUND_WIDTH * unit);
    const height = Math.round(GROUND_TILE_LENGTH * unit);
    const rowHeight = unit / 2;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    const rect = (x, y, w, h, color) => {
      ctx.fillStyle = color;
      ctx.fillRect(x, y, w, h);
    };
    let seed = 7;
    const random = () => {
      seed = (seed * 16807) % 2147483647;
      return (seed - 1) / 2147483646;
    };
    // Averages close to the old flat '#d8d3c4' road, so overall scene
    // brightness is unchanged.
    const palette = GROUND_PALETTE;
    const stoneColors = palette.stones;
    const stone = (x, y, w, h, base, highlight, shade) => {
      // 1px mortar gap on the right/bottom edges; lit top edge, shaded bottom.
      rect(x, y, w - 1, h - 1, base);
      rect(x, y, w - 1, 1, highlight);
      rect(x, y + h - 2, w - 1, 1, shade);
    };

    rect(0, 0, width, height, palette.mortar);
    for (let y = 0; y < height; y += rowHeight) {
      let x = (y / rowHeight) % 2 === 0 ? 0 : -Math.round(unit * 0.35);
      while (x < width) {
        const w = Math.round(unit * (0.55 + random() * 0.3));
        const base = stoneColors[Math.floor(random() * stoneColors.length)];
        stone(x, y, w, rowHeight, base, palette.stoneHighlight, palette.stoneShade);
        x += w;
      }
    }

    const sidewalk = Math.round(SIDEWALK_WIDTH * unit);
    const gutter = Math.round(unit * 0.35);
    const curb = Math.round(unit * 0.25);
    const slabColors = palette.slabs;
    const slabLength = unit;
    for (const [start, end] of [[0, sidewalk], [width - sidewalk, width]]) {
      rect(start, 0, end - start, height, palette.pavementBase);
      // Two slabs across, staggered by half a slab so the joints don't line up.
      const slabWidth = Math.round((end - start - curb) / 2);
      const slabStart = start === 0 ? 0 : start + curb;
      for (let column = 0; column < 2; column++) {
        const x = slabStart + column * slabWidth;
        for (let y = column * (slabLength / 2); y < height; y += slabLength) {
          const base = slabColors[Math.floor(random() * slabColors.length)];
          stone(x, y, slabWidth, slabLength, base, palette.slabHighlight, palette.slabShade);
          // A slab overhanging the bottom edge continues at the top, so the
          // texture still tiles seamlessly along the street.
          if (y + slabLength > height) {
            stone(x, y - height, slabWidth, slabLength, base, palette.slabHighlight, palette.slabShade);
          }
        }
      }
      const curbX = start === 0 ? end - curb : start;
      for (let y = 0; y < height; y += unit * 1.5) stone(curbX, y, curb, unit * 1.5, ...palette.curb);
    }
    for (let y = 0; y < height; y += rowHeight) {
      stone(sidewalk, y, gutter, rowHeight, ...palette.gutter);
      stone(width - sidewalk - gutter, y, gutter, rowHeight, ...palette.gutter);
    }

    const setWidth = Math.round(unit * 0.4);
    for (const laneEdge of [-LANE_WIDTH / 2, LANE_WIDTH / 2]) {
      const x = Math.round((laneEdge + GROUND_WIDTH / 2) * unit - setWidth / 2);
      for (let y = 0; y < height; y += rowHeight) stone(x, y, setWidth, rowHeight, ...palette.laneSett);
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(1, GROUND_LENGTH / GROUND_TILE_LENGTH);
    // Crisp stone edges up close; mipmapped + anisotropic in the distance,
    // where the stones get small and viewed at a grazing angle and would
    // otherwise shimmer as they scroll.
    texture.magFilter = THREE.NearestFilter;
    texture.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
    return texture;
  }

  /** Soft radial-gradient blob for the character's ground-contact shadow. */
  createShadowTexture() {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    const ctx = canvas.getContext('2d');
    const gradient = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
    gradient.addColorStop(0, 'rgba(0, 0, 0, 0.45)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    return new THREE.CanvasTexture(canvas);
  }

  resetPlayer() {
    this.playerLane = 1;
    this.playerTargetLane = 1;
    this.playerX = LANE_X[1];
    this.playerY = 0;
    this.jumpVelocity = 0;
    // Airborne for any reason -- a jump, or running off the edge of a roof.
    this.isJumping = false;
    // Height the current airborne arc started from, so jump pose frames are
    // picked relative to it (a jump off a roof looks the same as one off the
    // ground) rather than from absolute height.
    this.jumpBaseY = 0;
    // Height of the traversable surface under the player this frame (0 =
    // ground), and of the one they last stood on -- the camera eases its
    // look target toward the latter so riding a roof frames the same as the
    // ground without the view jolting on every hop.
    this.surfaceY = 0;
    this.standingY = 0;
    this.cameraSurfaceY = 0;
    // Absorbs the upward snap of a lenient roof landing (see
    // SURFACE_MOUNT_TOLERANCE): the character and camera are drawn this far
    // below the real position and ease up to it, reading as a quick climb
    // onto the roof instead of a teleport.
    this.landingSnapOffset = 0;
    this.bobPhase = 0;
    this.leanAmount = 0;
    this.currentAngle = 'center';
    this.currentStride = 0;
    this.currentPose = 'run';
    // The plane's geometry origin is the character's feet/ground point, so
    // no half-height offset is needed here.
    this.player.position.set(this.playerX, 0, 0);
    this.player.scale.set(this.playerBaseWidth, this.playerBaseHeight, 1);
    this.setPlayerFrame(this.runFrameTextures.center[0]);
    this.updateShadow();
  }

  /**
   * Starts (or restarts) a run: resets score/speed/pool state and switches
   * into the 'playing' state. Safe to call repeatedly (Play again button).
   */
  start() {
    this.resetPlayer();
    this.distance = 0;
    this.speed = BASE_SPEED;
    this.coinsCollected = 0;
    this.itemCounts = {};
    this.runStartedAt = performance.now();
    this.callbacks.onItemsChange?.({});
    this.timeSinceSpawn = 0;
    this.displayedScore = -1;
    this.lastVoiceMilestone = 0;
    this.voicePlaying = false;
    this.voiceToken = (this.voiceToken || 0) + 1;
    this.difficulty = 0;
    this.busLiveryIndex = 0;
    this.clearUntilDistance = 0;
    this.resetWeather();
    this.resetPuddles();

    this.musicSound.currentTime = 0;
    this.musicSound.playbackRate = 1;
    this.musicSound.play().catch(() => {});
    // Browsers require an AudioContext to be resumed from a user gesture --
    // `start()` only ever runs from the Play/Play-again button's click
    // handler, so this is always a valid place to do it. A no-op if it's
    // already running (e.g. on replay).
    this.audioContext.resume().catch(() => {});

    for (const obstacle of this.obstacles) {
      obstacle.active = false;
      obstacle.mesh.visible = false;
    }
    for (const coin of this.coins) {
      coin.active = false;
      coin.mesh.visible = false;
    }
    this.prefillTrack();

    this.powerup.active = false;
    this.powerup.mesh.visible = false;
    this.effect = null;
    this.callbacks.onPowerUp?.(null, 0);

    this.state = 'playing';
    this.updateScoreDisplay();
    this.checkPowerUp();
  }

  /**
   * Obstacles only ever appear at the far spawn line, so a run used to open
   * with ~8s of empty road (1s to the first wave, then ~7s for it to travel
   * in from SPAWN_Z). This fast-forwards the ordinary spawner -- same waves,
   * same spacing rules, just run ahead of time -- until the nearest thing on
   * the track is FIRST_OBSTACLE_Z away, so the run starts with the road
   * already populated and the first obstacle a couple of seconds out.
   * Nothing is collided with or collected, since nothing gets near the player.
   */
  prefillTrack() {
    const step = 1 / 30;
    let timeSinceSpawn = SPAWN_INTERVAL_SECONDS_START;
    let nearestZ = -Infinity;
    for (let i = 0; i < 1000 && nearestZ < FIRST_OBSTACLE_Z; i++) {
      timeSinceSpawn += step;
      if (timeSinceSpawn >= SPAWN_INTERVAL_SECONDS_START + (Math.random() - 0.5) * SPAWN_INTERVAL_JITTER_SECONDS) {
        timeSinceSpawn = 0;
        this.spawnWave();
      }
      const dz = BASE_SPEED * step;
      for (const item of [...this.obstacles, ...this.coins]) {
        if (!item.active) continue;
        item.z += dz;
        item.mesh.position.z = item.z;
        const nearEnd = item.z + (item.type ? OBSTACLE_DEFS[item.type].length / 2 : 0);
        nearestZ = Math.max(nearestZ, nearEnd);
      }
    }
    this.timeSinceSpawn = timeSinceSpawn;
  }

  handleKeyDown(event) {
    if (event.key === 'Escape' || event.key === 'p' || event.key === 'P') {
      if (this.state === 'playing') this.pause();
      else if (this.state === 'paused') this.resume();
      return;
    }
    if (this.state !== 'playing') return;

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight' || event.key === 'ArrowUp'
      || event.key === 'ArrowDown' || event.key === ' ') {
      event.preventDefault();
    }

    if (event.key === 'ArrowLeft') {
      this.changeLane(-1);
    } else if (event.key === 'ArrowRight') {
      this.changeLane(1);
    } else if (event.key === 'ArrowUp' || event.key === ' ') {
      this.jump();
    }
  }

  handleTouchStart(event) {
    const touch = event.changedTouches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
  }

  handleTouchEnd(event) {
    if (this.state !== 'playing' || this.touchStartX === undefined) return;

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      if (Math.abs(deltaX) > SWIPE_DISTANCE_THRESHOLD) {
        this.changeLane(deltaX > 0 ? 1 : -1);
      }
    } else if (deltaY < -SWIPE_DISTANCE_THRESHOLD) {
      this.jump();
    }
  }

  changeLane(direction) {
    if (this.puddleEffect?.type === 'inverted') direction = -direction;
    this.playerTargetLane = Math.min(2, Math.max(0, this.playerTargetLane + direction));
  }

  jump() {
    if (this.isJumping) return;
    this.isJumping = true;
    this.jumpBaseY = this.playerY;
    this.jumpVelocity = JUMP_VELOCITY;
    this.playBuffer(this.jumpSoundBuffer, 0.5);
  }

  /**
   * Called from outside (IntersectionObserver / visibilitychange) to pause
   * or resume the render loop without touching game state.
   * @param {boolean} active
   */
  setActive(active) {
    if (this.active === active) return;
    this.active = active;

    if (this.active) {
      this.lastFrameTime = performance.now();
      this.requestIdleRender();
      this.rafId = requestAnimationFrame(this.tick);
      if (this.state === 'playing') this.musicSound.play().catch(() => {});
    } else {
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      this.musicSound.pause();
    }
  }

  /**
   * Freezes a run in place: the state leaves 'playing', so `update` (and with
   * it every timer, spawn and animation) stops, and the canvas simply keeps
   * showing its last frame. Sound is silenced too -- the music element is
   * paused and the AudioContext suspended so in-flight effects don't finish
   * over the pause card.
   */
  pause() {
    if (this.state !== 'playing') return;
    this.state = 'paused';
    this.pausedAt = performance.now();
    this.musicSound.pause();
    this.audioContext.suspend().catch(() => {});
    this.callbacks.onPauseChange?.(true);
  }

  resume() {
    if (this.state !== 'paused') return;
    this.state = 'playing';
    // Keep the paused stretch out of the run's measured duration, and out of
    // the first frame's dt.
    this.runStartedAt += performance.now() - this.pausedAt;
    this.lastFrameTime = performance.now();
    if (this.active) this.musicSound.play().catch(() => {});
    this.audioContext.resume().catch(() => {});
    this.callbacks.onPauseChange?.(false);
  }

  /**
   * Frame-time readout for diagnosing stutter on a real device: add
   * ?game-stats=1 to the page URL. Shows worst and average frame time over
   * the last second, draw calls, triangles and what's currently active, so a
   * hitch can be tied to something concrete (a power-up appearing, rain
   * starting) instead of guessed at from a desktop.
   */
  updateStats(frameMs) {
    const stats = this.stats;
    if (!stats) return;
    stats.frames++;
    stats.total += frameMs;
    stats.worst = Math.max(stats.worst, frameMs);
    if (frameMs > 33) stats.dropped++;
    const now = performance.now();
    if (now - stats.since < 1000) return;
    const info = this.renderer.info.render;
    stats.el.textContent =
      `${(stats.total / stats.frames).toFixed(1)}ms avg | ${stats.worst.toFixed(0)}ms worst | ` +
      `${stats.dropped} dropped | ${info.calls} calls | ${(info.triangles / 1000).toFixed(1)}k tris | ` +
      `${ACTIVE_THEME_ID}${this.effect ? ' | ' + this.effect.type : ''}${this.stormRaining ? ' | rain' : ''}`;
    stats.frames = 0;
    stats.total = 0;
    stats.worst = 0;
    stats.dropped = 0;
    stats.since = now;
  }

  tick(now) {
    const frameMs = now - this.lastFrameTime;
    const dt = Math.min(MAX_DELTA_TIME, frameMs / 1000);
    this.lastFrameTime = now;
    if (this.stats) this.updateStats(frameMs);

    if (this.state === 'playing') {
      this.update(dt);
      this.render();
    } else if (this.idleRenderFrames > 0) {
      // Menus and the game-over card sit over the canvas. Redrawing an
      // unchanging scene 60 times a second behind them only burns main-thread
      // time (and taps) -- draw a few frames after anything changes, then rest.
      this.idleRenderFrames -= 1;
      this.render();
    }
    this.rafId = requestAnimationFrame(this.tick);
  }

  /** Asks for a few more frames outside of play (resize, new character, game over, late textures). */
  requestIdleRender() {
    this.idleRenderFrames = 3;
  }

  update(dt) {
    this.difficulty = 1 - Math.exp(-this.distance / DIFFICULTY_DISTANCE_SCALE);
    this.speed = THREE.MathUtils.lerp(BASE_SPEED, MAX_SPEED, this.difficulty);
    this.distance += this.speed * dt;
    this.timeSinceSpawn += dt;

    // Obstacles move before the player's vertical physics runs, so "what
    // surface is under me" is answered against this frame's positions, not
    // last frame's; collisions are checked after both have settled.
    this.updateLanePosition(dt);
    this.moveObstacles(dt);
    this.updateJump(dt);
    this.updateRunAnimation(dt);
    this.updateCamera(dt);
    this.checkObstacleCollisions();
    this.updateCoins(dt);
    this.updatePuddles(dt);
    this.updatePowerUp(dt);
    this.updateEffect(dt);
    this.updateGround(dt);
    this.updateBuildings(dt);
    this.updateStreetProps(dt);
    if (this.mistPatches) this.updateGroundMist(dt);
    if (this.bats) this.updateFlyingBats(dt);
    this.skyClouds.rotation.y += SKY_CLOUD_DRIFT * dt;
    this.updateWeather(dt);
    // After the weather, which sets the fog colour every frame.
    if (this.fogBank) this.updateFogBanks(dt);

    const spawnInterval = THREE.MathUtils.lerp(
      SPAWN_INTERVAL_SECONDS_START,
      SPAWN_INTERVAL_SECONDS_MIN,
      this.difficulty
    );
    if (this.timeSinceSpawn >= spawnInterval + (Math.random() - 0.5) * SPAWN_INTERVAL_JITTER_SECONDS) {
      this.timeSinceSpawn = 0;
      this.spawnWave();
    }

    this.updateScoreDisplay();
  }

  updateLanePosition(dt) {
    const targetX = LANE_X[this.playerTargetLane];
    const ease = this.puddleEffect?.type === 'slippery' ? LANE_EASE_SLIPPERY : LANE_EASE;
    this.playerX += (targetX - this.playerX) * Math.min(1, dt * ease);
    this.player.position.x = this.playerX;
    // How far into an active lane change the character still is; drives
    // which angle of run-cycle art shows in `updateRunAnimation` (turning
    // toward the real art instead of just tilting a flat plane).
    this.leanAmount = targetX - this.playerX;

    // Collision uses the lane closest to where the player character actually
    // is on screen, not the lane they just swiped toward — snapping this
    // instantly on input made the hitbox jump ahead of (or behind) the
    // visible character mid-transition, which read as the game killing you
    // for something that hadn't visually happened yet.
    let closestLane = 0;
    let closestDistance = Infinity;
    for (let lane = 0; lane < LANE_X.length; lane++) {
      const distance = Math.abs(LANE_X[lane] - this.playerX);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestLane = lane;
      }
    }
    this.playerLane = closestLane;
  }

  /**
   * Vertical motion against whatever surface is under the player: the
   * ground, or a traversable obstacle top (bus roof). Walking off an edge
   * starts a fall; any descent lands on the highest surface underneath.
   * Support is sampled before integrating, so a fast fall that would step
   * past a roof in one frame still lands on it (no tunneling).
   */
  updateJump(dt) {
    const supportY = this.getSupportHeight();
    this.surfaceY = supportY;

    if (!this.isJumping) {
      if (this.playerY <= supportY) {
        this.playerY = supportY;
        this.standingY = supportY;
        return;
      }
      // Ran off the end of a roof (or changed lanes off its side).
      this.isJumping = true;
      this.jumpBaseY = this.playerY;
      this.jumpVelocity = 0;
    }

    this.playerY += this.jumpVelocity * dt;
    this.jumpVelocity -= GRAVITY * dt;

    if (this.jumpVelocity <= 0 && this.playerY <= supportY) {
      // Keep the character/camera drawn where they were and ease them up to
      // a lifted landing (a mount can snap the feet up by up to
      // SURFACE_MOUNT_TOLERANCE).
      this.landingSnapOffset -= supportY - this.playerY;
      this.playerY = supportY;
      this.standingY = supportY;
      this.isJumping = false;
      this.jumpVelocity = 0;
    }
  }

  /**
   * Highest surface under the player's feet that they're at, above, or
   * within SURFACE_MOUNT_TOLERANCE of -- 0 (ground) if none. A roof the
   * player is well below isn't support; that's the bus body, which
   * `checkObstacleCollisions` treats as a crash instead.
   */
  getSupportHeight() {
    let support = 0;
    for (const obstacle of this.obstacles) {
      if (!obstacle.active || obstacle.lane !== this.playerLane) continue;
      const def = OBSTACLE_DEFS[obstacle.type];
      if (def.topSurfaceHeight === undefined || !this.isUnderPlayerFeet(obstacle, def)) continue;
      const topY = obstacle.baseY + def.topSurfaceHeight;
      if (this.playerY >= topY - SURFACE_MOUNT_TOLERANCE && topY > support) support = topY;
    }
    return support;
  }

  /** Whether an obstacle with real length currently spans the player's footprint along Z. */
  isUnderPlayerFeet(obstacle, def) {
    const front = obstacle.z + def.length / 2;
    const rear = obstacle.z - def.length / 2;
    return front > -PLAYER_FOOT_HALF_DEPTH && rear < PLAYER_FOOT_HALF_DEPTH;
  }

  /**
   * Position.y for the character is composed here rather than in
   * `updateJump`: jump arc height plus a running bob that only applies while
   * grounded (bobbing mid-air on top of a jump arc would fight with it).
   * Also picks which texture is showing -- the 2 real jump frames while
   * airborne (low/takeoff vs high/peak, by height, not by time, so it
   * matches the actual arc), otherwise the grounded run cycle -- and keeps
   * the ground shadow in sync.
   */
  updateRunAnimation(dt) {
    let bobOffset = 0;
    if (!this.isJumping) {
      this.bobPhase += dt * this.speed * BOB_CYCLES_PER_WORLD_UNIT;
      bobOffset = Math.abs(Math.sin(this.bobPhase)) * BOB_AMPLITUDE;
    }
    this.player.position.y = this.playerY + bobOffset + this.landingSnapOffset;

    if (this.isJumping) {
      // Mika's 2 real jump frames (low takeoff / high peak) use the original
      // hand-tuned height threshold; characters with more frames (e.g. Jas's
      // takeoff/mid-air/landing set) spread evenly across the jump's actual
      // physical apex height instead, since a single threshold doesn't
      // generalize to an arbitrary frame count.
      // Height is measured from where this arc started (ground or a roof);
      // a fall off a roof edge goes negative and holds the first frame.
      const frameCount = this.jumpFrameTextures.length;
      const jumpHeight = this.playerY - this.jumpBaseY;
      const jumpFrame =
        frameCount <= 2
          ? jumpHeight < JUMP_HIGH_POSE_HEIGHT
            ? 0
            : 1
          : THREE.MathUtils.clamp(Math.floor((jumpHeight / JUMP_APEX_HEIGHT) * frameCount), 0, frameCount - 1);
      if (this.currentPose !== 'jump' || jumpFrame !== this.currentStride) {
        this.currentPose = 'jump';
        this.currentStride = jumpFrame;
        this.setPlayerFrame(this.jumpFrameTextures[jumpFrame]);
      }
    } else {
      // Turn toward whichever lane the character is actively moving into by
      // swapping to that angle's real art; settles back to center once the
      // lane change finishes and leanAmount decays under the threshold. Only
      // applies for characters that actually have left/right lean art --
      // Jas currently only has a center/back angle, so always stays center.
      let angle = 'center';
      if (this.runAngles.length > 1) {
        if (this.leanAmount > LEAN_ANGLE_THRESHOLD) angle = 'right';
        else if (this.leanAmount < -LEAN_ANGLE_THRESHOLD) angle = 'left';
      }

      // The stride frames within an angle were cropped left-to-right from
      // the sheet's own run-cycle timeline, so cycling through them in order
      // is already a real run cycle -- no need to bounce back and forth the
      // way a single "center" filler frame would. Frame count varies by
      // character (Mika has 4, Jas has 5), hence `runFrameCount` rather than
      // a hardcoded stride divisor.
      const cycleProgress = (this.bobPhase % (Math.PI * 2)) / (Math.PI * 2);
      // Lean angles can have fewer frames than the center cycle (Jas: 3 vs 4),
      // so index by each angle's own frame count.
      const angleFrameCount = this.runFrameTextures[angle].length;
      const stride = Math.floor(cycleProgress * angleFrameCount) % angleFrameCount;

      if (this.currentPose !== 'run' || angle !== this.currentAngle || stride !== this.currentStride) {
        this.currentPose = 'run';
        this.currentAngle = angle;
        this.currentStride = stride;
        this.setPlayerFrame(this.runFrameTextures[angle][stride]);
      }
    }

    this.fitPlayerToFrame();
    this.updateShadow();
  }

  /** Swaps the player's texture; characters with `nativeScale` also resize the plane to the frame's own pixel size (feet stay at the plane's bottom edge). */
  setPlayerFrame(texture) {
    this.player.material.map = texture;
    this.fitPlayerToFrame();
  }

  /**
   * Sizes the plane to the current frame's pixels. Also runs every frame
   * (see `updateRunAnimation`): a frame's image can finish loading after it
   * was first shown (e.g. the first jump), and without a re-fit it would
   * stay stretched into the previous frame's box.
   */
  fitPlayerToFrame() {
    const image = this.player.material.map?.image;
    if (!this.nativeScale || !this.runCellHeightPx || !image?.width) return;
    const unitsPerPixel = (PLAYER_HEIGHT / NATIVE_REFERENCE_HEIGHT_PX) * this.nativeScaleFactor;
    this.player.scale.set(image.width * unitsPerPixel, image.height * unitsPerPixel, 1);
  }

  updateShadow() {
    // Cast onto the roof when over one -- but only once the player is
    // actually above it (not mid-mount, still below the roof edge).
    const shadowBaseY = this.surfaceY <= this.playerY ? this.surfaceY : 0;
    const heightFade = 1 - THREE.MathUtils.clamp((this.playerY - shadowBaseY) / SHADOW_MAX_HEIGHT_FOR_FADE, 0, 1);
    const scale = SHADOW_WORLD_SIZE * (0.6 + 0.4 * heightFade);
    this.shadow.position.x = this.playerX;
    this.shadow.position.y = shadowBaseY + 0.02;
    this.shadow.scale.set(scale, scale * 0.6, 1);
    this.shadow.material.opacity = heightFade;
  }

  moveObstacles(dt) {
    for (const obstacle of this.obstacles) {
      if (!obstacle.active) continue;

      // Remembered so collisions can test the whole step, not just where the
      // obstacle ended up (see `checkObstacleCollisions`).
      obstacle.prevZ = obstacle.z;
      obstacle.z += this.speed * dt;
      if (obstacle.type === 'rider') this.moveRider(obstacle, dt);
      obstacle.mesh.position.z = obstacle.z;

      // Long obstacles (buses) stay until their rear end is past, not their center.
      if (obstacle.z - OBSTACLE_DEFS[obstacle.type].length / 2 > DESPAWN_Z) {
        obstacle.active = false;
        obstacle.mesh.visible = false;
      }
    }
    this.checkRiderBumps();
  }

  /** A rider's own motion on top of the world scroll, plus its animation. */
  moveRider(rider, dt) {
    if (rider.state === 'waiting' && rider.z > rider.triggerZ) rider.state = 'riding';
    if (rider.state === 'riding') {
      if (rider.mode === 'along') {
        rider.z -= RIDER_ALONG_SPEED * dt;
        const swerve = rider.swerve;
        if (swerve && swerve.state !== 'done') {
          if (swerve.state === 'pending' && rider.z >= RIDER_SWERVE_TRIGGER_Z) swerve.state = 'moving';
          if (swerve.state === 'moving') {
            swerve.elapsed += dt;
            const progress = Math.min(1, swerve.elapsed / RIDER_SWERVE_SECONDS);
            const eased = progress * progress * (3 - 2 * progress);
            rider.x = LANE_X[swerve.from] + (LANE_X[swerve.to] - LANE_X[swerve.from]) * eased;
            if (progress >= 1) swerve.state = 'done';
          }
        }
      } else {
        rider.x += rider.direction * RIDER_CROSS_SPEED * dt;
        if (Math.abs(rider.x) >= RIDER_CROSS_END_X) {
          rider.x = Math.sign(rider.x) * RIDER_CROSS_END_X;
          rider.state = 'parked';
        }
      }
      rider.frameTime += dt;
    }
    rider.lane = this.closestLane(rider.x);
    rider.mesh.position.x = rider.x;
    this.showRiderFrame(rider);
  }

  closestLane(x) {
    let closest = 0;
    for (let lane = 1; lane < LANE_X.length; lane++) {
      if (Math.abs(LANE_X[lane] - x) < Math.abs(LANE_X[closest] - x)) closest = lane;
    }
    return closest;
  }

  /** Picks the atlas frame and shadow shape for the rider's current state. */
  showRiderFrame(rider) {
    const def = RIDER_DEFS[rider.riderKind];
    const step = Math.floor(rider.frameTime * RIDER_FRAMES_PER_SECOND);
    let frame;
    if (rider.state === 'crashed') {
      frame = rider.cells - 1;
      rider.sprite.scale.set(RIDER_CRASHED_SCALE, RIDER_CRASHED_SCALE, 1);
      rider.shadow.scale.set(1.1 * RIDER_CRASHED_SCALE, 1, 0.6 * RIDER_CRASHED_SCALE);
    } else if (rider.mode === 'along') {
      frame = def.sideFrames + (step % def.backFrames);
      rider.sprite.scale.set(1, 1, 1);
      rider.shadow.scale.set(0.55, 1, 0.9);
    } else {
      // Side view; the art faces +x, so mirror it for riders heading -x.
      frame = rider.state === 'riding' ? step % def.sideFrames : 0;
      rider.sprite.scale.set(rider.direction < 0 ? -1 : 1, 1, 1);
      rider.shadow.scale.set(1.2, 1, 0.35);
    }
    rider.texture.offset.x = frame / rider.cells;
  }

  /**
   * A moving rider that runs into anything on the road -- or that anything
   * runs into, e.g. a crate catching up with a rider slower than the world
   * -- crashes. Two riders colliding both crash. Buses are hit anywhere
   * along their length. Things riding on bus roofs are out of reach.
   */
  checkRiderBumps() {
    for (const rider of this.obstacles) {
      if (!rider.active || rider.type !== 'rider' || rider.state !== 'riding') continue;
      for (const other of this.obstacles) {
        if (other === rider || !other.active || other.baseY > 0) continue;
        const def = OBSTACLE_DEFS[other.type];
        let hit;
        if (def.topSurfaceHeight !== undefined) {
          hit =
            Math.abs(rider.x - LANE_X[other.lane]) < BUS_WIDTH / 2 + 0.35 &&
            rider.z < other.z + def.length / 2 + 0.3 &&
            rider.z > other.z - def.length / 2 - 0.3;
        } else {
          const otherX = other.type === 'rider' ? other.x : LANE_X[other.lane];
          if (other.type === 'rider' && other.state !== 'riding' && other.state !== 'crashed') continue;
          hit = Math.abs(rider.x - otherX) < RIDER_HIT_HALF_WIDTH && Math.abs(rider.z - other.z) < RIDER_BUMP_HALF_DEPTH;
        }
        if (!hit) continue;
        this.crashRider(rider);
        if (other.type === 'rider' && other.state === 'riding') this.crashRider(other);
        break;
      }
    }
  }

  crashRider(rider) {
    rider.state = 'crashed';
    rider.clearHeight = RIDER_CRASHED_CLEAR_HEIGHT;
    this.showRiderFrame(rider);
  }

  checkObstacleCollisions() {
    if (this.hasEffect('invincible')) {
      // Anything about to reach the player counts as already dealt with, so
      // nothing that was driven through can hit once the effect wears off.
      // Roof/body overlaps are handled by `updateEffect` (it won't end
      // while the player is still inside one).
      for (const obstacle of this.obstacles) {
        if (obstacle.active && obstacle.z > COLLISION_Z_MIN - 4) obstacle.resolved = true;
      }
      return;
    }
    for (const obstacle of this.obstacles) {
      if (!obstacle.active) continue;
      const def = OBSTACLE_DEFS[obstacle.type];

      // Traversable obstacles: the body blocks, the top supports. Checked
      // every frame while overlapping rather than resolved once -- the
      // player can ride the roof, change lanes, or drop off at any point
      // along it. Being within SURFACE_MOUNT_TOLERANCE of the top (i.e. a
      // mount in progress) isn't a hit; `updateJump` lands that on the roof.
      if (def.topSurfaceHeight !== undefined) {
        const bodyTopY = obstacle.baseY + def.topSurfaceHeight - SURFACE_MOUNT_TOLERANCE;
        if (obstacle.lane === this.playerLane && this.isUnderPlayerFeet(obstacle, def) && this.playerY < bodyTopY) {
          this.gameOver();
          return;
        }
        continue;
      }

      // Riders: same once-resolved window, but by actual x distance (they're
      // often between lanes) and a per-rider clear height. The rider shows
      // its crash pose when hit, alongside the player's own.
      if (obstacle.type === 'rider') {
        const sweptPastPlayer = obstacle.z > COLLISION_Z_MIN && (obstacle.prevZ ?? obstacle.z) < COLLISION_Z_MAX;
        if (!obstacle.resolved && sweptPastPlayer && Math.abs(obstacle.x - this.playerX) < RIDER_HIT_HALF_WIDTH) {
          if (this.playerY - obstacle.baseY <= obstacle.clearHeight) {
            if (obstacle.state !== 'crashed') this.crashRider(obstacle);
            this.gameOver();
            return;
          }
          obstacle.resolved = true;
        } else if (obstacle.z >= COLLISION_Z_MAX) {
          obstacle.resolved = true;
        }
        continue;
      }

      // Once an obstacle is behind the collision window it's permanently
      // safe, regardless of any lane change afterwards — without this, an
      // obstacle the player dodged by simply never sharing its lane (as
      // opposed to jumping it) never got marked resolved, so swiping back
      // through its lane a moment later while it was still on screen (but
      // already past) could re-trigger a collision against something the
      // player had clearly already passed.
      const windowMin = def.hitHalfDepth !== undefined ? -def.hitHalfDepth : COLLISION_Z_MIN;
      const windowMax = def.hitHalfDepth !== undefined ? def.hitHalfDepth : COLLISION_Z_MAX;
      const inReach =
        def.hitHalfWidth !== undefined
          ? Math.abs(LANE_X[obstacle.lane] - this.playerX) < def.hitHalfWidth
          : obstacle.lane === this.playerLane;
      // Swept, not point-sampled: at high speed (and a long frame) an
      // obstacle can step clean over the collision window between frames,
      // which used to let a hit through unnoticed.
      const swept = obstacle.z > windowMin && (obstacle.prevZ ?? obstacle.z) < windowMax;
      if (!obstacle.resolved && inReach && swept) {
        // Relative to the obstacle's own base, so a crate riding on a bus
        // roof needs a jump off that roof, same as one on the ground.
        const clearedByJump = this.playerY - obstacle.baseY > def.clearHeight;
        if (!clearedByJump) {
          this.gameOver();
          return;
        }
        obstacle.resolved = true;
      } else if (obstacle.z >= windowMax) {
        obstacle.resolved = true;
      }
    }
  }

  updateCoins(dt) {
    for (const coin of this.coins) {
      if (!coin.active) continue;

      coin.z += this.speed * dt;
      coin.mesh.position.z = coin.z;
      // Spins around the vertical axis (like a product on a turntable) --
      // these are flat rectangular pickup art now, not a torus, so spinning
      // around Z would just rotate the image in-plane like a clock hand.
      coin.mesh.rotation.y += dt * 4;

      if (coin.z > DESPAWN_Z) {
        coin.active = false;
        coin.mesh.visible = false;
        continue;
      }

      const magnet = this.hasEffect('magnet') && coin.z > MAGNET_RANGE_Z;
      if (magnet) {
        // Slide sideways into the player's path; the lane check below is
        // widened to match so a pulled coin is collected, not just moved.
        coin.mesh.position.x += (this.playerX - coin.mesh.position.x) * Math.min(1, dt * MAGNET_PULL_RATE);
      }
      const inLane = magnet
        ? Math.abs(coin.mesh.position.x - this.playerX) < LANE_WIDTH * 0.5
        : coin.lane === this.playerLane;
      if (inLane && coin.z > COLLISION_Z_MIN && coin.z < COLLISION_Z_MAX) {
        coin.active = false;
        coin.mesh.visible = false;
        this.coinsCollected += 1;
        // Count by pickup type (during a banana campaign every pickup is a banana).
        this.countItem(ITEM_KEY_BY_VARIANT[coin.variant]);
        this.playBuffer(this.collectSoundBuffer, COIN_COLLECT_VOLUME);
      }
    }
  }

  updateGround(dt) {
    // One texture repeat spans GROUND_TILE_LENGTH world units, so this
    // scrolls the stones at exactly world speed -- they stay locked to the
    // obstacles on them (buses are long enough to show any mismatch). The
    // ground plane's V runs away from the camera, so increasing the offset
    // moves the stones toward the player.
    this.groundTexture.offset.y += (this.speed * dt) / GROUND_TILE_LENGTH;
  }

  /**
   * Builds both rows of townhouses once, from just behind the camera out to
   * BUILDING_ROW_FAR_Z. The houses are the pool: `updateBuildings` scrolls
   * them and recycles each one from behind the camera to the far end of its
   * row with a new look. Each house is one mesh drawn in one call (see
   * `buildHouseGeometry`); facade textures/materials are cached by look.
   */
  buildStreet() {
    this.buildingMaterials = new Map();
    this.buildings = [];
    this.streetFarEdge = {};
    this.lastBuildingColor = {};
    for (const side of [-1, 1]) {
      let edge = BUILDING_ROW_NEAR_Z;
      while (edge > BUILDING_ROW_FAR_Z) {
        const mesh = new THREE.Mesh(new THREE.BufferGeometry());
        mesh.position.x = side * (GROUND_WIDTH / 2 + BUILDING_DEPTH / 2);
        this.scene.add(mesh);
        const building = { side, mesh, width: 0, z: 0 };
        this.dressBuilding(building);
        this.placeBuilding(building, edge);
        edge -= building.width;
        this.buildings.push(building);
      }
      this.streetFarEdge[side] = edge;
    }
  }

  /**
   * Gives a house a new random height, color, roof and chimney. Its width is
   * picked once, when first built, and kept through recycling: re-rolling it
   * would let the row's total length random-walk over a long run, drifting
   * the far end of the street into view.
   */
  dressBuilding(building) {
    const pick = (items) => Math.floor(Math.random() * items.length);
    const { side } = building;
    building.widthIndex ??= pick(BUILDING_WIDTHS);
    const { widthIndex } = building;
    const upperFloors = BUILDING_UPPER_FLOORS[pick(BUILDING_UPPER_FLOORS)];
    // Never the same color twice in a row -- a terrace of identical
    // neighbours reads as one long building instead of a row of houses.
    const colorful = Math.random() < BUILDING_COLORFUL_SHARE;
    const first = colorful ? 0 : BUILDING_COLORFUL_COUNT;
    const count = colorful ? BUILDING_COLORFUL_COUNT : BUILDING_WALL_COLORS.length - BUILDING_COLORFUL_COUNT;
    let colorIndex = first + Math.floor(Math.random() * count);
    if (colorIndex === this.lastBuildingColor[side]) colorIndex = first + ((colorIndex - first + 1) % count);
    this.lastBuildingColor[side] = colorIndex;

    const width = BUILDING_WIDTHS[widthIndex];
    const wallHeight = BUILDING_GROUND_FLOOR_HEIGHT + upperFloors * BUILDING_FLOOR_HEIGHT;
    const gableToStreet = Math.random() < BUILDING_GABLE_TO_STREET_CHANCE;
    const roofHeight = gableToStreet ? width * 0.75 : BUILDING_DEPTH * 0.32;
    const material = this.getFacadeMaterial(widthIndex, upperFloors, colorIndex);

    // Sunk partway into the roof slope near the ridge so it pokes up above
    // the roofline as seen from the street.
    let chimney = null;
    if (Math.random() < BUILDING_CHIMNEY_CHANCE) {
      const slopeFraction = gableToStreet ? 0.5 : 0.25;
      chimney = {
        x: gableToStreet ? 0 : -side * (BUILDING_DEPTH / 2) * slopeFraction,
        y: wallHeight + roofHeight * (1 - slopeFraction) + 0.2,
        z: gableToStreet ? (Math.random() < 0.5 ? -1 : 1) * (width / 2) * slopeFraction : 0,
      };
    }

    building.width = width;
    building.mesh.geometry.dispose();
    building.mesh.geometry = buildHouseGeometry({
      side,
      width,
      wallHeight,
      gableToStreet,
      roofHeight,
      chimney,
      wallColor: BUILDING_WALL_COLORS[colorIndex],
      roofColor: BUILDING_ROOF_COLORS[pick(BUILDING_ROOF_COLORS)],
      facadeFraction: material.map.userData.facadeFraction,
    });
    building.mesh.material = material;
  }

  /** Positions a house so its near end (the end toward the player) is at `nearEdge`. */
  placeBuilding(building, nearEdge) {
    building.z = nearEdge - building.width / 2;
    building.mesh.position.z = building.z;
  }

  updateBuildings(dt) {
    const dz = this.speed * dt;
    this.streetFarEdge[-1] += dz;
    this.streetFarEdge[1] += dz;
    for (const building of this.buildings) {
      building.z += dz;
      building.mesh.position.z = building.z;
      if (building.z - building.width / 2 > BUILDING_ROW_NEAR_Z) {
        this.dressBuilding(building);
        this.placeBuilding(building, this.streetFarEdge[building.side]);
        this.streetFarEdge[building.side] -= building.width;
      }
    }
  }

  /**
   * Sky dome (vertex-colored gradient, drawn first and behind everything,
   * unaffected by fog or lights) plus the cloud layer, grouped so `render`
   * can keep the whole sky centered on the camera -- it reads as infinitely
   * far away instead of something the player runs toward.
   */
  buildSky() {
    this.sky = new THREE.Group();
    const domeGeometry = new THREE.SphereGeometry(SKY_RADIUS, 32, 16);
    const horizon = new THREE.Color(SKY_HORIZON_COLOR);
    const zenith = new THREE.Color(SKY_ZENITH_COLOR);
    const domePositions = domeGeometry.getAttribute('position');
    const domeColors = [];
    const color = new THREE.Color();
    for (let i = 0; i < domePositions.count; i++) {
      // Sine of the elevation. The haze thins fast just above the horizon,
      // then the blue deepens more slowly toward the zenith.
      const elevation = Math.max(0, domePositions.getY(i) / SKY_RADIUS);
      color.copy(horizon).lerp(zenith, 1 - Math.pow(1 - elevation, 2.4));
      domeColors.push(color.r, color.g, color.b);
    }
    domeGeometry.setAttribute('color', new THREE.Float32BufferAttribute(domeColors, 3));
    const dome = new THREE.Mesh(
      domeGeometry,
      new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.BackSide, fog: false, depthWrite: false })
    );
    dome.renderOrder = -1;
    this.skyDomeMaterial = dome.material;

    // Clouds, and whatever else the theme hangs in the sky: stars, a moon,
    // drifting bats. They all ride the same unlit mesh, so the whole sky
    // layer stays one draw call however much is up there.
    const cloudBoxes = [];
    for (const { center, boxes } of SKY_CLOUDS) {
      for (const [w, h, d, x, y, z] of boxes) {
        cloudBoxes.push({ size: [w, h, d], position: [center[0] + x, center[1] + y, center[2] + z], color: SKY_CLOUD_COLOR });
      }
    }
    if (SKY_STARS) {
      const starRandom = createSeededRandom(SKY_STARS.seed ?? 11);
      const size = SKY_STARS.size ?? 0.9;
      for (let i = 0; i < (SKY_STARS.count ?? 0); i++) {
        // Spread across the half of the dome the camera can see, above the
        // rooflines so they don't sit behind the street.
        const angle = Math.PI * (0.15 + starRandom() * 0.7);
        const elevation = (SKY_STARS.minElevation ?? 10) + starRandom() * 55;
        const radius = 120 + starRandom() * 30;
        cloudBoxes.push({
          size: [size, size, size],
          position: [-Math.cos(angle) * radius, elevation, -Math.sin(angle) * radius],
          color: SKY_STARS.color ?? '#ffffff',
        });
      }
    }
    if (SKY_MOON) {
      const [mx, my, mz] = SKY_MOON.position;
      const size = SKY_MOON.size ?? 10;
      if (SKY_MOON.glowSize) {
        cloudBoxes.push({ size: [SKY_MOON.glowSize, SKY_MOON.glowSize, 1], position: [mx, my, mz - 2], color: SKY_MOON.glowColor ?? '#555577' });
      }
      cloudBoxes.push({ size: [size, size, 1], position: [mx, my, mz], color: SKY_MOON.color ?? '#fdf6d8' });
      // Craters rather than a crescent: a crescent needs a bite the exact
      // color of the sky behind it, which the dome's gradient doesn't give us.
      const crater = SKY_MOON.craterColor ?? '#e4dcbe';
      for (const [cx, cy, cs] of [[-0.22, 0.18, 0.22], [0.2, -0.12, 0.3], [0.05, 0.3, 0.16]]) {
        cloudBoxes.push({ size: [size * cs, size * cs, 1], position: [mx + size * cx, my + size * cy, mz + 0.5], color: crater });
      }
    }
    if (SKY_BATS) {
      for (const [bx, by, bz] of SKY_BATS.flocks ?? []) {
        const color = SKY_BATS.color ?? '#13111c';
        // Body plus two swept wings, a few per flock at slightly different sizes.
        for (const [ox, oy, oz, scale] of [[0, 0, 0, 1], [4, 2.5, 3, 0.75], [-3.5, 3.5, -2, 0.6]]) {
          cloudBoxes.push({ size: [1.1 * scale, 1.1 * scale, 1.1 * scale], position: [bx + ox, by + oy, bz + oz], color });
          cloudBoxes.push({ size: [2.6 * scale, 0.5 * scale, 0.9 * scale], position: [bx + ox - 1.6 * scale, by + oy + 0.5 * scale, bz + oz], color });
          cloudBoxes.push({ size: [2.6 * scale, 0.5 * scale, 0.9 * scale], position: [bx + ox + 1.6 * scale, by + oy + 0.5 * scale, bz + oz], color });
        }
      }
    }
    this.skyClouds = new THREE.Mesh(
      buildColoredBoxesGeometry(cloudBoxes, { unlit: true }),
      new THREE.MeshBasicMaterial({ vertexColors: true, fog: false, depthWrite: false })
    );
    this.skyClouds.renderOrder = -1;
    this.skyCloudMaterial = this.skyClouds.material;
    this.sky.add(dome, this.skyClouds);
    this.scene.add(this.sky);
  }

  /**
   * Bats crossing the street: one instanced sprite each, flying toward the
   * player on a lazy weave and recycling once past. The wingbeat is the
   * quad squashing horizontally rather than a flipbook -- a single frame
   * keeps every bat in one draw call, and at this size a silhouette folding
   * its wings reads the same as swapped frames would.
   */
  buildFlyingBats() {
    const count = FLYING_BATS.count ?? 7;
    // A theme can hand over a real flight sheet instead of the drawn sprite;
    // the flight itself is the same either way (see `newBat`).
    if (FLYING_BATS.sheet) {
      this.buildSheetBats(count);
      return;
    }
    const texture = createBatTexture(FLYING_BATS.color ?? '#15121f', FLYING_BATS.eyeColor ?? '#c8352a');
    this.batTexture = texture;
    const geometry = new THREE.PlaneGeometry(1, 1);
    this.batMesh = new THREE.InstancedMesh(
      geometry,
      new THREE.MeshBasicMaterial({ map: texture, transparent: true, alphaTest: 0.35, side: THREE.DoubleSide }),
      count
    );
    this.batMesh.frustumCulled = false;
    this.batMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.scene.add(this.batMesh);

    const random = createSeededRandom(FLYING_BATS.seed ?? 17);
    this.bats = [];
    for (let i = 0; i < count; i++) this.bats.push(this.newBat(random, true));
    this.batMatrix = new THREE.Matrix4();
    this.batPosition = new THREE.Vector3();
    this.batQuaternion = new THREE.Quaternion();
    this.batScale = new THREE.Vector3();
    this.batAxis = new THREE.Vector3(0, 1, 0);
    this.writeBatMatrices();
  }

  /**
   * Bats from a sprite sheet: a strip of wingbeat frames, played as a
   * flipbook. One mesh per bat rather than one instanced mesh, because each
   * bat is on its own frame and instances can't hold their own texture
   * offset without a custom shader -- a handful of extra draw calls, and the
   * drawn-sprite path above still does it in one.
   */
  buildSheetBats(count) {
    const frames = FLYING_BATS.frames ?? 5;
    const base = this.loadTexture(FLYING_BATS.sheet, (loaded) => {
      // Clones made before the image lands miss its settings, so copy them over.
      for (const sprite of this.batSprites ?? []) {
        sprite.texture.colorSpace = loaded.colorSpace;
        sprite.texture.magFilter = loaded.magFilter;
        sprite.texture.minFilter = loaded.minFilter;
        sprite.texture.needsUpdate = true;
      }
    });
    this.batTexture = base;
    // Unit width, so `size` means the same thing here as for the drawn bat:
    // the wingspan. The sheet's cells are much wider than they are tall, so
    // scaling by height instead would make these bats wider than a lane.
    const aspect = FLYING_BATS.sheetAspect ?? 348 / 161;
    const geometry = new THREE.PlaneGeometry(1, 1 / aspect);
    this.batSprites = [];
    this.bats = [];
    const random = createSeededRandom(FLYING_BATS.seed ?? 17);
    for (let i = 0; i < count; i++) {
      const texture = base.clone();
      texture.needsUpdate = true;
      texture.repeat.set(1 / frames, 1);
      const mesh = new THREE.Mesh(
        geometry,
        new THREE.MeshBasicMaterial({ map: texture, transparent: true, alphaTest: 0.4, side: THREE.DoubleSide })
      );
      mesh.frustumCulled = false;
      this.scene.add(mesh);
      this.batSprites.push({ mesh, texture });
      this.bats.push(this.newBat(random, true));
    }
    this.batFrames = frames;
    this.batAxis = new THREE.Vector3(0, 1, 0);
    this.writeBatMatrices();
  }

  /** A bat's flight: where it crosses, how high, how fast it beats. */
  newBat(random = Math.random, spreadOut = false) {
    return {
      // Drifts across the street as it comes, so it crosses the view rather
      // than running straight down it.
      x: (random() * 2 - 1) * (FLYING_BATS.spread ?? 7),
      driftX: (random() * 2 - 1) * (FLYING_BATS.drift ?? 1.6),
      height: (FLYING_BATS.minHeight ?? 3.2) + random() * (FLYING_BATS.heightRange ?? 4),
      // Bobs up and down as it flaps.
      bob: 0.25 + random() * 0.4,
      bobRate: 1.4 + random() * 1.6,
      phase: random() * Math.PI * 2,
      z: spreadOut ? BAT_NEAR_Z - random() * (BAT_NEAR_Z - BAT_FAR_Z) : BAT_FAR_Z,
      size: (FLYING_BATS.minSize ?? 0.7) + random() * (FLYING_BATS.sizeRange ?? 0.6),
      speed: (FLYING_BATS.minSpeed ?? 5) + random() * (FLYING_BATS.speedRange ?? 5),
      flapRate: 7 + random() * 6,
    };
  }

  updateFlyingBats(dt) {
    for (let i = 0; i < this.bats.length; i++) {
      const bat = this.bats[i];
      // Flies toward the player on top of the world scrolling past.
      bat.z += (this.speed + bat.speed) * dt;
      bat.x += bat.driftX * dt;
      bat.phase += dt * bat.flapRate;
      if (bat.z > BAT_NEAR_Z || Math.abs(bat.x) > (FLYING_BATS.spread ?? 7) + 4) {
        this.bats[i] = this.newBat();
      }
    }
    this.writeBatMatrices();
  }

  writeBatMatrices() {
    if (this.batSprites) {
      for (let i = 0; i < this.bats.length; i++) {
        const bat = this.bats[i];
        const { mesh, texture } = this.batSprites[i];
        // The sheet animates the wings, so the sprite keeps its shape and
        // only rides the bob.
        mesh.position.set(bat.x, bat.height + Math.sin(bat.phase) * bat.bob, bat.z);
        mesh.quaternion.setFromAxisAngle(this.batAxis, bat.driftX * 0.25);
        mesh.scale.set(bat.size, bat.size, 1);
        const frame = Math.floor(bat.phase / (Math.PI * 2) * this.batFrames) % this.batFrames;
        texture.offset.x = ((frame % this.batFrames) + this.batFrames) % this.batFrames / this.batFrames;
      }
      return;
    }
    for (let i = 0; i < this.bats.length; i++) {
      const bat = this.bats[i];
      // Wings folding: the sprite narrows and widens on the beat, and the
      // whole bat rides up and down with it.
      const beat = Math.sin(bat.phase);
      const wings = 0.45 + 0.55 * Math.abs(Math.cos(bat.phase));
      this.batPosition.set(bat.x, bat.height + beat * bat.bob, bat.z);
      // Banks toward the way it's drifting.
      this.batQuaternion.setFromAxisAngle(this.batAxis, bat.driftX * 0.25);
      this.batScale.set(bat.size * wings, bat.size * 0.62, 1);
      this.batMatrix.compose(this.batPosition, this.batQuaternion, this.batScale);
      this.batMesh.setMatrixAt(i, this.batMatrix);
    }
    this.batMesh.instanceMatrix.needsUpdate = true;
  }

  /**
   * The fog you move through, as opposed to the distance it hides: tall soft
   * billboards standing across the street, drifting toward the camera and
   * sweeping past it. Pulling the distance fog in alone reads as the far end
   * being cut off; it's these passing over the view that make it feel like
   * being inside a bank. They're only drawn while a bank is up.
   */
  buildFogVolume() {
    const volume = FOG_BANKS.volume;
    const count = volume.count ?? 9;
    const texture = this.mistTextures?.[0] ?? createMistTexture(GROUND_MIST?.seed ?? 5);
    if (!this.mistTextures) this.mistTextures = [texture];
    // Upright, facing the camera down the street.
    const geometry = new THREE.PlaneGeometry(1, 1);
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      color: volume.color ?? FOG_BANKS.color ?? '#8a85a8',
      transparent: true,
      opacity: 0,
      depthWrite: false,
      side: THREE.DoubleSide,
      // Not fogged itself: a fog bank tinted by the distance fog it stands
      // in just dissolves into the haze, and the whole point of these is the
      // contrast of something passing close by.
      fog: false,
    });
    this.fogVolumeMesh = new THREE.InstancedMesh(geometry, material, count);
    this.fogVolumeMesh.frustumCulled = false;
    this.fogVolumeMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.fogVolumeMesh.visible = false;
    this.fogVolumeMesh.renderOrder = 2;
    this.scene.add(this.fogVolumeMesh);

    const random = createSeededRandom((GROUND_MIST?.seed ?? 5) + 31);
    this.fogVolume = [];
    for (let i = 0; i < count; i++) {
      this.fogVolume.push({
        x: (random() * 2 - 1) * (volume.spread ?? 6),
        y: (volume.minHeight ?? 1.2) + random() * (volume.heightRange ?? 3),
        // Spread down the street so they arrive in a steady procession.
        z: FOG_VOLUME_NEAR_Z - (i / count) * (FOG_VOLUME_NEAR_Z - FOG_VOLUME_FAR_Z) - random() * 6,
        width: (volume.minSize ?? 14) + random() * (volume.sizeRange ?? 14),
        height: (volume.minVertical ?? 7) + random() * (volume.verticalRange ?? 6),
        speed: 0.55 + random() * 0.4,
      });
    }
    this.fogVolumeMatrix = new THREE.Matrix4();
    this.fogVolumePosition = new THREE.Vector3();
    this.fogVolumeQuaternion = new THREE.Quaternion();
    this.fogVolumeScale = new THREE.Vector3();
  }

  /** Drifts the fog banks past the camera and fades the whole set with the bank. */
  updateFogVolume(dt, intensity) {
    const visible = intensity > 0.01;
    this.fogVolumeMesh.visible = visible;
    if (!visible) return;
    const volume = FOG_BANKS.volume;
    this.fogVolumeMesh.material.opacity = (volume.opacity ?? 0.42) * intensity;
    for (const bank of this.fogVolume) {
      bank.z += this.speed * dt * bank.speed;
      if (bank.z > FOG_VOLUME_NEAR_Z) {
        bank.z = FOG_VOLUME_FAR_Z;
        bank.x = (Math.random() * 2 - 1) * (volume.spread ?? 6);
        bank.width = (volume.minSize ?? 14) + Math.random() * (volume.sizeRange ?? 14);
        bank.y = (volume.minHeight ?? 1.2) + Math.random() * (volume.heightRange ?? 3);
      }
    }
    for (let i = 0; i < this.fogVolume.length; i++) {
      const bank = this.fogVolume[i];
      this.fogVolumePosition.set(bank.x, bank.y, bank.z);
      this.fogVolumeScale.set(bank.width, bank.height, 1);
      this.fogVolumeMatrix.compose(this.fogVolumePosition, this.fogVolumeQuaternion, this.fogVolumeScale);
      this.fogVolumeMesh.setMatrixAt(i, this.fogVolumeMatrix);
    }
    this.fogVolumeMesh.instanceMatrix.needsUpdate = true;
  }

  /**
   * Fog banks: every so often the street fogs over properly, pulling the
   * view in so obstacles only appear once they're close, then thinning back
   * out. The visibility it closes to is bounded by
   * `FOG_BANK_MIN_VISIBILITY` -- there has to be enough road showing to read
   * and clear whatever is in it (a cactus needs ~4 units of warning, a bus
   * mount ~8), so a theme can make it murky but not unfair.
   */
  updateFogBanks(dt) {
    const bank = this.fogBank;
    bank.timer -= dt;
    if (bank.timer <= 0) {
      bank.inside = !bank.inside;
      const range = bank.inside ? FOG_BANKS.durationRange ?? [5, 9] : FOG_BANKS.clearRange ?? [14, 26];
      bank.timer = randomBetween(...range);
    }
    const target = bank.inside ? 1 : 0;
    const step = dt / (FOG_BANKS.fadeSeconds ?? 2.5);
    bank.intensity += Math.max(-step, Math.min(step, target - bank.intensity));
    if (bank.intensity <= 0 && target === 0) {
      if (this.fogVolumeMesh) this.fogVolumeMesh.visible = false;
      return;
    }

    // Thickest the bank may get, floored so the road never closes in past
    // what the player can react to.
    const far = Math.max(FOG_BANK_MIN_VISIBILITY, FOG_BANKS.far ?? 34);
    const near = Math.min(FOG_BANKS.near ?? 6, far - 4);
    this.scene.fog.near = THREE.MathUtils.lerp(FOG_NEAR, near, bank.intensity);
    this.scene.fog.far = THREE.MathUtils.lerp(FOG_FAR, far, bank.intensity);
    // Lift the fog toward a paler grey as it thickens. Without this a night
    // theme's fog colour is nearly black, so a bank reads as the lights
    // going out rather than as fog rolling in.
    if (FOG_BANKS.color) {
      this.fogBankColor ??= new THREE.Color(FOG_BANKS.color);
      this.scene.fog.color.lerp(this.fogBankColor, bank.intensity * (FOG_BANKS.colorStrength ?? 0.75));
      this.scene.background.copy(this.scene.fog.color);
    }
    // The mist reads this to thicken with the bank. It leads on the way in,
    // so the ground fogs up before the view closes -- you can see the bank
    // coming -- and simply follows on the way out.
    bank.lead = Math.min(1, bank.intensity * (FOG_BANKS.mistLead ?? 2.2));
    if (this.fogVolume) this.updateFogVolume(dt, bank.intensity);
  }

  /**
   * Low mist over the road: a drift of soft patches lying flat just above
   * the cobbles, scrolling toward the player and recycling behind the
   * camera. Full-width sheets were tried first -- seen at this grazing angle
   * they overlap into a flat grey wash that hides the street instead of
   * curling over it. Separate patches keep the gaps that make it read as
   * fog, and as one instanced mesh they still cost a single draw call.
   * Unlit, so night lighting can't dim them; the scene's distance fog still
   * blends them into the horizon.
   */
  buildGroundMist() {
    const count = GROUND_MIST.count ?? 22;
    const texture = createMistTexture(GROUND_MIST.seed ?? 5);
    this.mistTextures = [texture];
    const geometry = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      color: GROUND_MIST.color ?? '#cfc9e6',
      transparent: true,
      opacity: GROUND_MIST.opacity ?? 0.5,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    this.mistMesh = new THREE.InstancedMesh(geometry, material, count);
    this.mistMesh.frustumCulled = false;
    this.mistMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.scene.add(this.mistMesh);

    const random = createSeededRandom(GROUND_MIST.seed ?? 5);
    this.mistPatches = [];
    for (let i = 0; i < count; i++) {
      this.mistPatches.push({
        // How far this patch has been pushed apart by the player running
        // through it; recovers once they're past.
        disturbed: 0,
        pushX: 0,
        // Spread across the road and a little way up the pavements.
        x: (random() * 2 - 1) * (GROUND_MIST.spread ?? GROUND_WIDTH * 0.55),
        y: (GROUND_MIST.minHeight ?? 0.06) + random() * (GROUND_MIST.heightRange ?? 0.5),
        z: MIST_NEAR_Z - random() * (MIST_NEAR_Z - MIST_FAR_Z),
        size: (GROUND_MIST.minSize ?? 6) + random() * (GROUND_MIST.sizeRange ?? 9),
        spin: (random() * 2 - 1) * 0.12,
        angle: random() * Math.PI * 2,
        // Patches sit at different depths in the drift, so they slide past
        // each other rather than moving as one sheet.
        speed: 0.55 + random() * 0.55,
      });
    }
    this.mistMatrix = new THREE.Matrix4();
    this.mistPosition = new THREE.Vector3();
    this.mistQuaternion = new THREE.Quaternion();
    this.mistScale = new THREE.Vector3();
    this.mistAxis = new THREE.Vector3(0, 1, 0);
    this.mistTint = new THREE.Color(1, 1, 1);
    // Seed the per-instance tints so the attribute exists before the first write.
    for (let i = 0; i < count; i++) this.mistMesh.setColorAt(i, this.mistTint);
    this.writeMistMatrices();
  }

  updateGroundMist(dt) {
    const random = Math.random;
    const wake = GROUND_MIST.wake ?? 2.6;
    // Thicker and heavier as a fog bank closes in (see `updateFogBanks`).
    const thickness = this.fogBank?.lead ?? 0;
    this.mistThickness = thickness;
    this.mistMesh.material.opacity = (GROUND_MIST.opacity ?? 0.22) * (1 + thickness * (GROUND_MIST.bankBoost ?? 2.2));
    for (const patch of this.mistPatches) {
      patch.z += this.speed * dt * patch.speed;
      patch.angle += patch.spin * dt;

      // Running through a patch breaks it up: it thins out, billows wider
      // and gets shouldered aside, then settles once the player is past.
      const reach = wake + patch.size * 0.25;
      const near = Math.abs(patch.z) < reach && Math.abs(patch.x - this.playerX) < reach;
      if (near) {
        patch.disturbed = Math.min(1, patch.disturbed + dt * 3.5);
        patch.pushX += Math.sign(patch.x - this.playerX || 1) * dt * 1.6 * patch.disturbed;
        patch.angle += patch.spin * dt * 4;
      } else {
        patch.disturbed = Math.max(0, patch.disturbed - dt * 0.8);
        patch.pushX *= Math.max(0, 1 - dt * 1.5);
      }
      if (patch.z - patch.size > MIST_NEAR_Z) {
        // Back to the far end with a new size and lane, so the drift never
        // settles into a visible loop.
        patch.z = MIST_FAR_Z;
        patch.disturbed = 0;
        patch.pushX = 0;
        patch.x = (random() * 2 - 1) * (GROUND_MIST.spread ?? GROUND_WIDTH * 0.55);
        patch.size = (GROUND_MIST.minSize ?? 6) + random() * (GROUND_MIST.sizeRange ?? 9);
        patch.y = (GROUND_MIST.minHeight ?? 0.06) + random() * (GROUND_MIST.heightRange ?? 0.5);
      }
    }
    this.writeMistMatrices();
  }

  writeMistMatrices() {
    for (let i = 0; i < this.mistPatches.length; i++) {
      const patch = this.mistPatches[i];
      // Patches swell as a bank rolls in, so the ground thickens rather than
      // just brightening.
      const spread = (1 + patch.disturbed * 0.35) * (1 + (this.mistThickness ?? 0) * (GROUND_MIST.bankSwell ?? 0.55));
      this.mistPosition.set(patch.x + patch.pushX, patch.y, patch.z);
      this.mistQuaternion.setFromAxisAngle(this.mistAxis, patch.angle);
      this.mistScale.set(patch.size * spread, 1, patch.size * 0.7 * spread);
      this.mistMatrix.compose(this.mistPosition, this.mistQuaternion, this.mistScale);
      this.mistMesh.setMatrixAt(i, this.mistMatrix);
      // Per-instance tint: a disturbed patch is dimmed toward nothing, which
      // against a dark street reads as it being torn apart. (Instances share
      // one material, so this is the only per-patch fade available.)
      this.mistTint.setScalar(1 - patch.disturbed * 0.8);
      this.mistMesh.setColorAt(i, this.mistTint);
    }
    this.mistMesh.instanceMatrix.needsUpdate = true;
    if (this.mistMesh.instanceColor) this.mistMesh.instanceColor.needsUpdate = true;
  }

  /**
   * Trees and lamps alternate along each pavement at STREET_PROP_SPACING,
   * the right row offset by half a step so the two sides don't mirror each
   * other. Each slot is one instance in one of two InstancedMeshes; the
   * slots scroll and wrap by the row's full length, so the spacing never
   * drifts.
   */
  buildStreetProps() {
    const kinds = STREET_PROPS ?? [
      { id: 'tree', boxes: TREE_BOXES, vary: 'turn' },
      { id: 'lamp', boxes: LAMP_BOXES, vary: 'faceRoad' },
    ];
    const slotsPerSide = kinds.length * Math.ceil((BUILDING_ROW_NEAR_Z - BUILDING_ROW_FAR_Z) / (STREET_PROP_SPACING * kinds.length));
    // Each kind takes every nth slot on both pavements, so one instanced mesh
    // per kind covers the whole street in a single draw call.
    const perType = Math.ceil(slotsPerSide / kinds.length) * 2;
    const material = new THREE.MeshLambertMaterial({ vertexColors: true });
    this.propMeshes = kinds.map((kind) => {
      const mesh = new THREE.InstancedMesh(buildColoredBoxesGeometry(kind.boxes), material, perType);
      // Instances span the whole street; the base geometry's bounds (one
      // prop at the origin) would cull them all incorrectly.
      mesh.frustumCulled = false;
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      this.scene.add(mesh);
      return mesh;
    });
    this.streetProps = [];
    this.streetPropRowLength = slotsPerSide * STREET_PROP_SPACING;
    const nextIndex = kinds.map(() => 0);
    for (const side of [-1, 1]) {
      for (let slot = 0; slot < slotsPerSide; slot++) {
        const kindIndex = slot % kinds.length;
        const kind = kinds[kindIndex].vary === 'faceRoad' ? 'lamp' : 'tree';
        const prop = {
          kind,
          mesh: this.propMeshes[kindIndex],
          index: nextIndex[kindIndex]++,
          side,
          z: BUILDING_ROW_NEAR_Z - slot * STREET_PROP_SPACING - (side > 0 ? STREET_PROP_SPACING / 2 : 0),
          rotation: 0,
          scale: 1,
        };
        this.varyStreetProp(prop);
        this.streetProps.push(prop);
      }
    }
    if (COBWEBS) this.buildCobwebs(kinds);

    this.streetPropMatrix = new THREE.Matrix4();
    this.streetPropPosition = new THREE.Vector3();
    this.streetPropQuaternion = new THREE.Quaternion();
    this.streetPropScale = new THREE.Vector3();
    this.streetPropAxis = new THREE.Vector3(0, 1, 0);
    this.writeStreetPropMatrices();
  }

  /**
   * Cobwebs slung under the street lamps: one instanced quad per lamp,
   * tucked below the lantern and facing the road, so the lit ones catch the
   * lamp glow. Some lamps have none -- those instances are scaled away.
   */
  buildCobwebs(kinds) {
    const lampSlots = this.streetProps.filter((prop) => prop.kind === 'lamp');
    if (!lampSlots.length) return;
    const geometry = new THREE.PlaneGeometry(1, 1);
    const material = new THREE.MeshBasicMaterial({
      map: createCobwebTexture(COBWEBS.color ?? '#d8d6e8'),
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.InstancedMesh(geometry, material, lampSlots.length);
    mesh.frustumCulled = false;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.scene.add(mesh);
    this.propMeshes.push(mesh);
    lampSlots.forEach((lamp, index) => {
      const prop = {
        kind: 'cobweb',
        mesh,
        index,
        side: lamp.side,
        // Hanging off the lamp's road-side arm, just under the lantern.
        x: lamp.side * (STREET_PROP_X - 0.42),
        y: 2.42,
        z: lamp.z,
        // Followed around the row, so a web stays on its own lamp.
        lamp,
        rotation: 0,
        scale: 0,
      };
      this.varyStreetProp(prop);
      this.streetProps.push(prop);
    });
  }

  /** Trees get a random size and turn each time they wrap, so the row doesn't visibly repeat; lamps always face the road. */
  varyStreetProp(prop) {
    if (prop.kind === 'cobweb') {
      // Flat against the facade, facing the street. Only some slots carry one,
      // and an empty slot is scaled away rather than tracked separately.
      prop.rotation = prop.side < 0 ? Math.PI / 2 : -Math.PI / 2;
      prop.scale = Math.random() < (COBWEBS?.chance ?? 0) ? (COBWEBS?.size ?? 1) * (0.75 + Math.random() * 0.5) : 0;
    } else if (prop.kind === 'tree') {
      prop.rotation = Math.random() * Math.PI * 2;
      prop.scale = 0.85 + Math.random() * 0.3;
    } else {
      prop.rotation = prop.side < 0 ? Math.PI : 0;
    }
  }

  updateStreetProps(dt) {
    const dz = this.speed * dt;
    for (const prop of this.streetProps) {
      if (prop.lamp) {
        // Webs don't wrap on their own; they ride the lamp they hang on. A
        // lamp that has just wrapped jumps far back down the street, which is
        // when its web re-rolls (so the same lamp isn't always webbed).
        if (prop.lamp.z < prop.z - 1) this.varyStreetProp(prop);
        prop.z = prop.lamp.z;
        continue;
      }
      prop.z += dz;
      if (prop.z > BUILDING_ROW_NEAR_Z) {
        prop.z -= this.streetPropRowLength;
        this.varyStreetProp(prop);
      }
    }
    this.writeStreetPropMatrices();
  }

  writeStreetPropMatrices() {
    for (const prop of this.streetProps) {
      this.streetPropPosition.set(prop.x ?? prop.side * STREET_PROP_X, prop.y ?? 0, prop.z);
      this.streetPropQuaternion.setFromAxisAngle(this.streetPropAxis, prop.rotation);
      this.streetPropScale.setScalar(prop.scale);
      this.streetPropMatrix.compose(this.streetPropPosition, this.streetPropQuaternion, this.streetPropScale);
      prop.mesh.setMatrixAt(prop.index, this.streetPropMatrix);
    }
    for (const mesh of this.propMeshes) mesh.instanceMatrix.needsUpdate = true;
  }

  /** One material per facade look, shared by both rows (lighting is baked into vertex colors). */
  getFacadeMaterial(widthIndex, upperFloors, colorIndex) {
    const key = `${widthIndex}|${upperFloors}|${colorIndex}`;
    if (!this.buildingMaterials.has(key)) {
      const seed = widthIndex * 100 + upperFloors * 10 + colorIndex + 1;
      const map = createFacadeTexture(BUILDING_WIDTHS[widthIndex], upperFloors, BUILDING_WALL_COLORS[colorIndex], seed);
      this.buildingMaterials.set(key, new THREE.MeshLambertMaterial({ map, vertexColors: true }));
    }
    return this.buildingMaterials.get(key);
  }

  /** Builds a texture from a small character-grid pixel-art picture. */
  createPixelArtTexture(art, palette) {
    const canvas = document.createElement('canvas');
    canvas.width = art[0].length;
    canvas.height = art.length;
    const ctx = canvas.getContext('2d');
    art.forEach((row, y) => {
      [...row].forEach((ch, x) => {
        if (ch === '.') return;
        ctx.fillStyle = palette[ch];
        ctx.fillRect(x, y, 1, 1);
      });
    });
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.magFilter = THREE.NearestFilter;
    texture.minFilter = THREE.NearestFilter;
    texture.generateMipmaps = false;
    return texture;
  }

  buildPuddles() {
    this.puddles = [];
    this.puddleEffect = null;
    if (!PUDDLES_ENABLED) return;

    const makeSprite = (texture) => {
      const sprite = new THREE.Sprite(
        new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false, fog: false })
      );
      sprite.renderOrder = 11;
      sprite.visible = false;
      this.scene.add(sprite);
      return sprite;
    };

    // Dizzy (slide / slippery): three pixel stars circling over the head.
    const starTexture = this.createPixelArtTexture(
      [
        '...K...',
        '..KYK..',
        'KKYYYKK',
        '.KYYYK.',
        '.KYKYK.',
        '..K.K..',
        '.......',
      ],
      { K: '#b8860b', Y: '#ffd23f' }
    );
    this.playerStars = [0, 1, 2].map(() => makeSprite(starTexture));

    // Inverted controls: a blue badge with two opposing arrows (reverse card).
    this.playerReverseIcon = makeSprite(
      this.createPixelArtTexture(
        [
          '..KKKKKKKK..',
          '.KBBBBBBBBK.',
          'KBBBWBBBBBBK',
          'KBBWWWWWWBBK',
          'KBBBWBBBBBBK',
          'KBBBBBBBBBBK',
          'KBBBBBBWBBBK',
          'KBBWWWWWWBBK',
          'KBBBBBBWBBBK',
          'KBBBBBBBBBBK',
          '.KBBBBBBBBK.',
          '..KKKKKKKK..',
        ],
        { K: '#ffffff', B: '#2a6fd6', W: '#ffffff' }
      )
    );

    // Puddle art: a chunky pixel-art ellipse of water.
    const puddleTexture = this.createPixelArtTexture(
      [
        '....DDDDDDDD....',
        '..DDBBBBBBBBDD..',
        '.DBBBBLLBBBBBBD.',
        'DBBBLLBBBBBBBBBD',
        'DBBBBBBBBBLBBBBD',
        'DBBBBBBBBLLBBBBD',
        '.DBBBBBBBBBBBBD.',
        '..DDBBBBBBBBDD..',
        '....DDDDDDDD....',
      ],
      { D: '#2f6fa8', B: '#5aa9e6', L: '#c9e8ff' }
    );
    for (let i = 0; i < PUDDLE_POOL_SIZE; i++) {
      const mesh = new THREE.Mesh(
        new THREE.PlaneGeometry(LANE_WIDTH * 0.85, PUDDLE_LENGTH),
        new THREE.MeshBasicMaterial({ map: puddleTexture, transparent: true, depthWrite: false })
      );
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.y = 0.03;
      mesh.visible = false;
      this.scene.add(mesh);
      this.puddles.push({ mesh, lane: 1, z: SPAWN_Z, active: false, triggered: false });
    }
    this.puddleTimer = randomBetween(...PUDDLE_FIRST_RANGE);
  }

  resetPuddleStatus() {
    this.puddleEffect = null;
    if (this.playerStars) {
      for (const star of this.playerStars) star.visible = false;
      this.playerReverseIcon.visible = false;
    }
  }

  resetPuddles() {
    if (!PUDDLES_ENABLED) return;
    this.resetPuddleStatus();
    this.puddleTimer = randomBetween(...PUDDLE_FIRST_RANGE);
    for (const puddle of this.puddles) {
      puddle.active = false;
      puddle.mesh.visible = false;
    }
  }

  updatePuddles(dt) {
    if (!PUDDLES_ENABLED) return;

    // Puddles only form while it's actually raining.
    if (this.stormRaining && this.stormIntensity > 0.5) this.puddleTimer -= dt;
    if (this.puddleTimer <= 0) {
      this.puddleTimer = randomBetween(...PUDDLE_INTERVAL_RANGE);
      this.spawnPuddle();
    }

    const time = performance.now() / 1000;
    for (const puddle of this.puddles) {
      if (!puddle.active) continue;
      puddle.z += this.speed * dt;
      if (puddle.z - PUDDLE_LENGTH / 2 > DESPAWN_Z) {
        puddle.active = false;
        puddle.mesh.visible = false;
        continue;
      }
      puddle.mesh.position.z = puddle.z;
      if (
        !puddle.triggered &&
        puddle.lane === this.playerLane &&
        this.playerY < PUDDLE_MAX_JUMP_HEIGHT &&
        Math.abs(puddle.z) < PUDDLE_LENGTH / 2
      ) {
        puddle.triggered = true;
        this.applyPuddleEffect();
      }
    }

    if (this.puddleEffect) {
      this.puddleEffect.remaining -= dt;
      if (this.puddleEffect.remaining <= 0) this.resetPuddleStatus();
    }
    const type = this.puddleEffect?.type;
    const headY = this.playerY + this.playerBaseHeight + 0.45;
    this.playerReverseIcon.visible = type === 'inverted';
    if (type === 'inverted') {
      const bob = Math.sin(time * 12) * 0.05;
      this.playerReverseIcon.scale.set(0.42, 0.42, 1);
      this.playerReverseIcon.position.set(this.playerX, headY + bob, 0);
    }
    const dizzy = type === 'slide' || type === 'slippery';
    this.playerStars.forEach((star, i) => {
      star.visible = dizzy;
      if (!dizzy) return;
      const angle = time * 7 + (i * Math.PI * 2) / 3;
      star.scale.set(0.24, 0.24, 1);
      star.position.set(this.playerX + Math.cos(angle) * 0.42, headY + Math.sin(angle) * 0.1, 0);
    });
  }

  /** True if no ground-level obstacle in `lane` is within `clearance` of `z`. */
  isLaneClearOfObstacles(lane, z, clearance) {
    for (const obstacle of this.obstacles) {
      if (!obstacle.active || obstacle.lane !== lane || obstacle.baseY > 0) continue;
      const halfLength = OBSTACLE_DEFS[obstacle.type].length / 2;
      if (Math.abs(obstacle.z - z) < clearance + halfLength) return false;
    }
    return true;
  }

  spawnPuddle() {
    const puddle = this.puddles.find((p) => !p.active);
    if (!puddle) return;
    const lanes = [0, 1, 2].filter((lane) => this.isLaneClearOfObstacles(lane, SPAWN_Z, PUDDLE_CLEARANCE));
    if (lanes.length === 0) {
      this.puddleTimer = 1; // try again shortly
      return;
    }
    puddle.lane = this.pickRandom(lanes);
    puddle.z = SPAWN_Z;
    puddle.active = true;
    puddle.triggered = false;
    puddle.mesh.position.set(LANE_X[puddle.lane], 0.03, puddle.z);
    puddle.mesh.visible = true;
  }

  applyPuddleEffect() {
    const effect = this.pickRandom(PUDDLE_EFFECTS);
    this.puddleEffect = { type: effect.type, remaining: effect.seconds };
    if (effect.type === 'slide') {
      // Skids into a neighbouring lane (always toward one that exists).
      const options = [this.playerLane - 1, this.playerLane + 1].filter((lane) => lane >= 0 && lane < LANE_X.length);
      this.playerTargetLane = this.pickRandom(options);
    }
  }

  buildWeather() {
    this.clearSkyColor = new THREE.Color(SKY_HORIZON_COLOR);
    this.stormSkyColor = new THREE.Color(STORM_SKY_TINT);
    this.stormFogColor = new THREE.Color(STORM_FOG_COLOR);
    this.tmpColor = new THREE.Color();

    // Rain: one LineSegments, two vertices per drop, recycled as they fall.
    this.rainDrops = [];
    const positions = new Float32Array(RAIN_DROP_COUNT * 6);
    for (let i = 0; i < RAIN_DROP_COUNT; i++) {
      this.rainDrops.push({
        x: (Math.random() * 2 - 1) * RAIN_AREA_HALF_WIDTH,
        y: Math.random() * RAIN_AREA_HEIGHT,
        z: RAIN_FAR_Z + Math.random() * (RAIN_NEAR_Z - RAIN_FAR_Z),
        speed: RAIN_FALL_SPEED * (0.8 + Math.random() * 0.4),
      });
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.rainPositions = geometry.getAttribute('position');
    this.rainMaterial = new THREE.LineBasicMaterial({ color: '#cfe3f5', transparent: true, opacity: 0, depthWrite: false });
    this.rain = new THREE.LineSegments(geometry, this.rainMaterial);
    this.rain.frustumCulled = false;
    this.rain.visible = false;
    this.scene.add(this.rain);

    // Ground warning marker for the lane about to be struck: soft yellow
    // fill with a dashed border.
    const markerCanvas = document.createElement('canvas');
    markerCanvas.width = 128;
    markerCanvas.height = 256;
    const mctx = markerCanvas.getContext('2d');
    mctx.fillStyle = 'rgba(255, 242, 122, 0.4)';
    mctx.fillRect(0, 0, 128, 256);
    mctx.strokeStyle = '#ffe14a';
    mctx.lineWidth = 8;
    mctx.setLineDash([22, 14]);
    mctx.strokeRect(8, 8, 112, 240);
    const markerTexture = new THREE.CanvasTexture(markerCanvas);
    markerTexture.colorSpace = THREE.SRGBColorSpace;
    this.strikeMarker = new THREE.Mesh(
      new THREE.PlaneGeometry(LANE_WIDTH * 0.95, 2.6),
      new THREE.MeshBasicMaterial({ map: markerTexture, transparent: true, opacity: 0, depthWrite: false, fog: false })
    );
    this.strikeMarker.rotation.x = -Math.PI / 2;
    this.strikeMarker.position.y = 0.06;
    this.strikeMarker.visible = false;
    this.scene.add(this.strikeMarker);

    // Pixel-art "!" badge (12x12 art, drawn 1 art-pixel = 1 canvas pixel and
    // scaled up with nearest-neighbour filtering so it stays crisp).
    const badgeArt = [
      '..KKKKKKKK..',
      '.KRRRRRRRRK.',
      'KRRRRWWRRRRK',
      'KRRRRWWRRRRK',
      'KRRRRWWRRRRK',
      'KRRRRWWRRRRK',
      'KRRRRWWRRRRK',
      'KRRRRRRRRRRK',
      'KRRRRWWRRRRK',
      'KRRRRWWRRRRK',
      '.KRRRRRRRRK.',
      '..KKKKKKKK..',
    ];
    const badgePalette = { K: '#ffffff', R: '#e8262b', W: '#ffffff' };
    const badgeCanvas = document.createElement('canvas');
    badgeCanvas.width = badgeCanvas.height = 12;
    const bctx = badgeCanvas.getContext('2d');
    badgeArt.forEach((row, y) => {
      [...row].forEach((ch, x) => {
        if (ch === '.') return;
        bctx.fillStyle = badgePalette[ch];
        bctx.fillRect(x, y, 1, 1);
      });
    });
    const badgeTexture = new THREE.CanvasTexture(badgeCanvas);
    badgeTexture.colorSpace = THREE.SRGBColorSpace;
    badgeTexture.magFilter = THREE.NearestFilter;
    badgeTexture.minFilter = THREE.NearestFilter;
    badgeTexture.generateMipmaps = false;
    this.warningBadge = new THREE.Sprite(
      new THREE.SpriteMaterial({ map: badgeTexture, transparent: true, depthTest: false, fog: false })
    );
    this.warningBadge.renderOrder = 11;
    this.warningBadge.visible = false;
    this.scene.add(this.warningBadge);

    // Bolt: camera-facing ribbons (GL lines are 1px, far too thin) -- a soft
    // glow under a white core, for the main trunk and two short branches.
    this.boltLine = new THREE.Group();
    const makeRibbon = (pointCount, color, opacity, halfWidth) => {
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pointCount * 2 * 3), 3));
      const index = [];
      for (let i = 0; i < pointCount - 1; i++) {
        const k = i * 2;
        index.push(k, k + 1, k + 2, k + 1, k + 3, k + 2);
      }
      geometry.setIndex(index);
      const mesh = new THREE.Mesh(
        geometry,
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity, fog: false, depthWrite: false, depthTest: false, side: THREE.DoubleSide })
      );
      mesh.frustumCulled = false;
      mesh.renderOrder = 10;
      this.boltLine.add(mesh);
      return { mesh, halfWidth, pointCount };
    };
    const layerSpecs = [
      ['#ffe866', 0.3, 1],
      ['#fffbe0', 1, 0.32],
    ];
    this.boltTrunk = layerSpecs.map(([c, o, w]) => makeRibbon(BOLT_POINTS, c, o, BOLT_HALF_WIDTH * w));
    this.boltBranches = [0, 1].map(() =>
      layerSpecs.map(([c, o, w]) => makeRibbon(BOLT_BRANCH_POINTS, c, o, BOLT_HALF_WIDTH * w * 0.55))
    );
    this.boltLine.visible = false;
    this.scene.add(this.boltLine);

    // Full-canvas white flash.
    this.flashEl = document.createElement('div');
    this.flashEl.style.cssText = 'position:absolute;inset:0;background:#fff;opacity:0;pointer-events:none;z-index:2;';
    const host = this.canvas.parentElement;
    if (host) {
      if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
      host.appendChild(this.flashEl);
    }

    this.resetWeather();
  }

  resetWeather() {
    this.stormIntensity = 0;
    this.stormRaining = false;
    this.stormTimer = randomBetween(...STORM_FIRST_CLEAR_RANGE);
    // Every run opens in clear air: a fresh start shouldn't drop the player
    // straight into a fog bank they never saw roll in.
    if (this.fogBank) {
      this.fogBank.inside = false;
      this.fogBank.intensity = 0;
      this.fogBank.lead = 0;
      this.fogBank.timer = randomBetween(...(FOG_BANKS.firstClearRange ?? [8, 16]));
      this.scene.fog.near = FOG_NEAR;
      this.scene.fog.far = FOG_FAR;
      if (this.mistMesh) this.mistMesh.material.opacity = GROUND_MIST?.opacity ?? 0.22;
    }
    this.strike = null;
    this.chainFromLane = null;
    this.strikeCooldown = this.nextStrikeCooldown();
    this.flashLevel = 0;
    this.playerHitTimer = 0;
    if (this.rain) {
      this.rain.visible = false;
      this.strikeMarker.visible = false;
      this.warningBadge.visible = false;
      this.boltLine.visible = false;
      this.flashEl.style.opacity = '0';
      if (this.spill) for (const item of this.spill) { item.active = false; item.mesh.visible = false; }
      this.applyStormLook();
      if (this.player) this.player.material.color.copy(this.playerRestTint);
    }
  }

  updateWeather(dt) {
    // Storm on/off cycle.
    this.stormTimer -= dt;
    if (this.stormTimer <= 0) {
      this.stormRaining = !this.stormRaining;
      this.stormTimer = randomBetween(...(this.stormRaining ? STORM_RAIN_RANGE : STORM_CLEAR_RANGE));
      this.strikeCooldown = Math.max(this.strikeCooldown, 1.5);
    }
    const target = this.stormRaining ? 1 : 0;
    const step = dt / STORM_FADE_SECONDS;
    this.stormIntensity += Math.max(-step, Math.min(step, target - this.stormIntensity));
    this.applyStormLook();

    // Rain.
    this.rain.visible = this.stormIntensity > 0.01;
    if (this.rain.visible) {
      this.rainMaterial.opacity = 0.55 * this.stormIntensity;
      const pos = this.rainPositions.array;
      const centerX = this.playerX;
      for (let i = 0; i < this.rainDrops.length; i++) {
        const d = this.rainDrops[i];
        d.y -= d.speed * dt;
        d.x -= RAIN_SLANT * dt;
        d.z += this.speed * dt * 0.6;
        if (d.y < 0 || d.z > RAIN_NEAR_Z) {
          d.y = RAIN_AREA_HEIGHT;
          d.x = (Math.random() * 2 - 1) * RAIN_AREA_HALF_WIDTH + RAIN_SLANT;
          d.z = RAIN_FAR_Z + Math.random() * (RAIN_NEAR_Z - RAIN_FAR_Z);
        }
        const o = i * 6;
        pos[o] = centerX + d.x;
        pos[o + 1] = d.y;
        pos[o + 2] = d.z;
        pos[o + 3] = centerX + d.x - RAIN_SLANT * 0.03;
        pos[o + 4] = d.y + RAIN_DROP_LENGTH;
        pos[o + 5] = d.z - 0.02;
      }
      this.rainPositions.needsUpdate = true;
    }

    // Lightning: only in a properly developed storm.
    if (this.strike) {
      this.updateStrike(dt);
    } else if (this.stormRaining && this.stormIntensity > 0.7) {
      this.strikeCooldown -= dt;
      if (this.strikeCooldown <= 0) this.beginStrike();
    }

    this.updateSpill(dt);
    this.updateWarningBadge();

    // Flash + hit feedback decay. A collision earlier in this same frame has
    // already ended the run (and cleared the flash); the loop stops after this
    // frame, so re-lighting the flash here would leave it stuck over the
    // game-over card. Lightning landing this frame is dropped the same way.
    if (this.state === 'gameover') this.flashLevel = 0;
    this.flashLevel = Math.max(0, this.flashLevel - dt * 4);
    this.flashEl.style.opacity = String(this.flashLevel * 0.85);
    if (this.playerHitTimer > 0) {
      this.playerHitTimer -= dt;
      const blink = Math.floor(this.playerHitTimer * 14) % 2 === 0;
      if (!this.hasEffect('invincible')) {
        this.player.material.color.set(this.playerHitTimer > 0 && blink ? '#ffe45c' : this.playerRestTint);
      }
    }
  }

  applyStormLook() {
    const t = this.stormIntensity;
    this.skyDomeMaterial.color.copy(this.tmpColor.set('#ffffff').lerp(this.stormSkyColor, t * 0.85));
    this.skyCloudMaterial.color.copy(this.tmpColor.set('#ffffff').lerp(this.stormSkyColor, t));
    this.scene.fog.color.copy(this.clearSkyColor).lerp(this.stormFogColor, t);
    this.scene.background.copy(this.scene.fog.color);
  }

  /** Gap to the next strike: roomy early in a run, a bit tighter late (see STORM_PRESSURE_*). */
  nextStrikeCooldown() {
    const pressure = rampBetween(this.difficulty ?? 0, STORM_PRESSURE_FROM, STORM_PRESSURE_TO);
    const scale = THREE.MathUtils.lerp(LIGHTNING_INTERVAL_SCALE_EASY, LIGHTNING_INTERVAL_SCALE_HARD, pressure);
    return randomBetween(...LIGHTNING_INTERVAL_RANGE) * scale;
  }

  beginStrike() {
    let lane = Math.random() < LIGHTNING_TARGET_PLAYER_CHANCE
      ? this.playerTargetLane
      : Math.floor(Math.random() * LANE_X.length);
    // A chained strike never repeats the lane just struck, so dodging the
    // first one doesn't mean staying put.
    if (this.chainFromLane !== null) {
      const others = [0, 1, 2].filter((l) => l !== this.chainFromLane);
      lane = others.includes(this.playerTargetLane) && Math.random() < 0.7 ? this.playerTargetLane : this.pickRandom(others);
      this.chainFromLane = null;
    }
    const pressure = rampBetween(this.difficulty ?? 0, STORM_PRESSURE_FROM, STORM_PRESSURE_TO);
    const warning = THREE.MathUtils.lerp(LIGHTNING_WARNING_SECONDS, LIGHTNING_WARNING_FLOOR_SECONDS, pressure);
    this.strike = { lane, elapsed: 0, phase: 'warning', warning };
    this.strikeMarker.position.x = LANE_X[lane];
    this.strikeMarker.position.z = 0;
    this.strikeMarker.visible = true;
  }

  updateStrike(dt) {
    const strike = this.strike;
    strike.elapsed += dt;
    if (strike.phase === 'warning') {
      // Pulses faster as the strike nears.
      const progress = strike.elapsed / strike.warning;
      const pulse = 0.5 + 0.5 * Math.sin(progress * progress * 30);
      this.strikeMarker.material.opacity = 0.4 + 0.35 * pulse;
      if (progress >= 1) {
        strike.phase = 'bolt';
        strike.elapsed = 0;
        this.strikeMarker.visible = false;
        this.warningBadge.visible = false;
        this.fireBolt(strike.lane);
      }
    } else if (strike.elapsed >= LIGHTNING_BOLT_SECONDS) {
      this.boltLine.visible = false;
      const struckLane = this.strike.lane;
      this.strike = null;
      const chainChance = LIGHTNING_CHAIN_CHANCE_MAX * rampBetween(this.difficulty ?? 0, LIGHTNING_CHAIN_FROM, 1);
      if (Math.random() < chainChance) {
        this.chainFromLane = struckLane;
        this.strikeCooldown = LIGHTNING_CHAIN_DELAY_SECONDS;
      } else {
        this.strikeCooldown = this.nextStrikeCooldown();
      }
    } else {
      this.boltLine.visible = strike.elapsed < 0.12 || Math.floor(strike.elapsed * 24) % 3 !== 0;
    }
  }

  fireBolt(lane) {
    const x = LANE_X[lane];
    const writePath = (layers, path) => {
      for (const { mesh, halfWidth } of layers) {
        const pos = mesh.geometry.getAttribute('position');
        path.forEach(([px, py, pz], i) => {
          const w = halfWidth * (1 - 0.5 * (i / (path.length - 1)));
          pos.setXYZ(i * 2, px - w, py, pz);
          pos.setXYZ(i * 2 + 1, px + w, py, pz);
        });
        pos.needsUpdate = true;
      }
    };
    // Sharp zig-zag: alternate sides with random amplitude, pinned to the
    // struck lane at the ground.
    const trunk = [];
    let side = Math.random() < 0.5 ? -1 : 1;
    for (let i = 0; i < BOLT_POINTS; i++) {
      const t = i / (BOLT_POINTS - 1);
      let offset = 0;
      if (i > 0 && i < BOLT_POINTS - 1) {
        side = -side;
        offset = side * (0.25 + Math.random() * 0.75) * (1 - t * 0.6);
      }
      trunk.push([x + offset, BOLT_TOP_Y * (1 - t), BOLT_Z]);
    }
    writePath(this.boltTrunk, trunk);
    // Branches fork off the upper trunk and angle outward and downward.
    this.boltBranches.forEach((layers, b) => {
      const [sx, sy, sz] = trunk[3 + b * 3];
      const dir = (b === 0 ? -1 : 1) * (Math.random() < 0.5 ? -1 : 1);
      const branch = [];
      let bx = sx;
      let by = sy;
      for (let i = 0; i < BOLT_BRANCH_POINTS; i++) {
        branch.push([bx, by, sz]);
        bx += dir * (0.3 + Math.random() * 0.7);
        by -= 1.1 + Math.random() * 0.9;
      }
      writePath(layers, branch);
    });
    this.boltLine.visible = true;
    this.flashLevel = 1;
    this.playThunder();

    if (!this.hasEffect('invincible') && Math.abs(this.playerX - x) < LIGHTNING_HIT_HALF_WIDTH) {
      const lostItems = Math.ceil(this.coinsCollected * LIGHTNING_LOSS_FRACTION);
      this.playerHitTimer = 0.9;
      if (lostItems > 0) {
        this.coinsCollected -= lostItems;
        // The same pickups come out of the item counts, and fly out of the player.
        const lostVariants = this.loseItems(lostItems);
        this.spillPickups(lostItems, lostVariants);
        this.callbacks.onPenalty?.(lostItems * COIN_VALUE);
      }
      this.updateScoreDisplay();
    }
  }

  /** Scatters pickups out of the player; they arc, bounce and fade out. */
  spillPickups(lostItems, variants = []) {
    const count = Math.min(lostItems, this.spill.length);
    for (let i = 0; i < count; i++) {
      const item = this.spill[i];
      this.assignPickupVariant(item, variants[i] ?? Math.floor(Math.random() * PICKUP_URLS.length));
      item.mesh.scale.multiplyScalar(SPILL_SCALE);
      item.halfHeight = item.mesh.scale.y / 2;
      item.active = true;
      item.age = 0;
      item.vx = (Math.random() - 0.5) * 8;
      item.vy = 9 + Math.random() * 5;
      item.vz = -1 - Math.random() * 3;
      item.mesh.position.set(this.playerX, this.playerY + 0.9, 0);
      item.mesh.visible = true;
    }
  }

  updateSpill(dt) {
    for (const item of this.spill) {
      if (!item.active) continue;
      item.age += dt;
      if (item.age >= SPILL_LIFETIME) {
        item.active = false;
        item.mesh.visible = false;
        continue;
      }
      item.vy -= SPILL_GRAVITY * dt;
      const m = item.mesh;
      m.position.x += item.vx * dt;
      m.position.y += item.vy * dt;
      // Drifts with the road only a little, so the scatter stays on screen
      // near the player instead of streaking past the camera.
      m.position.z += (item.vz + this.speed * SPILL_ROAD_DRIFT) * dt;
      if (m.position.y < item.halfHeight) {
        m.position.y = item.halfHeight;
        item.vy = Math.abs(item.vy) * SPILL_BOUNCE;
        item.vx *= 0.8;
        item.vz *= 0.7;
      }
      m.rotation.y += dt * 8;
      // Blink out over the last stretch.
      m.visible = item.age < SPILL_LIFETIME - 0.5 || Math.floor(item.age * 16) % 2 === 0;
    }
  }

  updateWarningBadge() {
    const strike = this.strike;
    // Only shown while the player is standing in the lane about to be hit.
    const show = strike !== null && strike.phase === 'warning' && !this.hasEffect('invincible')
      && Math.abs(this.playerX - LANE_X[strike.lane]) < LIGHTNING_HIT_HALF_WIDTH;
    this.warningBadge.visible = show;
    if (!show) return;
    const pop = Math.min(1, strike.elapsed / 0.15);
    const bob = Math.sin(strike.elapsed * 14) * 0.06;
    const size = 0.42 * (0.6 + 0.4 * pop);
    this.warningBadge.scale.set(size, size, 1);
    this.warningBadge.position.set(this.playerX, this.playerY + this.playerBaseHeight + (this.puddleEffect ? 0.95 : 0.45) + bob, 0);
  }

  /** Synthesised crack + rumble: no asset needed. */
  playThunder() {
    const ctx = this.audioContext;
    if (ctx.state !== 'running') return;
    const seconds = 1.4;
    const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * seconds), ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let low = 0;
    for (let i = 0; i < data.length; i++) {
      const t = i / data.length;
      low += (Math.random() * 2 - 1 - low) * 0.06;
      data[i] = low * 6 * Math.pow(1 - t, 2.2) + (Math.random() * 2 - 1) * 0.5 * Math.exp(-t * 30);
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const gain = ctx.createGain();
    gain.gain.value = 0.7;
    source.connect(gain);
    gain.connect(ctx.destination);
    source.start(0);
  }

  updateCamera(dt) {
    this.cameraSurfaceY += (this.standingY - this.cameraSurfaceY) * Math.min(1, dt * CAMERA_SURFACE_EASE);
    this.landingSnapOffset *= Math.max(0, 1 - dt * CAMERA_SNAP_EASE);
  }

  /**
   * Tracks what the player is carrying this run, per item key. A lightning
   * hit takes items back out again (see `loseItems`), so these are net counts
   * -- what ends up submitted is what the player still held at game over.
   */
  countItem(key) {
    if (!key) return;
    this.itemCounts = this.itemCounts || {};
    this.itemCounts[key] = (this.itemCounts[key] || 0) + 1;
    this.callbacks.onItemsChange?.({ ...this.itemCounts });
  }

  /**
   * Removes `count` pickups from the item counts after a lightning hit. Each
   * one is picked at random weighted by what's held, so a player carrying
   * mostly bananas loses mostly bananas. The gold Jasmin isn't a pickup (it
   * never counted towards the score) so it can't be lost. Returns the pickup
   * variant of each lost item so the spilled sprites match.
   * @param {number} count
   * @returns {number[]}
   */
  loseItems(count) {
    const lost = [];
    this.itemCounts = this.itemCounts || {};
    for (let i = 0; i < count; i++) {
      const held = [];
      let total = 0;
      ITEM_KEY_BY_VARIANT.forEach((key, variant) => {
        const n = this.itemCounts[key] || 0;
        if (n > 0) {
          held.push({ variant, key, n });
          total += n;
        }
      });
      if (total === 0) break;
      let roll = Math.random() * total;
      let pick = held[held.length - 1];
      for (const entry of held) {
        roll -= entry.n;
        if (roll < 0) {
          pick = entry;
          break;
        }
      }
      this.itemCounts[pick.key] -= 1;
      lost.push(pick.variant);
    }
    this.callbacks.onItemsChange?.({ ...this.itemCounts });
    return lost;
  }

  /** Points come only from collected items -- distance run no longer scores. */
  getScore() {
    return this.coinsCollected * COIN_VALUE;
  }

  updateScoreDisplay() {
    const score = this.getScore();
    if (score !== this.displayedScore) {
      this.displayedScore = score;
      this.callbacks.onScoreChange(score);
    }
    this.checkVoiceMilestone(score);
  }

  /**
   * Every VOICE_MILESTONE_INTERVAL points, plays a random voice line from
   * the active character's own sound bank -- a small "the character is
   * cheering you on" beat dropped in periodically rather than on every
   * score change (which would be constant and grating).
   * @param {number} score
   */
  checkVoiceMilestone(score) {
    const milestone = Math.floor(score / VOICE_MILESTONE_INTERVAL);
    if (milestone <= this.lastVoiceMilestone || this.voiceUrls.length === 0) return;
    // A long line is still playing: wait for it to finish instead of talking over it (the
    // milestone stays pending and is spoken as soon as the voice is free).
    if (this.voicePlaying) return;
    this.lastVoiceMilestone = milestone;
    const url = this.voiceUrls[Math.floor(Math.random() * this.voiceUrls.length)];
    this.playVoice(this.loadSoundBuffer(url), 0.8);
  }

  /** Plays one voice line and marks the voice busy until it ends (see `checkVoiceMilestone`). */
  async playVoice(bufferPromise, volume) {
    this.voicePlaying = true;
    const token = (this.voiceToken = (this.voiceToken || 0) + 1);
    const free = () => {
      if (this.voiceToken === token) this.voicePlaying = false;
    };
    try {
      const buffer = await bufferPromise;
      if (!buffer || this.audioContext.state !== 'running') return free();
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      const gain = this.audioContext.createGain();
      gain.gain.value = volume;
      source.connect(gain);
      gain.connect(this.audioContext.destination);
      source.onended = free;
      source.start(0);
    } catch {
      free();
    }
  }

  /**
   * Picks an unused pooled obstacle/coin and places it at the spawn line.
   * A handful of hand-picked patterns keep runs varied without needing
   * per-frame allocation or a level-data file.
   */
  spawnWave() {
    // A wave only stays if it leaves the player a way through (see
    // `canReachSafely`); otherwise it's undone and another is tried.
    const reachableBefore = this.canReachSafely();
    const savedClearUntil = this.clearUntilDistance;
    const savedLiveryIndex = this.busLiveryIndex;
    for (let attempt = 0; attempt < FAIR_SPAWN_ATTEMPTS; attempt++) {
      const activeBefore = new Set();
      for (const item of this.obstacles) if (item.active) activeBefore.add(item);
      for (const item of this.coins) if (item.active) activeBefore.add(item);

      this.spawnWaveUnchecked();
      if (!reachableBefore || this.canReachSafely()) return;

      for (const item of [...this.obstacles, ...this.coins]) {
        if (item.active && !activeBefore.has(item)) {
          item.active = false;
          item.mesh.visible = false;
        }
      }
      this.clearUntilDistance = savedClearUntil;
      this.busLiveryIndex = savedLiveryIndex;
    }
  }

  /**
   * Can a player standing in their current lane get through everything
   * already on the ground, given that a lane change takes
   * FAIR_LANE_STEP_SECONDS? Searches forward in small time steps: from each
   * lane, stay (if the lane is clear) or move one lane over (both lanes must
   * stay clear while moving). Only ground-level blockers count and jumping
   * is ignored, so it errs toward leaving more room than strictly needed.
   * Crates of a wall (jumped by design) and crossing riders (timed hazards,
   * not lane blockers) are left out.
   * @returns {boolean} false only if every path is cut off
   */
  canReachSafely() {
    const speed = this.speed;
    const dt = FAIR_TIME_STEP;
    const frames = Math.floor(FAIR_MAX_HORIZON_SECONDS / dt);
    const blocked = [0, 1, 2].map(() => new Uint8Array(frames + 1));
    let lastBlocked = 0;
    for (const o of this.obstacles) {
      if (!o.active || o.wall || o.baseY > 0) continue;
      if (o.type === 'rider' && o.mode === 'crossing') continue;
      const half = OBSTACLE_DEFS[o.type].length / 2 + COLLISION_Z_MAX;
      const along = o.type === 'rider' && o.mode === 'along' && o.state === 'riding';
      const velocity = along ? speed - RIDER_ALONG_SPEED : speed;
      const tOut = (half - o.z) / velocity;
      if (tOut <= 0) continue; // already past the player
      const tIn = Math.max(0, -(o.z + half) / velocity);
      const first = Math.floor(tIn / dt);
      const last = Math.min(frames, Math.ceil(tOut / dt));
      for (const lane of this.occupiedLanes(o)) {
        for (let i = first; i <= last; i++) blocked[lane][i] = 1;
      }
      lastBlocked = Math.max(lastBlocked, last);
    }
    if (lastBlocked === 0) return true;

    const stepFrames = Math.ceil(FAIR_LANE_STEP_SECONDS / dt);
    const end = Math.min(frames, lastBlocked + 1);
    const arrive = Array.from({ length: end + stepFrames + 1 }, () => [false, false, false]);
    arrive[0][this.playerTargetLane] = true;
    const clearBetween = (lane, from, to) => {
      for (let i = from; i <= to; i++) if (blocked[lane][i]) return false;
      return true;
    };
    for (let i = 0; i <= end; i++) {
      for (let lane = 0; lane < 3; lane++) {
        if (!arrive[i][lane]) continue;
        // Standing in a blocked lane is a crash -- except right now, where a
        // player already overlapping something has bigger problems than this wave.
        if (i > 0 && blocked[lane][i]) continue;
        if (i === end) return true;
        if (!blocked[lane][i + 1]) arrive[i + 1][lane] = true;
        for (const next of [lane - 1, lane + 1]) {
          if (next < 0 || next > 2) continue;
          const to = Math.min(i + stepFrames, end);
          if (clearBetween(lane, i + 1, to) && clearBetween(next, i + 1, to)) arrive[i + stepFrames][next] = true;
        }
      }
    }
    return false;
  }

  /**
   * One of the fading-in patterns (see the PROGRESSIVE section of the
   * constants), rolled in this order. Returns true if one spawned.
   */
  spawnProgressivePattern() {
    const d = this.difficulty;
    const chance = (from, to, max) => max * rampBetween(d, from, to);
    if (d >= JUMP_SWERVE_FROM && Math.random() < chance(JUMP_SWERVE_FROM, JUMP_SWERVE_TO, JUMP_SWERVE_CHANCE_MAX)) {
      if (this.spawnJumpThenSwerve()) return true;
    }
    if (d >= SLALOM_FROM && Math.random() < chance(SLALOM_FROM, SLALOM_TO, SLALOM_CHANCE_MAX)) {
      if (this.spawnSlalom()) return true;
    }
    if (d >= COMBO_FROM && Math.random() < chance(COMBO_FROM, COMBO_TO, COMBO_CHANCE_MAX)) {
      if (this.spawnCrateThenCactus()) return true;
    }
    if (d >= BAIT_FROM && Math.random() < chance(BAIT_FROM, BAIT_TO, BAIT_CHANCE_MAX)) {
      if (this.spawnBait()) return true;
    }
    return false;
  }

  /** Pickups leading straight into a crate in the same lane: greedy, but beatable. */
  spawnBait() {
    const freeLanes = this.getFreeLanes(false);
    if (freeLanes.length < 2) return false;
    const lane = this.pickHazardLane(freeLanes);
    const lastCoinZ = SPAWN_Z - (BAIT_COIN_COUNT - 1) * 1.6;
    if (!this.spawnObstacle(lane, 'low', { z: lastCoinZ - BAIT_CRATE_GAP })) return false;
    this.spawnCoinTrail(lane, { count: BAIT_COIN_COUNT });
    return true;
  }

  /** A crate then a cactus in the same lane: hopping the crate lands you near the cactus, so swerve. */
  spawnCrateThenCactus() {
    const freeLanes = this.getFreeLanes(false);
    if (freeLanes.length < 3) return false;
    const lane = this.pickHazardLane(freeLanes);
    const gapSeconds = THREE.MathUtils.lerp(
      COMBO_GAP_SECONDS_START,
      COMBO_GAP_SECONDS_END,
      rampBetween(this.difficulty, COMBO_FROM, COMBO_TO)
    );
    if (!this.spawnObstacle(lane, 'low')) return false;
    this.spawnObstacle(lane, 'tall', { z: SPAWN_Z - gapSeconds * this.speed });
    this.clearUntilDistance = this.distance + (gapSeconds + 0.5) * this.speed;
    return true;
  }

  /** Cactus pairs whose open lane keeps moving: two steps, three later. */
  spawnSlalom() {
    if (this.getFreeLanes(false).length < 3) return false;
    const steps = this.difficulty >= SLALOM_THREE_STEP_FROM && Math.random() < 0.5 ? 3 : 2;
    const tighten = rampBetween(this.difficulty, SLALOM_FROM, SLALOM_TO);
    let open = this.randomLane();
    let seconds = 0;
    for (let step = 0; step < steps; step++) {
      for (let lane = 0; lane < 3; lane++) {
        if (lane !== open) this.spawnObstacle(lane, 'tall', { z: SPAWN_Z - seconds * this.speed });
      }
      if (step === steps - 1) break;
      // Usually the next lane over; the far side is held back until late.
      const others = [0, 1, 2].filter((lane) => lane !== open);
      const adjacent = others.filter((lane) => Math.abs(lane - open) === 1);
      const next = Math.random() < 0.3 * tighten ? this.pickRandom(others) : this.pickRandom(adjacent);
      seconds += SLALOM_BASE_GAP_SECONDS - 0.2 * tighten + SLALOM_GAP_PER_LANE_SECONDS * Math.abs(next - open);
      open = next;
    }
    this.clearUntilDistance = this.distance + (seconds + 0.6) * this.speed;
    return true;
  }

  /** A wall to jump, then -- just after landing -- a cactus pair to swerve round. */
  spawnJumpThenSwerve() {
    if (!this.spawnWallWave()) return false;
    const landingSeconds = JUMP_AIR_TIME * 1.35 + JUMP_SWERVE_LANDING_SECONDS;
    const open = this.randomLane();
    for (let lane = 0; lane < 3; lane++) {
      if (lane !== open) this.spawnObstacle(lane, 'tall', { z: SPAWN_Z - landingSeconds * this.speed });
    }
    this.clearUntilDistance = Math.max(this.clearUntilDistance, this.distance + (landingSeconds + 0.4) * this.speed);
    return true;
  }

  spawnWaveUnchecked() {
    // Nothing hazardous goes into the stretch a player is still airborne
    // over after jumping a wall -- they'd land straight onto it.
    if (this.distance < this.clearUntilDistance) {
      if (Math.random() < 0.5) {
        const lanes = this.getFreeLanes(false);
        if (lanes.length) this.spawnCoinTrail(this.pickRandom(lanes));
      }
      return;
    }

    // Bus waves are rolled separately first, so when one doesn't happen (or
    // can't -- no lane has room) the original patterns keep exactly their
    // original relative weights.
    const busChance = THREE.MathUtils.lerp(BUS_WAVE_CHANCE_START, BUS_WAVE_CHANCE_MAX, this.difficulty);
    if (Math.random() < busChance && this.spawnBusWave()) return;
    const riderChance = THREE.MathUtils.lerp(RIDER_WAVE_CHANCE_START, RIDER_WAVE_CHANCE_MAX, this.difficulty);
    if (Math.random() < riderChance && this.spawnRiderWave()) return;
    if (this.difficulty >= WALL_MIN_DIFFICULTY) {
      const wallChance = THREE.MathUtils.lerp(0, WALL_CHANCE_MAX, (this.difficulty - WALL_MIN_DIFFICULTY) / (1 - WALL_MIN_DIFFICULTY));
      if (Math.random() < wallChance && this.spawnWallWave()) return;
    }
    if (this.spawnProgressivePattern()) return;

    // Weighted rather than uniform: the two-simultaneous-pillars pattern is
    // the hardest (only one safe lane, no jump escape), so it starts rare
    // and gets more common as `difficulty` climbs over the course of a run,
    // rather than being a fixed fraction of waves throughout.
    const doublePillarChance = THREE.MathUtils.lerp(
      DOUBLE_PILLAR_CHANCE_START,
      DOUBLE_PILLAR_CHANCE_MAX,
      this.difficulty
    );
    const singleObstacleChance = 0.4;
    const coinTrailChance = 0.25;
    const roll = Math.random();

    // Lanes still occupied by a bus at the spawn line are skipped. A hazard
    // is only placed while another lane stays open, so a lane blocked by a
    // bus never combines with new hazards into a wall with no ground-level
    // way through (the roof is a bonus route, not a forced one). The
    // two-pillar pattern needs all three lanes and otherwise drops to one.
    const freeLanes = this.getFreeLanes(false);
    const hazardLane = freeLanes.length >= 2 ? this.pickHazardLane(freeLanes) : null;

    if (roll < singleObstacleChance) {
      if (hazardLane !== null) this.spawnObstacle(hazardLane, Math.random() < 0.5 ? 'low' : 'tall');
    } else if (roll < singleObstacleChance + doublePillarChance) {
      if (freeLanes.length === 3) {
        // Aimed like a single hazard: the pair covers the player's lane, so
        // the open one is always a lane they have to move to.
        const first = this.pickHazardLane(freeLanes);
        const lanes = [first, this.pickRandom(freeLanes.filter((lane) => lane !== first))];
        this.spawnObstacle(lanes[0], 'tall');
        this.spawnObstacle(lanes[1], 'tall');
      } else if (hazardLane !== null) {
        this.spawnObstacle(hazardLane, 'tall');
      }
    } else if (roll < singleObstacleChance + doublePillarChance + coinTrailChance) {
      if (freeLanes.length > 0) this.spawnCoinTrail(this.pickRandom(freeLanes));
    } else if (hazardLane !== null) {
      const coinLane = this.pickRandom(freeLanes.filter((lane) => lane !== hazardLane));
      this.spawnObstacle(hazardLane, 'low');
      this.spawnCoinTrail(coinLane);
    }
  }

  /**
   * Spawns a standard or articulated bus with its front at the spawn line,
   * sometimes with pickups or a crate riding its roof. Returns false (and
   * spawns nothing) if no lane has room or no pooled bus is free, so the
   * caller falls back to a regular wave.
   */
  spawnBusWave() {
    const busLanes = this.getFreeLanes(true);
    // Keep at least one other lane open at ground level (see `spawnWave`).
    const openLanes = this.getFreeLanes(false);
    const lanes = busLanes.filter((lane) => openLanes.some((other) => other !== lane));
    if (lanes.length === 0) return false;

    const lane = this.pickRandom(lanes);
    const type = Math.random() < BUS_ARTICULATED_SHARE ? 'busArticulated' : 'bus';
    // Strictly alternate liveries; if that livery's pool happens to be
    // exhausted, fall back to the other rather than skip the bus.
    const livery = BUS_LIVERY_ORDER[this.busLiveryIndex % BUS_LIVERY_ORDER.length];
    const bus = this.spawnObstacle(lane, type, { livery }) ?? this.spawnObstacle(lane, type);
    if (!bus) return false;
    this.busLiveryIndex = BUS_LIVERY_ORDER.indexOf(bus.livery) + 1;

    const def = OBSTACLE_DEFS[type];
    const roofY = bus.baseY + def.topSurfaceHeight;
    // The end nearest the player (the bus's rear -- see `buildBusTemplate`).
    const nearEnd = bus.z + def.length / 2;
    if (Math.random() < BUS_ROOF_COIN_CHANCE) {
      // A trail along the roof -- a reason to climb up rather than go round.
      this.spawnCoinTrail(lane, {
        startZ: nearEnd - 1.5,
        y: roofY + 0.7,
        count: type === 'busArticulated' ? 5 : 3,
        onSurface: true,
      });
    } else if (
      type === 'busArticulated' &&
      this.difficulty >= BUS_ROOF_CRATE_MIN_DIFFICULTY &&
      Math.random() < BUS_ROOF_CRATE_CHANCE
    ) {
      // On the far section, past where any ground-jump landing can end up,
      // so there's always roof to land on and react before it.
      this.spawnObstacle(lane, 'low', { z: nearEnd - def.length * 0.8, baseY: roofY });
    }
    return true;
  }

  /**
   * Picks the lane for a hazard. As difficulty climbs it's increasingly the
   * lane the player is actually in, so they have to react instead of
   * watching an obstacle go by in a lane they were never in.
   */
  pickHazardLane(freeLanes) {
    const targetChance = THREE.MathUtils.lerp(TARGETED_LANE_CHANCE_START, TARGETED_LANE_CHANCE_MAX, this.difficulty);
    if (Math.random() < targetChance && freeLanes.includes(this.playerLane)) return this.playerLane;
    return this.pickRandom(freeLanes);
  }

  /**
   * A crate in every lane: the one pattern with no way round, only over.
   * Needs all three lanes, and refuses if anything is already sitting in the
   * stretch the player will still be airborne over -- they can't dodge what
   * they land on. Sets `clearUntilDistance` so later waves keep that stretch
   * clear too.
   */
  spawnWallWave() {
    if (this.getFreeLanes(false).length < 3) return false;
    // Ground covered while airborne, plus a margin to land and read again.
    const landingRun = JUMP_AIR_TIME * this.speed * 1.35;
    const occupied = this.obstacles.some(
      (o) => o.active && o.baseY === 0 && o.z < SPAWN_Z + 1 && o.z > SPAWN_Z - landingRun
    );
    if (occupied) return false;
    for (let lane = 0; lane < LANE_X.length; lane++) {
      const crate = this.spawnObstacle(lane, 'low');
      // Jumped by design, so `canReachSafely` doesn't treat it as a lane blocker.
      if (crate) crate.wall = true;
    }
    this.clearUntilDistance = this.distance + landingRun;
    return true;
  }

  /**
   * Spawns one rider at the spawn line: either riding away down a free lane
   * (only while another lane stays open -- see `spawnWave`), or waiting on
   * one pavement to cross to the other. A crossing rider sets off at the
   * moment that puts it at a random point across the road as it reaches the
   * player, so it's a moving hazard to read and time, not a surprise.
   */
  spawnRiderWave() {
    const mode = Math.random() < RIDER_CROSSING_SHARE ? 'crossing' : 'along';
    let lane = null;
    let openLanes = [];
    if (mode === 'along') {
      // Read before the rider is activated, so it doesn't count its own stale lane.
      openLanes = this.getFreeLanes(false);
      if (openLanes.length < 2) return false;
      lane = this.pickRandom(openLanes);
    }
    const kind = this.pickRandom(RIDER_KINDS);
    const rider =
      this.obstacles.find((o) => !o.active && o.type === 'rider' && o.riderKind === kind) ??
      this.obstacles.find((o) => !o.active && o.type === 'rider');
    if (!rider) return false;

    rider.active = true;
    rider.resolved = false;
    rider.baseY = 0;
    rider.z = SPAWN_Z;
    rider.mode = mode;
    rider.frameTime = Math.random();
    rider.clearHeight = RIDER_CLEAR_HEIGHT;
    rider.swerve = null;
    if (mode === 'along') {
      rider.state = 'riding';
      rider.direction = 0;
      rider.x = LANE_X[lane];
      rider.protected = Math.random() < RIDER_ALONG_PROTECTED_CHANCE;
      const swerveChance = RIDER_SWERVE_CHANCE_MAX * rampBetween(this.difficulty, RIDER_SWERVE_FROM, RIDER_SWERVE_TO);
      if (Math.random() < swerveChance) {
        const targets = [lane - 1, lane + 1].filter((l) => openLanes.includes(l));
        if (targets.length) {
          rider.swerve = { from: lane, to: this.pickRandom(targets), state: 'pending', elapsed: 0 };
          // Kept clear of new spawns so it isn't crashed into mid-swerve.
          rider.protected = true;
        }
      }
    } else {
      rider.state = 'waiting';
      rider.direction = Math.random() < 0.5 ? -1 : 1;
      rider.x = -rider.direction * RIDER_CROSS_START_X;
      const targetX = (Math.random() * 2 - 1) * LANE_WIDTH;
      rider.triggerZ = (-this.speed * Math.abs(targetX - rider.x)) / RIDER_CROSS_SPEED;
    }
    rider.lane = this.closestLane(rider.x);
    rider.mesh.visible = true;
    rider.mesh.position.set(rider.x, 0, rider.z);
    this.showRiderFrame(rider);
    return true;
  }

  /**
   * Lanes with room for a new spawn at the spawn line. Point obstacles and
   * coins never blocked each other before buses existed, so only spacing
   * that involves a bus is enforced: a run-up before a bus, room to drop
   * off the back of one, and a jumpable gap between two buses in a row.
   * Things riding a roof are covered by their bus and ignored here.
   * @param {boolean} forBus whether the new spawn is a bus
   */
  getFreeLanes(forBus) {
    return [0, 1, 2].filter((lane) => {
      // A fresh puddle keeps its lane clear of new obstacles right behind it.
      if (this.puddles?.some((p) => p.active && p.lane === lane && p.z < SPAWN_Z + PUDDLE_CLEARANCE)) return false;
      for (const obstacle of this.obstacles) {
        if (!obstacle.active || obstacle.baseY > 0 || !this.occupiesLane(obstacle, lane)) continue;
        const def = OBSTACLE_DEFS[obstacle.type];
        // A spawn at SPAWN_Z closes on a rider ahead of it at
        // RIDER_ALONG_SPEED, and catches it before the player does exactly
        // while the rider is still nearer the spawn line than this.
        if (
          obstacle.type === 'rider' &&
          obstacle.protected &&
          obstacle.mode === 'along' &&
          obstacle.state === 'riding' &&
          obstacle.z < SPAWN_Z * (1 - RIDER_ALONG_SPEED / this.speed)
        ) {
          return false;
        }
        const isBus = def.topSurfaceHeight !== undefined;
        if (!isBus && !forBus) continue;
        const clearance = isBus ? (forBus ? BUS_CHAIN_GAP : BUS_EXIT_CLEARANCE) : BUS_RUNUP_CLEARANCE;
        if (obstacle.z - def.length / 2 < SPAWN_Z + clearance) return false;
      }
      if (forBus) {
        for (const coin of this.coins) {
          if (coin.active && !coin.onSurface && coin.lane === lane && coin.z < SPAWN_Z + BUS_RUNUP_CLEARANCE) return false;
        }
      }
      return true;
    });
  }

  pickRandom(items) {
    return items[Math.floor(Math.random() * items.length)];
  }

  /** The lanes an obstacle takes up: its own, plus both of a rider's while it is still swerving. */
  occupiedLanes(obstacle) {
    const swerve = obstacle.type === 'rider' ? obstacle.swerve : null;
    return swerve && swerve.state !== 'done' && obstacle.state === 'riding' ? [swerve.from, swerve.to] : [obstacle.lane];
  }

  occupiesLane(obstacle, lane) {
    return this.occupiedLanes(obstacle).includes(lane);
  }

  randomLane() {
    return Math.floor(Math.random() * 3);
  }

  twoRandomLanes() {
    const first = this.randomLane();
    let second = this.randomLane();
    while (second === first) second = this.randomLane();
    return [first, second];
  }

  /**
   * Activates a pooled obstacle. Defaults put it on the ground with its
   * near end at the spawn line (for point obstacles, centered on it).
   * `livery` (buses only) restricts it to that livery's pool.
   * @returns the obstacle, or undefined if none of that type is free
   */
  spawnObstacle(lane, type, { z, baseY = 0, livery } = {}) {
    const obstacle = this.obstacles.find((o) => !o.active && o.type === type && (!livery || o.livery === livery));
    if (!obstacle) return undefined;

    const def = OBSTACLE_DEFS[type];
    obstacle.active = true;
    obstacle.resolved = false;
    obstacle.wall = false;
    obstacle.lane = lane;
    obstacle.z = z ?? SPAWN_Z - def.length / 2;
    obstacle.baseY = baseY;
    obstacle.mesh.visible = true;
    // Box/billboard obstacles are centered on their origin; buses have
    // theirs at ground level.
    const originY = def.height !== undefined ? def.height / 2 : 0;
    obstacle.mesh.position.set(LANE_X[lane], baseY + originY, obstacle.z);
    return obstacle;
  }

  spawnCoinTrail(lane, { startZ = SPAWN_Z, y = 0.9, count = 3, onSurface = false } = {}) {
    const free = this.coins.filter((c) => !c.active).slice(0, count);
    // One variant per trail, not per item -- a run of 3 is always all
    // bananas or all snack packs, never mixed.
    const variant = this.hasEffect('banana')
      ? BANANA_VARIANT_INDEX
      : Math.floor(Math.random() * PICKUP_URLS.length);
    free.forEach((coin, index) => {
      coin.active = true;
      coin.lane = lane;
      coin.onSurface = onSurface;
      coin.z = startZ - index * 1.6;
      coin.mesh.visible = true;
      this.assignPickupVariant(coin, variant);
      coin.mesh.position.set(LANE_X[lane], y, coin.z);
    });
  }

  gameOver() {
    this.state = 'gameover';
    this.flashLevel = 0;
    this.flashEl.style.opacity = '0';
    this.boltLine.visible = false;
    this.strikeMarker.visible = false;
    this.warningBadge.visible = false;
    this.player.material.color.copy(this.playerRestTint);
    this.resetPuddleStatus();
    // Swap to the real "hit" pose art (dizzy stars) the instant a run ends,
    // rather than freezing on whatever mid-stride running frame happened to
    // be showing -- gives the collision an actual visual reaction.
    this.currentPose = 'injured';
    this.setPlayerFrame(this.injuredFrameTextures[1]);
    this.requestIdleRender();
    this.musicSound.pause();
    this.playBuffer(this.gameOverSoundBuffer, 0.6);

    // Computed directly rather than read from `displayedScore`, which is only
    // refreshed at the end of `update()` — after collision handling runs — so
    // it can still be one frame stale at the exact moment a run ends.
    const finalScore = this.getScore();
    this.displayedScore = finalScore;

    const best = Number(window.localStorage.getItem(HIGH_SCORE_KEY) || 0);
    const isNewHigh = finalScore > best;
    if (isNewHigh) {
      window.localStorage.setItem(HIGH_SCORE_KEY, String(finalScore));
    }

    this.callbacks.onGameOver(finalScore, Math.max(best, finalScore), isNewHigh, {
      items: { ...(this.itemCounts || {}) },
      seconds: Math.round(((performance.now() - (this.runStartedAt || performance.now())) / 1000) * 10) / 10,
    });
  }

  handleResize() {
    const container = this.canvas.parentElement || this.canvas;
    const width = container.clientWidth || 1;
    const height = container.clientHeight || 1;

    this.renderer.setSize(width, height, true);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.requestIdleRender();
  }

  render() {
    this.camera.position.set(this.playerX, this.playerY + CAMERA_HEIGHT + this.landingSnapOffset, CAMERA_DISTANCE);
    this.camera.lookAt(this.playerX, CAMERA_LOOK_HEIGHT + this.cameraSurfaceY, CAMERA_LOOK_AHEAD);
    this.sky.position.copy(this.camera.position);
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * Full teardown: cancels the render loop, drops every DOM/observer
   * listener, and disposes every geometry/material/texture plus the
   * WebGLRenderer itself. This is the first WebGL-backed component in the
   * theme, and browsers hard-cap concurrent contexts, so leaving any of
   * this out would eventually break the page after repeated navigation.
   */
  destroy() {
    this.setActive(false);
    this.destroyed = true;
    clearTimeout(this.warmUpTimer);

    this.canvas.removeEventListener('touchstart', this.handleTouchStart);
    this.canvas.removeEventListener('touchend', this.handleTouchEnd);
    window.removeEventListener('keydown', this.handleKeyDown);
    this.resizeObserver.disconnect();
    this.musicSound.pause();
    this.musicSound.src = '';
    if (this.flashEl) this.flashEl.remove();
    // One-shot effect/voice sounds are AudioBuffers, not HTMLMediaElements
    // (see the constructor) -- nothing to pause/release there; closing the
    // context releases everything scheduled on it.
    this.audioContext.close().catch(() => {});

    this.scene.traverse((object) => {
      if (object.geometry) object.geometry.dispose();
      if (object.material) {
        // Defensively handles a material array too, in case any mesh here
        // ever goes back to per-face materials -- array.map would otherwise
        // resolve to Array.prototype.map, not a texture.
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        for (const material of materials) {
          if (material.map) material.map.dispose();
          material.dispose();
        }
      }
    });

    // Only one of the character's textures is ever assigned as the player's
    // active `material.map` at a time, so the traversal above would miss
    // disposing the rest; voice-line sounds aren't part of the scene at all.
    this.disposeCharacterTextures();
    // Same reasoning: with 18 pooled pickups sharing only 3 material sets,
    // some (or before the first spawn, all) variants may not be the
    // currently-assigned `material` on any coin mesh at disposal time.
    // (Double-disposing whatever the traversal above already caught is a
    // harmless no-op.)
    for (const texture of this.pickupTextures) texture.dispose();
    // Street: cached facade materials not currently worn by any house.
    for (const material of this.buildingMaterials.values()) {
      material.map.dispose();
      material.dispose();
    }
    for (const material of this.pickupFaceMaterials) material.dispose();
    for (const material of this.pickupSideMaterials) material.dispose();
    // Not every pooled coin necessarily ends up wearing both shared pickup
    // geometries by disposal time (18 coins, 3 variants) -- disposing both
    // directly, redundant with whatever the traversal above already caught.
    this.pickupBoxGeometry.dispose();
    this.pickupBananaGeometry.dispose();

    // Rider atlases: only the per-rider clones sit on materials.
    for (const texture of this.riderBaseTextures) texture.dispose();
    for (const mesh of this.propMeshes) mesh.dispose();
    for (const texture of this.mistTextures ?? []) texture.dispose();
    this.mistMesh?.dispose();
    this.fogVolumeMesh?.dispose();
    this.batMesh?.dispose();
    this.batTexture?.dispose();
    for (const sprite of this.batSprites ?? []) sprite.texture.dispose();
    this.renderer.dispose();
    this.renderer.forceContextLoss();
  }
}

/**
 * A damped spring (like iOS's) sampled into a CSS `linear()` easing: quick
 * launch, one small overshoot, settle. Computed once and cached.
 */
const IMMERSIVE_TRANSITION_MS = 700;
let cachedSpring = null;
function springEasing() {
  if (cachedSpring) return cachedSpring;
  const omega = 2 * Math.PI * 1.6; // natural frequency
  const zeta = 0.62; // damping ratio (<1 overshoots)
  const wd = omega * Math.sqrt(1 - zeta * zeta);
  const points = [];
  const samples = 40;
  for (let i = 0; i <= samples; i += 1) {
    const t = (i / samples) * 0.9;
    const value = 1 - Math.exp(-zeta * omega * t)
      * (Math.cos(wd * t) + (zeta * omega / wd) * Math.sin(wd * t));
    points.push(i === samples ? 1 : value.toFixed(4));
  }
  cachedSpring = `linear(${points.join(', ')})`;
  return cachedSpring;
}

if (!customElements.get('runner-game-component')) {
  /**
   * The item on a locked character's carousel card ("600 x banana"), drawn the way the pickups
   * are in the game: the same art on a thin 3D body (the banana extruded from its traced
   * outline, the packs as a thin box with coloured edges), same materials and glow, turning like
   * a turntable. One shared offscreen renderer draws every card's item and copies the result
   * into that card's own 2D canvas, so any number of cards costs a single WebGL context.
   * Anything that goes wrong (no WebGL, unknown art) leaves the card's flat image showing.
   */
  const ITEM_ICON_SPIN_SPEED = 1.4; // radians/second; the in-game pickups turn at 4
  const ITEM_ICON_DEPTH_FRACTION = 0.035; // packs; the game uses PICKUP_DEPTH_FRACTION
  const ITEM_ICON_BANANA_DEPTH_FRACTION = 0.06; // the game uses BANANA_DEPTH_FRACTION
  const ITEM_ICON_TILT = 0.2; // radians the item leans back so its top edge catches the light
  const ITEM_ICON_SIZE = 128;

  /**
   * Traces the outline of an image's opaque area (a 64px-wide copy, simplified) as a closed polygon
   * in unit space (-0.5..0.5, y up), plus the average colour of that area. Used to give art that
   * isn't one of the known pickups a body that follows its real shape, like the banana's.
   */
  function traceArtOutline(image) {
    const size = 64;
    const aspect = image.width / image.height;
    const w = aspect >= 1 ? size : Math.max(8, Math.round(size * aspect));
    const h = aspect >= 1 ? Math.max(8, Math.round(size / aspect)) : size;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(image, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);
    const solid = (x, y) => x >= 0 && y >= 0 && x < w && y < h && data[(y * w + x) * 4 + 3] > 100;
    let r = 0, g = 0, b = 0, n = 0;
    for (let i = 0; i < w * h; i++) {
      if (data[i * 4 + 3] > 200) {
        r += data[i * 4];
        g += data[i * 4 + 1];
        b += data[i * 4 + 2];
        n++;
      }
    }
    if (n === 0) return null;
    // Boundary of the first solid pixel (scanning top-left) by square tracing on pixel corners.
    let start = null;
    for (let y = 0; y < h && !start; y++) for (let x = 0; x < w; x++) if (solid(x, y)) { start = [x, y]; break; }
    // Moore-neighbour trace over solid pixels.
    const dirs = [[1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1]];
    const points = [];
    let [cx, cy] = start;
    let back = 4; // we entered from the west (pixel to the left is empty)
    const startKey = `${cx},${cy}`;
    for (let guard = 0; guard < w * h * 4; guard++) {
      points.push([cx + 0.5, cy + 0.5]);
      let found = false;
      for (let k = 1; k <= 8; k++) {
        const d = (back + k) % 8;
        const nx = cx + dirs[d][0];
        const ny = cy + dirs[d][1];
        if (solid(nx, ny)) {
          back = (d + 4 + 1) % 8; // continue the sweep just after where we came from
          cx = nx;
          cy = ny;
          found = true;
          break;
        }
      }
      if (!found || (`${cx},${cy}` === startKey && points.length > 2)) break;
    }
    if (points.length < 6) return null;
    // Ramer-Douglas-Peucker simplification (closed path treated as open; it ends where it began).
    const simplify = (pts, eps) => {
      const keep = new Array(pts.length).fill(false);
      keep[0] = keep[pts.length - 1] = true;
      const stack = [[0, pts.length - 1]];
      while (stack.length) {
        const [lo, hi] = stack.pop();
        const [ax, ay] = pts[lo];
        const [bx, by] = pts[hi];
        const len = Math.hypot(bx - ax, by - ay) || 1;
        let far = -1;
        let farDist = eps;
        for (let i = lo + 1; i < hi; i++) {
          const dist = Math.abs((by - ay) * pts[i][0] - (bx - ax) * pts[i][1] + bx * ay - by * ax) / len;
          if (dist > farDist) { far = i; farDist = dist; }
        }
        if (far >= 0) { keep[far] = true; stack.push([lo, far], [far, hi]); }
      }
      return pts.filter((_, i) => keep[i]);
    };
    const outline = simplify(points, 0.9).map(([x, y]) => [x / w - 0.5, 0.5 - y / h]);
    return { outline, color: new THREE.Color(r / n / 255, g / n / 255, b / n / 255), aspect };
  }

  const itemIcons = {
    entries: new Set(),
    renderer: null,
    scene: null,
    camera: null,
    textures: new Map(),
    shared: null,
    frame: null,
    lastTime: 0,
    failed: false,

    /** Shared geometry/lights/renderer, built on first use. */
    setup() {
      if (this.renderer || this.failed) return !this.failed;
      try {
        const canvas = document.createElement('canvas');
        const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, preserveDrawingBuffer: true });
        renderer.setPixelRatio(1);
        renderer.setSize(ITEM_ICON_SIZE, ITEM_ICON_SIZE, false);
        renderer.setClearColor(0x000000, 0);
        const scene = new THREE.Scene();
        scene.add(new THREE.AmbientLight(AMBIENT_LIGHT_COLOR, AMBIENT_LIGHT_INTENSITY));
        const sun = new THREE.DirectionalLight(SUN_LIGHT_COLOR, SUN_LIGHT_INTENSITY);
        sun.position.set(...SUN_LIGHT_POSITION);
        const back = new THREE.DirectionalLight(BACK_LIGHT_COLOR, BACK_LIGHT_INTENSITY);
        back.position.set(...BACK_LIGHT_POSITION);
        scene.add(sun, back);
        const camera = new THREE.PerspectiveCamera(26, 1, 0.1, 20);
        camera.position.set(0, 0.18, 3.4);
        camera.lookAt(0, 0, 0);

        const bananaShape = new THREE.Shape();
        BANANA_SHAPE_POINTS.forEach(([x, y], i) => (i === 0 ? bananaShape.moveTo(x, y) : bananaShape.lineTo(x, y)));
        bananaShape.closePath();
        const bananaGeometry = new THREE.ExtrudeGeometry(bananaShape, {
          depth: 1,
          bevelEnabled: false,
          UVGenerator: BANANA_UV_GENERATOR,
        });
        bananaGeometry.translate(0, 0, -0.5);
        const boxGeometry = new THREE.BoxGeometry(1, 1, 1);
        boxGeometry.clearGroups();
        boxGeometry.addGroup(0, 24, 0);
        boxGeometry.addGroup(24, 12, 1);

        this.renderer = renderer;
        this.scene = scene;
        this.camera = camera;
        this.shared = { bananaGeometry, boxGeometry };
        return true;
      } catch {
        this.failed = true;
        this.renderer = null;
        return false;
      }
    },

    texture(url) {
      let texture = this.textures.get(url);
      if (!texture) {
        texture = new THREE.Texture();
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.magFilter = THREE.NearestFilter;
        texture.minFilter = THREE.LinearFilter;
        this.textures.set(url, texture);
        const useImage = (image) => {
          texture.image = image;
          texture.needsUpdate = true;
          this.start();
        };
        const plain = () => {
          const image = new Image();
          image.crossOrigin = 'anonymous';
          image.onload = () => useImage(image);
          image.src = url;
        };
        // The same file is usually already on the page as a plain <img> (no CORS). Firefox then
        // reuses that cached copy for a CORS request, which makes the image unusable as a WebGL
        // texture: the face stays blank and only the item's edge shows. Fetching it explicitly
        // with CORS and using a same-origin blob avoids that.
        if (typeof fetch === 'function' && typeof URL !== 'undefined' && URL.createObjectURL) {
          fetch(url, { mode: 'cors', cache: 'reload' })
            .then((response) => {
              if (!response.ok) throw new Error('image fetch failed');
              return response.blob();
            })
            .then((blob) => {
              const image = new Image();
              image.onload = () => useImage(image);
              image.onerror = plain;
              image.src = URL.createObjectURL(blob);
            })
            .catch(plain);
        } else {
          plain();
        }
      }
      return texture;
    },

    /** Which in-game pickup an icon URL is (0 = JoJo 5-pack, 1 = 3-pack, 2 = banana), or -1. */
    variantFor(url) {
      const index = PICKUP_URLS.findIndex((pickup) => pickup === url);
      if (index >= 0) return index;
      const match = /runner-game-pickup-(\d)/.exec(url || '');
      return match ? Number(match[1]) - 1 : -1;
    },

    buildMesh(url) {
      const variant = this.variantFor(url);
      const texture = this.texture(url);
      const brightness = OBSTACLE_TARGET_LIGHT_LEVEL / lambertLightLevel([0, 0, 1]);
      const tint = new THREE.Color(brightness, brightness, brightness);
      const face = new THREE.MeshLambertMaterial({
        map: texture,
        color: tint,
        emissive: 0xffffff,
        emissiveMap: texture,
        emissiveIntensity: PICKUP_GLOW_INTENSITY,
        transparent: true,
        alphaTest: 0.1,
      });
      const edgeColor = variant >= 0 ? PICKUP_SIDE_COLORS[variant] : 0x555555;
      const edge = new THREE.MeshLambertMaterial({
        color: new THREE.Color(edgeColor).multiplyScalar(brightness),
        emissive: new THREE.Color(edgeColor).multiplyScalar(PICKUP_GLOW_INTENSITY),
      });
      const isBanana = variant === BANANA_VARIANT_INDEX;
      const mesh = new THREE.Mesh(
        isBanana ? this.shared.bananaGeometry : this.shared.boxGeometry,
        isBanana ? [face, edge] : [edge, face]
      );
      // Fit the longest side to the same size whatever the art's shape.
      const aspect = variant >= 0 ? PICKUP_ASPECTS[variant] : 1;
      const width = aspect >= 1 ? 1.2 : 1.2 * aspect;
      const height = aspect >= 1 ? 1.2 / aspect : 1.2;
      const depth = height * (isBanana ? ITEM_ICON_BANANA_DEPTH_FRACTION : ITEM_ICON_DEPTH_FRACTION);
      mesh.scale.set(width, height, depth);
      const holder = new THREE.Group();
      holder.add(mesh);
      holder.rotation.x = -ITEM_ICON_TILT;
      holder.visible = false;
      this.scene.add(holder);
      return { holder, mesh, materials: [face, edge], texture, brightness, variant, shaped: variant >= 0 };
    },

    /** Art that isn't one of the known pickups gets a body that follows its own outline, once it has loaded. */
    shapeEntry(entry) {
      const image = entry.texture.image;
      if (entry.shaped || !image || !image.width) return;
      entry.shaped = true;
      try {
        const traced = traceArtOutline(image);
        if (!traced) return;
        const shape = new THREE.Shape();
        traced.outline.forEach(([x, y], i) => (i === 0 ? shape.moveTo(x, y) : shape.lineTo(x, y)));
        shape.closePath();
        const geometry = new THREE.ExtrudeGeometry(shape, { depth: 1, bevelEnabled: false, UVGenerator: BANANA_UV_GENERATOR });
        geometry.translate(0, 0, -0.5);
        // The edge takes the art's own colour (a darker shade), so a gold item gets a gold edge.
        const edge = entry.materials[1];
        edge.color.copy(traced.color).multiplyScalar(0.55 * entry.brightness);
        edge.emissive.copy(traced.color).multiplyScalar(PICKUP_GLOW_INTENSITY * 0.6);
        entry.mesh.geometry = geometry;
        entry.mesh.material = [entry.materials[0], edge]; // caps carry the art, walls the edge
        const aspect = traced.aspect;
        const width = aspect >= 1 ? 1.2 : 1.2 * aspect;
        const height = aspect >= 1 ? 1.2 / aspect : 1.2;
        entry.mesh.scale.set(width, height, height * ITEM_ICON_BANANA_DEPTH_FRACTION);
      } catch {
        // keep the thin box
      }
    },

    /** Starts drawing `url`'s item into `canvas`; returns false if it can't (the flat image stays). */
    attach(canvas, url) {
      if (!this.setup()) return false;
      const existing = [...this.entries].find((entry) => entry.canvas === canvas);
      if (existing && existing.url === url) {
        this.start();
        return true;
      }
      if (existing) this.detach(canvas);
      const built = this.buildMesh(url);
      this.entries.add({ canvas, url, ctx: canvas.getContext('2d'), angle: Math.random() * Math.PI * 2, ...built });
      canvas.width = ITEM_ICON_SIZE;
      canvas.height = ITEM_ICON_SIZE;
      this.start();
      return true;
    },

    detach(canvas) {
      for (const entry of this.entries) {
        if (entry.canvas !== canvas) continue;
        this.scene?.remove(entry.holder);
        for (const material of entry.materials) material.dispose();
        this.entries.delete(entry);
      }
    },

    start() {
      if (this.frame !== null || this.entries.size === 0) return;
      this.lastTime = performance.now();
      this.frame = requestAnimationFrame((now) => this.tick(now));
    },

    tick(now) {
      this.frame = null;
      const dt = Math.min(0.1, (now - this.lastTime) / 1000);
      this.lastTime = now;
      let anyVisible = false;
      for (const entry of this.entries) {
        const { canvas } = entry;
        // Only cards that are on screen (the start screen is hidden during a run).
        if (!canvas.isConnected || canvas.offsetParent === null || document.hidden) continue;
        anyVisible = true;
        if (!entry.shaped) this.shapeEntry(entry);
        entry.angle += dt * ITEM_ICON_SPIN_SPEED;
        entry.mesh.rotation.y = entry.angle;
        for (const other of this.entries) other.holder.visible = other === entry;
        this.renderer.render(this.scene, this.camera);
        entry.ctx.clearRect(0, 0, canvas.width, canvas.height);
        entry.ctx.drawImage(this.renderer.domElement, 0, 0, canvas.width, canvas.height);
      }
      // Nothing showing: stop until a card is shown again (see `refreshCharacterLocks`).
      if (anyVisible) this.frame = requestAnimationFrame((time) => this.tick(time));
    },
  };

  class RunnerGameComponent extends HTMLElement {
    constructor() {
      super();
      this.canvas = this.querySelector('[data-runner-canvas]');
      this.startOverlay = this.querySelector('[data-runner-start]');
      this.gameOverOverlay = this.querySelector('[data-runner-gameover]');
      this.scoreEl = this.querySelector('[data-runner-score]');
      this.finalScoreEl = this.querySelector('[data-runner-final-score]');
      this.bestScoreEl = this.querySelector('[data-runner-best-score]');
      this.newHighScoreEl = this.querySelector('[data-runner-new-high]');
      this.startButton = this.querySelector('[data-runner-start-button]');
      this.playAgainButton = this.querySelector('[data-runner-play-again]');
      this.pauseButton = this.querySelector('[data-runner-pause]');
      this.pauseOverlay = this.querySelector('[data-runner-pause-overlay]');
      this.resumeButton = this.querySelector('[data-runner-resume]');
      this.characterButtons = Array.from(this.querySelectorAll('[data-runner-character]'));
      this.characterWheel = this.querySelector('[data-runner-character-wheel]');

      // Lifetime progress + character unlocks (kept in this device's local storage).
      this.charactersEl = this.querySelector('[data-runner-characters]');
      this.unlockedEl = this.querySelector('[data-runner-unlocked]');
      this.restoreEl = this.querySelector('[data-runner-restore]');
      this.collectedEl = this.querySelector('[data-runner-collected]');
      this.collectedList = this.querySelector('[data-runner-collected-list]');
      this.restoreOpenButton = this.querySelector('[data-runner-restore-open]');
      this.knownEl = this.querySelector('[data-runner-known]');
      this.knownText = this.querySelector('[data-runner-known-text]');
      this.knownForget = this.querySelector('[data-runner-known-forget]');
      this.restoreForm = this.querySelector('[data-runner-restore-form]');
      this.restoreEmail = this.querySelector('[data-runner-restore-email]');
      this.restoreStatus = this.querySelector('[data-runner-restore-status]');
      this.progressModule = null;
      this.progress = null;

      // Leaderboard UI (all optional -- the game works without it).
      this.submitForm = this.querySelector('[data-runner-submit-form]');
      this.rerollButton = this.querySelector('[data-runner-reroll]');
      this.notYouButton = this.querySelector('[data-runner-not-you]');
      this.welcomeEl = this.querySelector('[data-runner-welcome]');
      // Email-first flow: email -> (returning: welcome back) or (new: name, username, consent).
      this.returningEl = this.querySelector('[data-runner-returning]');
      this.detailsEl = this.querySelector('[data-runner-details]');
      this.emailInput = this.querySelector('[data-runner-email]');
      this.emailContinueButton = this.querySelector('[data-runner-email-continue]');
      this.fullnameInput = this.querySelector('[data-runner-fullname]');
      this.usernameInput = this.querySelector('[data-runner-username]');
      this.consentInput = this.querySelector('[data-runner-consent]');
      this.playerState = 'email';
      this.formStatusEl = this.querySelector('[data-runner-form-status]');
      this.submitButton = this.querySelector('[data-runner-submit]');
      // The board normally lives in the game-over card; on the contained page it
      // is a separate section elsewhere on the page, so fall back to the document.
      const boardQuery = (selector) => this.querySelector(selector) || document.querySelector(selector);
      this.leaderboardEl = boardQuery('[data-runner-leaderboard]');
      this.leaderboard = null;
      this.analytics = null;
      this.gameLoadedSent = false;
      // What the run that just ended collected, per item key (submitted with the score).
      this.runItems = {};
      this.trackerEl = this.querySelector('[data-runner-tracker]');
      this.trackerItems = {};
      this.activeViewIndex = 0;
      this.lastHighlight = null;
      // The score of the run that just ended -- the only score the form submits.
      this.runScore = 0;
      this.runScoreEl = this.querySelector('[data-runner-run-score]');
      this.openSubmitButton = this.querySelector('[data-runner-open-submit]');
      this.cityInput = this.querySelector('[data-runner-city]');
      this.cityListEl = this.querySelector('[data-runner-city-list]');
      this.cityList = null;
      this.cityMatches = [];
      this.cityActive = -1;
      this.submittedAlias = null;

      this.game = null;
      this.observer = null;
      this.isIntersecting = false;

      // Phone "immersive" mode: while a run is live the game frame goes fixed
      // and full-screen, the header hides and the page is scroll-locked.
      this.frame = this.closest('.runner-game-section');
      this.immersiveQuery = window.matchMedia('(max-width: 719px), (pointer: coarse) and (max-height: 500px)');
      this.immersive = false;
      this.immersiveScrollY = 0;
      this.immersiveTimer = null;
      this.handleImmersiveTouchMove = (event) => {
        if (event.cancelable) event.preventDefault();
      };

      this.handleStart = this.handleStart.bind(this);
      this.handlePlayAgain = this.handlePlayAgain.bind(this);
      this.handleTooScary = this.handleTooScary.bind(this);
      this.handleSpookyBack = this.handleSpookyBack.bind(this);
      this.handlePauseClick = () => this.game?.pause();
      this.handleResumeClick = async () => {
        await this.enterImmersive();
        this.game?.resume();
      };
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleCharacterClick = this.handleCharacterClick.bind(this);
      this.handleWheelScroll = this.handleWheelScroll.bind(this);
      this.handleWheelPointerDown = this.handleWheelPointerDown.bind(this);
      this.handleWheelPointerMove = this.handleWheelPointerMove.bind(this);
      this.handleWheelPointerEnd = this.handleWheelPointerEnd.bind(this);
      this.handleWheelClickCapture = this.handleWheelClickCapture.bind(this);
      this.wheelDrag = null;
      this.wheelScrollFrame = null;
      this.wheelAnimation = null;
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleReroll = this.handleReroll.bind(this);
      this.handleNotYou = this.handleNotYou.bind(this);
      this.handleRestoreOpen = this.handleRestoreOpen.bind(this);
      this.handleKnownForget = this.handleKnownForget.bind(this);
      this.handleRestoreSubmit = this.handleRestoreSubmit.bind(this);
      this.handleEmailContinue = this.handleEmailContinue.bind(this);
      this.handleEmailEdit = this.handleEmailEdit.bind(this);
      this.validateUsername = this.validateUsername.bind(this);
      this.handleOpenSubmit = this.handleOpenSubmit.bind(this);
      this.validateCity = this.validateCity.bind(this);
      this.handleCityInput = this.handleCityInput.bind(this);
      this.handleCityKeydown = this.handleCityKeydown.bind(this);
      this.handleCityBlur = this.handleCityBlur.bind(this);
      this.handleCityListPointerDown = this.handleCityListPointerDown.bind(this);
      this.handleCityListClick = this.handleCityListClick.bind(this);
    }

    connectedCallback() {
      if (!this.canvas) return;
      // The theme decides lights, palettes, props and some pacing, and the
      // game bakes those into textures and geometry as it builds -- so it has
      // to be resolved and applied before anything is constructed. If the
      // module can't be loaded the everyday look is already in place.
      this.applySeasonalTheme().finally(() => this.startGame());
    }

    async applySeasonalTheme() {
      const config = (typeof window !== 'undefined' && window.__runnerThemeConfig) || {};
      try {
        const { THEMES, resolveThemeId } = await import(moduleUrl('game-themes'));
        const search = typeof window !== 'undefined' ? window.location.search : '';
        const previewing = Boolean(new URLSearchParams(search).get('game-theme'));
        // Order: ?game-theme= preview, then this device's choice from the switcher, then the section's setting.
        const choice = readThemeChoice();
        const id = !previewing && THEMES[choice] ? choice : resolveThemeId({ setting: config.setting, search });
        applyTheme({ id, ...THEMES[id] }, config.assets ?? {});
        this.currentThemeId = id;
        this.buildThemeSwitcher(THEMES, id);
        // "Too scary?" while a spooky theme is on; "Back to Halloween" once the original was chosen.
        for (const button of this.querySelectorAll('[data-runner-too-scary]')) button.hidden = id === 'default';
        for (const button of this.querySelectorAll('[data-runner-spooky-back]')) {
          button.hidden = !(id === 'default' && choice === 'default' && THEMES.halloween);
        }
      } catch {
        // Keeps the everyday theme.
      }
    }

    startGame() {
      if (!this.isConnected || this.game) return;
      let storedCharacterId;
      try {
        storedCharacterId = window.localStorage.getItem(CHARACTER_STORAGE_KEY);
      } catch {
        storedCharacterId = null;
      }
      this.selectedCharacterId = CHARACTER_DEFS[storedCharacterId] ? storedCharacterId : DEFAULT_CHARACTER;
      this.centeredCharacterId = this.selectedCharacterId;
      this.syncCharacterButtons();
      this.loadProgressModule();

      this.game = new RunnerGame(
        this.canvas,
        {
          onPenalty: (points) => this.showPenalty(points),
          onPowerUp: (type, seconds) => this.showPowerUp(type, seconds),
          onScoreChange: (score) => {
            if (this.scoreEl) this.scoreEl.textContent = String(score);
          },
          onItemsChange: (items) => this.updateTracker(items),
          onGameOver: (score, best, isNewHigh, run) => this.handleGameOver(score, best, isNewHigh, run),
          onPauseChange: (paused) => this.showPaused(paused),
        },
        this.selectedCharacterId
      );

      this.startButton?.addEventListener('click', this.handleStart);
      this.playAgainButton?.addEventListener('click', this.handlePlayAgain);
      for (const button of this.querySelectorAll('[data-runner-too-scary]')) button.addEventListener('click', this.handleTooScary);
      for (const button of this.querySelectorAll('[data-runner-spooky-back]')) button.addEventListener('click', this.handleSpookyBack);
      this.pauseButton?.addEventListener('click', this.handlePauseClick);
      this.resumeButton?.addEventListener('click', this.handleResumeClick);
      this.submitForm?.addEventListener('submit', this.handleSubmit);
      this.rerollButton?.addEventListener('click', this.handleReroll);
      this.notYouButton?.addEventListener('click', this.handleNotYou);
      this.restoreOpenButton?.addEventListener('click', this.handleRestoreOpen);
      this.knownForget?.addEventListener('click', this.handleKnownForget);
      this.restoreForm?.addEventListener('submit', this.handleRestoreSubmit);
      this.emailContinueButton?.addEventListener('click', this.handleEmailContinue);
      this.emailInput?.addEventListener('input', this.handleEmailEdit);
      this.usernameInput?.addEventListener('input', this.validateUsername);
      this.openSubmitButton?.addEventListener('click', this.handleOpenSubmit);
      this.cityInput?.addEventListener('input', this.handleCityInput);
      this.cityInput?.addEventListener('focus', this.handleCityInput);
      this.cityInput?.addEventListener('keydown', this.handleCityKeydown);
      this.cityInput?.addEventListener('blur', this.handleCityBlur);
      // pointerdown is prevented so tapping an option doesn't blur (and close) the field first.
      this.cityListEl?.addEventListener('pointerdown', this.handleCityListPointerDown);
      this.cityListEl?.addEventListener('click', this.handleCityListClick);
      for (const button of this.characterButtons) {
        button.addEventListener('click', this.handleCharacterClick);
        // Tabbing through the wheel brings the focused character into view too.
        button.addEventListener('focus', this.handleCharacterClick);
      }
      this.characterWheel?.addEventListener('scroll', this.handleWheelScroll, { passive: true });
      this.characterWheel?.addEventListener('pointerdown', this.handleWheelPointerDown);
      this.characterWheel?.addEventListener('pointermove', this.handleWheelPointerMove);
      this.characterWheel?.addEventListener('pointerup', this.handleWheelPointerEnd);
      this.characterWheel?.addEventListener('pointercancel', this.handleWheelPointerEnd);
      this.characterWheel?.addEventListener('click', this.handleWheelClickCapture, true);
      // Open the wheel on the remembered character, once layout has widths.
      const initial = this.characterButtons.find((button) => button.dataset.runnerCharacter === this.selectedCharacterId);
      if (initial) requestAnimationFrame(() => this.centerCharacterButton(initial, false));

      try {
        window.localStorage.removeItem(LEGACY_PENDING_SCORE_KEY);
      } catch {
        // nothing to clean up
      }

      // Loaded dynamically so a leaderboard problem can never break the game itself.
      if (this.submitForm || this.leaderboardEl) {
        import(moduleUrl('leaderboard'))
          .then((module) => {
            if (!module.isLeaderboardConfigured()) return;
            this.leaderboard = module;
            // A board outside the game-over card is always visible: fill it now.
            if (this.getBoards().some((board) => !this.contains(board.el))) {
              this.renderTabs();
              this.loadLeaderboard();
            }
          })
          .catch(() => {});
      }
      // Same isolation for analytics: it can never break the game.
      import(moduleUrl('analytics'))
        .then((module) => {
          this.analytics = module;
        })
        .catch(() => {});
      this.buildTracker();
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            this.isIntersecting = entries[entries.length - 1].isIntersecting;
            if (this.isIntersecting) this.trackGameLoaded();
            this.syncActiveState();
          },
          { threshold: 0.1 }
        );
        this.observer.observe(this);
      } else {
        this.isIntersecting = true;
        this.trackGameLoaded();
        this.syncActiveState();
      }
    }

    disconnectedCallback() {
      this.startButton?.removeEventListener('click', this.handleStart);
      this.playAgainButton?.removeEventListener('click', this.handlePlayAgain);
      for (const button of this.querySelectorAll('[data-runner-too-scary]')) button.removeEventListener('click', this.handleTooScary);
      for (const button of this.querySelectorAll('[data-runner-spooky-back]')) button.removeEventListener('click', this.handleSpookyBack);
      this.pauseButton?.removeEventListener('click', this.handlePauseClick);
      this.resumeButton?.removeEventListener('click', this.handleResumeClick);
      this.submitForm?.removeEventListener('submit', this.handleSubmit);
      this.rerollButton?.removeEventListener('click', this.handleReroll);
      this.notYouButton?.removeEventListener('click', this.handleNotYou);
      this.restoreOpenButton?.removeEventListener('click', this.handleRestoreOpen);
      this.knownForget?.removeEventListener('click', this.handleKnownForget);
      this.restoreForm?.removeEventListener('submit', this.handleRestoreSubmit);
      this.emailContinueButton?.removeEventListener('click', this.handleEmailContinue);
      this.emailInput?.removeEventListener('input', this.handleEmailEdit);
      this.usernameInput?.removeEventListener('input', this.validateUsername);
      this.openSubmitButton?.removeEventListener('click', this.handleOpenSubmit);
      this.cityInput?.removeEventListener('input', this.handleCityInput);
      this.cityInput?.removeEventListener('focus', this.handleCityInput);
      this.cityInput?.removeEventListener('keydown', this.handleCityKeydown);
      this.cityInput?.removeEventListener('blur', this.handleCityBlur);
      this.cityListEl?.removeEventListener('pointerdown', this.handleCityListPointerDown);
      this.cityListEl?.removeEventListener('click', this.handleCityListClick);
      for (const button of this.characterButtons) {
        button.removeEventListener('click', this.handleCharacterClick);
        button.removeEventListener('focus', this.handleCharacterClick);
      }
      this.characterWheel?.removeEventListener('scroll', this.handleWheelScroll);
      this.characterWheel?.removeEventListener('pointerdown', this.handleWheelPointerDown);
      this.characterWheel?.removeEventListener('pointermove', this.handleWheelPointerMove);
      this.characterWheel?.removeEventListener('pointerup', this.handleWheelPointerEnd);
      this.characterWheel?.removeEventListener('pointercancel', this.handleWheelPointerEnd);
      this.characterWheel?.removeEventListener('click', this.handleWheelClickCapture, true);
      if (this.wheelScrollFrame !== null) cancelAnimationFrame(this.wheelScrollFrame);
      if (this.wheelAnimation !== null) cancelAnimationFrame(this.wheelAnimation);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.observer?.disconnect();
      this.observer = null;
      this.exitImmersive(true);
      this.game?.destroy();
      this.game = null;
    }

    /**
     * Phone immersive mode: the game frame goes position:fixed above the page
     * (z-index raised in CSS) and springs out from its slot to the screen
     * edges. The rest of the page is left exactly as it was; only touch
     * scrolling is blocked while a run is live.
     */
    enterImmersive() {
      const frame = this.frame;
      if (this.immersive) return this.immersivePromise;
      if (!frame || !this.immersiveQuery.matches) return Promise.resolve();
      clearTimeout(this.immersiveTimer);
      this.immersive = true;

      const rect = frame.getBoundingClientRect();
      // Fixed takes the frame out of the flow; keep its slot so the page doesn't jump.
      const holder = frame.parentElement;
      if (holder) holder.style.height = `${rect.height}px`;
      frame.style.setProperty('--runner-spring', springEasing());
      this.setFrameRect(rect);
      frame.classList.add('is-immersive');
      frame.classList.remove('is-immersive-active');
      void frame.offsetWidth;

      document.addEventListener('touchmove', this.handleImmersiveTouchMove, { passive: false });
      frame.classList.add('is-immersive-active');
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.immersivePromise = new Promise((resolve) => setTimeout(resolve, reduced ? 0 : IMMERSIVE_TRANSITION_MS));
      return this.immersivePromise;
    }

    /** Springs the frame back into its slot in the page and restores touch scrolling. */
    exitImmersive(immediate = false) {
      const frame = this.frame;
      if (!this.immersive || !frame) return;
      this.immersive = false;
      clearTimeout(this.immersiveTimer);
      document.removeEventListener('touchmove', this.handleImmersiveTouchMove);

      const holder = frame.parentElement;
      const finish = () => {
        frame.classList.remove('is-immersive', 'is-immersive-active');
        for (const side of ['top', 'right', 'bottom', 'left']) {
          frame.style.removeProperty(`--runner-clip-${side}`);
        }
        if (holder) holder.style.height = '';
      };
      if (immediate) {
        finish();
        return;
      }
      if (holder) this.setFrameRect(holder.getBoundingClientRect());
      frame.classList.remove('is-immersive-active');
      this.immersiveTimer = setTimeout(finish, 800);
    }

    /** Sets the clip inset (CSS vars) that makes the full-screen frame look like `rect`. */
    setFrameRect(rect) {
      const style = this.frame.style;
      const vw = document.documentElement.clientWidth;
      const vh = window.innerHeight;
      style.setProperty('--runner-clip-top', `${Math.max(0, rect.top)}px`);
      style.setProperty('--runner-clip-right', `${Math.max(0, vw - rect.right)}px`);
      style.setProperty('--runner-clip-bottom', `${Math.max(0, vh - rect.bottom)}px`);
      style.setProperty('--runner-clip-left', `${Math.max(0, rect.left)}px`);
    }

    syncActiveState() {
      this.game?.setActive(this.isIntersecting && document.visibilityState === 'visible');
    }

    handleVisibilityChange() {
      // Switching tabs or apps mid-run pauses it, so the player comes back to
      // the pause card instead of straight into an obstacle.
      if (document.visibilityState === 'hidden') this.game?.pause();
      // Back from another tab or device: pick up any progress saved there.
      else this.syncProgressFromServer();
      this.syncActiveState();
    }

    /** Shows or hides the pause card; the pause button is hidden while it is up. */
    showPaused(paused) {
      if (paused) this.pauseOverlay?.removeAttribute('hidden');
      else this.pauseOverlay?.setAttribute('hidden', '');
      if (this.pauseButton) this.pauseButton.hidden = paused;
      if (paused) this.resumeButton?.focus({ preventScroll: true });
      if (paused) this.exitImmersive();
      else this.enterImmersive();
    }

    async handleStart() {
      if (this.starting) return;
      this.starting = true;
      this.startOverlay?.setAttribute('hidden', '');
      this.pauseOverlay?.setAttribute('hidden', '');
      if (this.pauseButton) this.pauseButton.hidden = false;
      // Pressing Play proves the game is on screen. Without this, an
      // IntersectionObserver reading that lagged or flickered (iOS toolbar
      // collapse, resize) left the loop paused: overlay gone, nothing moving.
      // The observer still corrects it on its next change.
      this.isIntersecting = true;
      // Expand to full screen first; the run begins once the transition lands.
      // The server times the run; the score is checked against it on submit.
      this.runId = null;
      this.leaderboard?.startRun?.().then((id) => {
        this.runId = id;
      });
      await this.enterImmersive();
      this.starting = false;
      this.game?.start();
      this.syncActiveState();
      this.trackRunStart();
    }

    /** Back to the start card so the player can pick a different character before the next run. */
    /**
     * "Too scary": remember the choice and switch to the everyday theme straight away. The theme is
     * built into the game when the page loads, so this reloads the page.
     */
    handleTooScary() {
      chooseTheme('default');
    }

    /** Back to Halloween (after "too scary"). */
    handleSpookyBack() {
      chooseTheme('halloween');
    }

    /** The picker on the start card: one button per theme, the current one pressed. */
    buildThemeSwitcher(themes, currentId) {
      const wrap = this.querySelector('[data-runner-themes]');
      if (!wrap) return;
      const ids = Object.keys(themes);
      if (ids.length < 2) return;
      wrap.replaceChildren();
      for (const id of ids) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'runner-game__theme-option';
        button.dataset.theme = id;
        button.textContent = wrap.dataset[`label${id.charAt(0).toUpperCase()}${id.slice(1)}`] || themes[id].label || id;
        button.setAttribute('aria-pressed', String(id === currentId));
        button.addEventListener('click', () => {
          if (id !== currentId) chooseTheme(id);
        });
        wrap.append(button);
      }
      wrap.hidden = false;
    }

    handlePlayAgain() {
      this.gameOverOverlay?.setAttribute('hidden', '');
      this.startOverlay?.removeAttribute('hidden');
      itemIcons.start();
      // The carousel's turning items stop while the start screen is hidden; wake them.
      requestAnimationFrame(() => itemIcons.start());
      // Hiding the start screen resets the wheel's scroll; put it back on the chosen character.
      const selected = this.characterButtons.find(
        (button) => button.dataset.runnerCharacter === this.selectedCharacterId
      );
      if (selected) requestAnimationFrame(() => this.centerCharacterButton(selected, false));
    }

    /**
     * @param {MouseEvent} event
     */
    /** Clicking (or tabbing to) a character spins it to the middle of the wheel; the scroll handler does the selecting. */
    handleCharacterClick(event) {
      this.centerCharacterButton(event.currentTarget, true);
    }

    /**
     * Scrolls a character to the middle of the wheel. Positions come from
     * offsetLeft/offsetWidth, which are the untransformed layout box: the
     * wheel is the buttons' offset parent (it's positioned), and unlike
     * getBoundingClientRect these don't include the arc's own scaling, which
     * would otherwise feed back into the next measurement. Doesn't scroll the
     * page the way scrollIntoView would.
     */
    centerCharacterButton(button, smooth) {
      const wheel = this.characterWheel;
      if (!wheel || !button) return;
      this.scrollWheelTo(button.offsetLeft + button.offsetWidth / 2 - wheel.clientWidth / 2, smooth);
    }

    /**
     * Scrolls the wheel, animating in JS rather than via
     * `scroll-behavior`/`scrollTo({behavior:'smooth'})`: those are silently
     * dropped in some environments (and can be swallowed by mandatory scroll
     * snapping), which would leave the wheel -- and so the selection -- stuck.
     */
    scrollWheelTo(target, smooth) {
      const wheel = this.characterWheel;
      if (!wheel) return;
      if (this.wheelAnimation !== null) {
        cancelAnimationFrame(this.wheelAnimation);
        this.wheelAnimation = null;
        wheel.style.scrollSnapType = '';
      }
      const max = wheel.scrollWidth - wheel.clientWidth;
      const to = Math.max(0, Math.min(max, target));
      const from = wheel.scrollLeft;
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      // A hidden page gets no animation frames, so an animation started there
      // would never advance and the wheel would sit stuck between characters.
      if (!smooth || reduceMotion || document.hidden || Math.abs(to - from) < 1) {
        wheel.scrollLeft = to;
        this.updateCenteredCharacter();
        return;
      }
      // Mandatory snapping re-snaps the wheel on every scroll we make, which
      // cancels an animation mid-flight; turn it off while we drive it, and
      // back on for the player's own swipes.
      wheel.style.scrollSnapType = 'none';
      const start = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - start) / WHEEL_SCROLL_DURATION_MS);
        const eased = 1 - Math.pow(1 - t, 3);
        wheel.scrollLeft = from + (to - from) * eased;
        if (t < 1) {
          this.wheelAnimation = requestAnimationFrame(step);
        } else {
          this.wheelAnimation = null;
          wheel.style.scrollSnapType = '';
          this.updateCenteredCharacter();
        }
      };
      this.wheelAnimation = requestAnimationFrame(step);
    }

    /**
     * Mouse drag: grab the wheel and pull it sideways. (Touch is left to the browser: it pans the
     * wheel sideways and the panel around it up and down.) On release the wheel glides to the
     * nearest character, nudged on by a flick.
     */
    handleWheelPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      this.wheelDrag = {
        id: event.pointerId,
        type: event.pointerType,
        x: event.clientX,
        y: event.clientY,
        startX: event.clientX,
        startY: event.clientY,
        lastT: performance.now(),
        velocity: 0,
        dragging: false,
      };
    }

    handleWheelPointerMove(event) {
      const drag = this.wheelDrag;
      const wheel = this.characterWheel;
      if (!drag || drag.id !== event.pointerId || !wheel) return;
      const dx = event.clientX - drag.x;
      if (!drag.dragging) {
        const moved = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY);
        if (moved < 6) return;
        drag.dragging = true;
        if (this.wheelAnimation !== null) {
          cancelAnimationFrame(this.wheelAnimation);
          this.wheelAnimation = null;
        }
        // Snapping would pull the wheel back on every step of the drag.
        wheel.style.scrollSnapType = 'none';
        try {
          wheel.setPointerCapture(event.pointerId);
        } catch {
          // The drag still works without capture while the pointer stays over the wheel.
        }
      }
      const step = -dx;
      wheel.scrollLeft += step;
      const now = performance.now();
      const dt = Math.max(1, now - drag.lastT);
      drag.velocity = drag.velocity * 0.6 + (step / dt) * 0.4;
      drag.lastT = now;
      drag.x = event.clientX;
      drag.y = event.clientY;
      event.preventDefault?.();
    }

    handleWheelPointerEnd(event) {
      const drag = this.wheelDrag;
      const wheel = this.characterWheel;
      if (!drag || drag.id !== event.pointerId) return;
      this.wheelDrag = null;
      if (!drag.dragging || !wheel) return;
      this.wheelDragged = true;
      setTimeout(() => {
        this.wheelDragged = false;
      }, 0);
      try {
        wheel.releasePointerCapture(event.pointerId);
      } catch {
        // already released
      }
      wheel.style.scrollSnapType = '';
      // Settle on the character nearest the middle, one further on after a quick flick.
      const viewCenter = wheel.scrollLeft + wheel.clientWidth / 2 + drag.velocity * 180;
      let nearest = null;
      let best = Infinity;
      for (const button of this.characterButtons) {
        const distance = Math.abs(button.offsetLeft + button.offsetWidth / 2 - viewCenter);
        if (distance < best) {
          best = distance;
          nearest = button;
        }
      }
      if (nearest) this.centerCharacterButton(nearest, true);
    }

    /** A drag that ends over a card must not also count as a tap on it. */
    handleWheelClickCapture(event) {
      if (!this.wheelDragged) return;
      event.stopPropagation();
      event.preventDefault();
    }

    handleWheelScroll() {
      // Coalesce the scroll stream into one update per frame -- except on a
      // hidden page, which gets no frames (see `scrollWheelTo`).
      if (document.hidden) {
        this.updateCenteredCharacter();
        return;
      }
      if (this.wheelScrollFrame !== null) return;
      this.wheelScrollFrame = requestAnimationFrame(() => {
        this.wheelScrollFrame = null;
        this.updateCenteredCharacter();
      });
    }

    /**
     * Whichever character sits nearest the middle of the wheel is the one
     * being chosen. Characters without a full sprite set yet can be spun to
     * and looked at, but don't become the runner -- they just hold Play
     * disabled until the player spins on to a playable one.
     */
    updateCenteredCharacter() {
      const wheel = this.characterWheel;
      // While the start screen is hidden (during a run, on the game-over card) the wheel has no
      // layout, so every character is "equally close" and the first one (Mika) would win and
      // overwrite the player's choice. Only judge a wheel that is actually on screen.
      if (!wheel || wheel.clientWidth === 0) return;
      const viewCenter = wheel.scrollLeft + wheel.clientWidth / 2;
      let centered = null;
      let closest = Infinity;
      for (const button of this.characterButtons) {
        const distance = Math.abs(button.offsetLeft + button.offsetWidth / 2 - viewCenter);
        if (distance < closest) {
          closest = distance;
          centered = button;
        }
      }
      this.layoutCharacterWheel(viewCenter);
      if (!centered) return;
      this.centeredCharacterId = centered.dataset.runnerCharacter;
      const isLocked = centered.dataset.runnerCharacterLocked === 'true';
      if (this.startButton) this.startButton.disabled = isLocked;
      if (!isLocked && this.centeredCharacterId !== this.selectedCharacterId) {
        this.selectCharacter(this.centeredCharacterId);
      }
      this.syncCharacterButtons();
    }

    /** Places every character on the arc according to how far it is from the middle. */
    layoutCharacterWheel(viewCenter) {
      for (const button of this.characterButtons) {
        const distance = (button.offsetLeft + button.offsetWidth / 2 - viewCenter) / button.offsetWidth;
        const clamped = Math.max(-2, Math.min(2, distance));
        const drop = WHEEL_ARC_DROP_PX * Math.min(1.6, clamped * clamped);
        const tilt = WHEEL_ARC_TILT_DEG * clamped;
        const scale = Math.max(0.7, 1 - WHEEL_ARC_SCALE_FALLOFF * Math.abs(clamped));
        button.style.transform = `translateY(${drop.toFixed(2)}px) rotate(${tilt.toFixed(2)}deg) scale(${scale.toFixed(3)})`;
        button.style.opacity = String(Math.max(0.45, 1 - WHEEL_ARC_FADE * Math.abs(clamped)));
      }
    }

    selectCharacter(characterId) {
      if (!CHARACTER_DEFS[characterId]) return;
      this.selectedCharacterId = characterId;
      this.game?.setCharacter(characterId);
      try {
        window.localStorage.setItem(CHARACTER_STORAGE_KEY, characterId);
      } catch {
        // Selection still works for this session even if persistence is blocked (e.g. private browsing).
      }
    }

    syncCharacterButtons() {
      for (const button of this.characterButtons) {
        const characterId = button.dataset.runnerCharacter;
        button.setAttribute('aria-pressed', String(characterId === this.selectedCharacterId));
        button.classList.toggle('is-centered', characterId === this.centeredCharacterId);
      }
    }

    /** HUD badge for the active gold-Jasmin benefit with a countdown; `type` null hides it. */
    showPowerUp(type, seconds) {
      const el = this.querySelector('[data-runner-powerup]');
      if (!el) return;
      if (!type) {
        el.hidden = true;
        return;
      }
      const label = el.dataset[`label${type[0].toUpperCase()}${type.slice(1)}`] || type;
      el.hidden = false;
      el.textContent = `${label} ${Math.ceil(seconds)}s`;
    }

    /** Briefly shows "-N" above the score after a lightning hit. */
    showPenalty(points) {
      const el = this.querySelector('[data-runner-penalty]');
      if (!el) return;
      el.textContent = `-${points}`;
      el.classList.remove('is-showing');
      void el.offsetWidth; // restart the animation
      el.classList.add('is-showing');
    }

    /**
     * @param {number} score
     * @param {number} best
     * @param {boolean} isNewHigh
     */
    handleGameOver(score, best, isNewHigh, run) {
      this.exitImmersive();
      if (this.pauseButton) this.pauseButton.hidden = true;
      this.runItems = run?.items || {};
      this.track('run_end', {
        score,
        items: this.runItems,
        seconds: run?.seconds,
        character: this.selectedCharacterId,
        new_best: isNewHigh,
      });
      if (this.finalScoreEl) this.finalScoreEl.textContent = String(score);
      if (this.bestScoreEl) this.bestScoreEl.textContent = String(best);
      if (this.newHighScoreEl) this.newHighScoreEl.hidden = !isNewHigh;
      this.gameOverOverlay?.removeAttribute('hidden');
      this.runScore = score;
      this.recordProgress(score, run);
      this.showLeaderboard();
    }

    // ---- Lifetime progress and character unlocks -------------------------------------------------

    get unlockRules() {
      // TESTING: with this on, only the characters in KEEP_LOCKED_WHILE_TESTING stay locked (the
      // rules in the templates are untouched). Set to false before going live.
      const rules = (window.__runnerLeaderboardConfig || {}).unlocks;
      const all = Array.isArray(rules) ? rules : [];
      return UNLOCK_ALL_CHARACTERS_FOR_TESTING ? all.filter((rule) => KEEP_LOCKED_WHILE_TESTING.includes(rule.character)) : all;
    }

    /** Loads the progress module and this device's saved progress, then updates the wheel. */
    async loadProgressModule() {
      if (this.progressReady) return this.progressReady;
      this.progressReady = (async () => {
        try {
          this.progressModule = await import(moduleUrl('progress'));
          this.progress = this.progressModule.loadProgress();
          // Unlocks are worked out from the totals, so rules added later apply to existing progress.
          const { progress } = this.progressModule.applyUnlocks(this.progress, this.unlockRules);
          this.progress = progress;
          this.refreshProgressUi();
          this.syncProgressFromServer();
        } catch {
          // Without progress everything behaves as before: no unlock rules are enforced.
          this.progressModule = null;
        }
      })();
      return this.progressReady;
    }

    /** Adds a finished run to the lifetime totals, unlocks anything newly earned, and tells the player. */
    async recordProgress(score, run) {
      await this.loadProgressModule();
      const mod = this.progressModule;
      if (!mod || !this.progress) return;
      const recorded = mod.recordRun(this.progress, { score, seconds: run?.seconds, items: run?.items });
      const { progress, newlyUnlocked } = mod.applyUnlocks(recorded, this.unlockRules);
      this.progress = progress;
      mod.saveProgress(progress);
      this.refreshProgressUi();

      if (this.unlockedEl) {
        const names = newlyUnlocked.map((rule) => this.characterName(rule.character));
        this.unlockedEl.hidden = names.length === 0;
        this.unlockedEl.replaceChildren();
        if (names.length) {
          // "New character unlocked: __NAME__!" -> the words as a small heading, the name large.
          const [before, after = ''] = (this.charactersEl?.dataset.msgUnlockedBanner || '__NAME__').split('__NAME__');
          const lead = document.createElement('span');
          lead.className = 'runner-game__unlocked-lead';
          lead.textContent = before.replace(/[:\s]+$/, '');
          const name = document.createElement('span');
          name.className = 'runner-game__unlocked-name';
          name.textContent = names.join(', ') + after.trim();
          this.unlockedEl.append(lead, name);
          // Replay the pop-in each time something new is unlocked.
          this.unlockedEl.classList.remove('is-popping');
          void this.unlockedEl.offsetWidth;
          this.unlockedEl.classList.add('is-popping');
        }
      }
      for (const rule of newlyUnlocked) this.track('unlock', { character: rule.character, goal: rule.type });

      // Players with an email get a backup copy, so a new device can load it back.
      const known = this.leaderboard?.getReturningPlayer?.();
      if (known?.email) this.backUpProgress(known.email);
    }

    /** Saves a copy of the lifetime totals under an email (fire-and-forget). */
    backUpProgress(email) {
      if (!email || !this.progressModule || !this.progress || !this.leaderboard?.saveProgressBackup) return;
      this.leaderboard.saveProgressBackup(email, this.progressModule.toSnapshot(this.progress));
    }

    characterName(characterId) {
      const button = this.characterButtons.find((b) => b.dataset.runnerCharacter === characterId);
      return button?.querySelector('.runner-game__character-name')?.textContent?.trim() || characterId;
    }

    /**
     * What a locked character needs, split for the sticker: the goal ("600"), what is collected
     * ("Bananer") and how much the player has so far ("100").
     */
    describeRule(rule, status) {
      const text = this.charactersEl?.dataset || {};
      const items = (window.__runnerLeaderboardConfig || {}).items || {};
      const unit = rule.type === 'item'
        ? items[rule.key]?.label || rule.key
        : rule.type === 'runs'
          ? text.msgRuns
          : text.msgScore;
      const format = new Intl.NumberFormat(document.documentElement.lang || 'da');
      return { goal: format.format(status.amount), unit: unit || '', have: format.format(status.current) };
    }

    /**
     * Applies unlock rules to the character wheel: a character is playable only if its artwork
     * exists (CHARACTER_DEFS) and it is either free or has been earned. Locked characters with a
     * rule show how far along the player is.
     */
    refreshCharacterLocks() {
      const mod = this.progressModule;
      if (!mod || !this.progress || this.characterButtons.length === 0) return;
      const rules = this.unlockRules;
      const text = this.charactersEl?.dataset || {};

      for (const button of this.characterButtons) {
        const id = button.dataset.runnerCharacter;
        // Remember the markup's own state once: "locked" there means no artwork yet ("coming soon").
        if (button.dataset.runnerBaseLocked === undefined) {
          button.dataset.runnerBaseLocked = String(button.dataset.runnerCharacterLocked === 'true');
        }
        const noArtYet = button.dataset.runnerBaseLocked === 'true';
        const rule = rules.find((r) => r.character === id);
        const earned = mod.isCharacterUnlocked(this.progress, rules, id);
        const locked = noArtYet || !earned;

        button.dataset.runnerCharacterLocked = String(locked);
        button.classList.toggle('runner-game__character-option--locked', locked);

        // Text over the portrait + a progress bar. How much colour the portrait gets back (and how
        // much it sharpens) follows how close the goal is: --unlock-progress, 0..1.
        let main = '';
        let unit = '';
        let have = '';
        let kind = 'text';
        let fraction = 1;
        let showBar = false;
        if (rule && !earned) {
          const status = mod.ruleProgress(this.progress, rule);
          const described = this.describeRule(rule, status);
          main = described.goal;
          unit = described.unit;
          have = described.have;
          kind = 'count';
          fraction = status.current / status.amount;
          showBar = true;
        } else if (noArtYet && rule && earned) {
          main = text.msgUnlockedSoon || '';
          showBar = true;
        } else if (noArtYet) {
          main = text.msgSoon || '';
          fraction = 0;
        }

        const overlay = button.querySelector('[data-runner-overlay]');
        if (overlay) {
          const mainEl = overlay.querySelector('[data-runner-overlay-main]');
          const unitEl = overlay.querySelector('[data-runner-overlay-unit]');
          const leadEl = overlay.querySelector('[data-runner-overlay-lead]');
          const haveEl = overlay.querySelector('[data-runner-overlay-have]');
          if (mainEl) mainEl.textContent = main;
          // "Lås op med" above the goal, and "Du har 100" below it, only when there is a goal.
          if (leadEl) {
            leadEl.textContent = kind === 'count' ? text.msgUnlockWith || '' : '';
            leadEl.hidden = leadEl.textContent === '';
          }
          if (haveEl) {
            // "DU HAR" small, the number large -- built as elements so each can be styled.
            haveEl.replaceChildren();
            if (have !== '') {
              const label = document.createElement('span');
              label.className = 'runner-game__character-overlay-have-label';
              label.textContent = text.msgYouHave || '';
              const number = document.createElement('span');
              number.className = 'runner-game__character-overlay-have-number';
              number.textContent = have;
              haveEl.append(label, number);
            }
            haveEl.hidden = have === '';
          }
          // The required item, spinning beside the count (item rules only).
          const iconEl = overlay.querySelector('[data-runner-overlay-icon]');
          if (iconEl) {
            const items = (window.__runnerLeaderboardConfig || {}).items || {};
            const wanted = rule && rule.type === 'item' && kind === 'count' ? String(rule.key || '').trim().toLowerCase() : '';
            const itemKey = wanted ? Object.keys(items).find((k) => k.toLowerCase() === wanted) : '';
            const icon = itemKey ? items[itemKey]?.icon : '';
            if (icon) iconEl.src = icon;
            const wrap = iconEl.closest('[data-runner-overlay-icon3d]');
            const canvas = wrap?.querySelector('[data-runner-overlay-canvas]');
            if (wrap && canvas) {
              wrap.hidden = !icon;
              wrap.dataset.item = itemKey || '';
              // The pickup-style 3D item when it can be drawn; the flat image otherwise.
              const drawn = Boolean(icon) && itemIcons.attach(canvas, icon);
              canvas.hidden = !drawn;
              iconEl.hidden = drawn || !icon;
              if (!icon) itemIcons.detach(canvas);
            } else {
              iconEl.hidden = !icon;
            }
          }
          if (unitEl) {
            // A word-joiner after each hyphen keeps "5-pak" together (no break at the hyphen).
            unitEl.textContent = unit.replace(/-/g, '-\u2060');
            unitEl.hidden = unit === '';
          }
          overlay.dataset.kind = kind;
          overlay.hidden = main === '';
        }
        const bar = button.querySelector('[data-runner-progress]');
        const fill = button.querySelector('[data-runner-progress-fill]');
        if (bar) {
          bar.hidden = !showBar;
          const percent = Math.round(Math.max(0, Math.min(1, fraction)) * 100);
          bar.setAttribute('aria-valuenow', String(percent));
          bar.setAttribute(
            'aria-label',
            [kind === 'count' ? text.msgUnlockWith : '', main, unit, have && `${text.msgYouHave || ''} ${have}`.trim()]
              .filter(Boolean)
              .join(' ')
          );
          bar.classList.toggle('is-near', showBar && percent >= 80 && percent < 100);
          // Judged on the real fraction, not the rounded percent: 2 of 600 is 0.3% (rounds to 0)
          // but is still progress and must show.
          bar.classList.toggle('has-progress', showBar && fraction > 0);
          // Any real progress shows at least a 6% sliver, so 2 of 600 doesn't look like 0.
          const shown = `${showBar && fraction > 0 ? Math.max(percent, 6) : percent}%`;
          if (fill) fill.style.setProperty('--fill', shown);
        }
        button.style.setProperty('--unlock-progress', String(Math.max(0, Math.min(1, fraction))));
      }

      // A remembered character that is locked on this device falls back to the default.
      const selected = this.characterButtons.find((b) => b.dataset.runnerCharacter === this.selectedCharacterId);
      if (selected && selected.dataset.runnerCharacterLocked === 'true' && this.selectedCharacterId !== DEFAULT_CHARACTER) {
        this.selectedCharacterId = DEFAULT_CHARACTER;
        this.game?.setCharacter?.(DEFAULT_CHARACTER);
      }
      this.updateCenteredCharacter();
    }

    /** Re-draws everything that depends on progress (wheel locks, the load-by-email link). */
    refreshProgressUi() {
      this.refreshCharacterLocks();
      this.refreshRestoreVisibility();
      this.refreshCollected();
      this.refreshKnownPlayer();
    }

    /**
     * "You've collected": the lifetime item totals (from this device's saved progress, which an
     * email load also fills in) as icon + count pills on the character page. Hidden until there's
     * at least one count, so a brand-new player sees nothing.
     */
    refreshCollected() {
      if (!this.collectedEl || !this.collectedList) return;
      const totals = this.progress?.totals?.items || {};
      const meta = (window.__runnerLeaderboardConfig || {}).items || {};
      const keys = Object.keys(meta).filter((key) => Number(totals[key]) > 0);
      // Nothing changed since the last draw: leave the chips (and their spin) alone.
      const signature = keys.map((key) => `${key}:${totals[key]}`).join('|');
      if (signature === this.collectedSignature) return;
      this.collectedSignature = signature;

      const format = new Intl.NumberFormat(document.documentElement.lang || 'da');
      // The old chips' spinners are released before the chips are replaced.
      this.collectedList.querySelectorAll('canvas').forEach((canvas) => itemIcons.detach(canvas));
      const spinners = [];
      this.collectedList.replaceChildren(
        ...keys.map((key, index) => {
          const label = meta[key].label || key;
          const count = format.format(totals[key]);
          const li = document.createElement('li');
          li.dataset.item = key;
          li.title = label;
          li.style.setProperty('--i', String(index));
          li.setAttribute('aria-label', `${label}: ${count}`);
          if (meta[key].icon) {
            const wrap = document.createElement('span');
            wrap.className = 'runner-game__collected-icon';
            wrap.setAttribute('aria-hidden', 'true');
            const canvas = document.createElement('canvas');
            canvas.hidden = true;
            const img = document.createElement('img');
            img.src = meta[key].icon;
            img.alt = '';
            wrap.append(canvas, img);
            li.append(wrap);
            spinners.push({ canvas, img, icon: meta[key].icon });
          }
          const number = document.createElement('span');
          number.className = 'runner-game__collected-count';
          number.textContent = count;
          li.append(number);
          return li;
        })
      );
      // The slowly turning 3D pickup when it can be drawn; the flat image otherwise.
      for (const { canvas, img, icon } of spinners) {
        const drawn = itemIcons.attach(canvas, icon);
        canvas.hidden = !drawn;
        img.hidden = drawn;
      }
      this.collectedEl.hidden = keys.length === 0;
    }

    /**
     * "Velkommen tilbage, <name>!" + "Ikke dig?" where the load-by-email link would be, for a
     * device that already knows the player's email. The name is the public username the server
     * has for that email; if it can't be fetched the greeting just goes without it.
     */
    async refreshKnownPlayer() {
      if (!this.knownEl || !this.knownText) return;
      try {
        if (!this.leaderboard) {
          const module = await import(moduleUrl('leaderboard'));
          if (module.isLeaderboardConfigured()) this.leaderboard = module;
        }
      } catch {
        // no leaderboard module: nobody can be "known"
      }
      const email = this.leaderboard?.getReturningPlayer?.()?.email;
      const text = this.knownEl.dataset;
      // No saved email, but this device has play on it: still "someone", so offer "Ikke dig?".
      const hasLocal = Boolean(this.progressModule && this.progress && this.progressModule.hasProgress(this.progress));
      this.knownEl.hidden = !email && !hasLocal;
      if (!email) {
        this.knownName = null;
        this.knownEmailShown = null;
        // "Velkommen tilbage" is only for a player who saved an email; local play gets just the link.
        this.knownText.textContent = '';
        return;
      }
      if (this.knownName === undefined || this.knownEmailShown !== email) {
        this.knownEmailShown = email;
        this.knownName = null;
        this.knownText.textContent = text.msgWelcomeShort || '';
        try {
          this.knownName = (await this.leaderboard.lookupPlayer(email)) || null;
        } catch {
          this.knownName = null;
        }
        // They may have pressed "Ikke dig?" while the name was loading.
        if (this.leaderboard?.getReturningPlayer?.()?.email !== email) return;
      }
      this.knownText.textContent = this.knownName
        ? (text.msgWelcome || '').replace('__NAME__', this.knownName)
        : text.msgWelcomeShort || '';
    }

    /** "Ikke dig?": forget the saved email, so the load-by-email link comes back and later runs aren't backed up under it. */
    handleKnownForget() {
      const mod = this.progressModule;
      // With no email to forget, "Ikke dig?" means wiping this device's saved play for the next player.
      if (!this.leaderboard?.getReturningPlayer?.()?.email && mod && this.progress && mod.hasProgress(this.progress)) {
        const question = this.knownEl?.dataset.msgForgetConfirm || '';
        if (question && !window.confirm(question)) return;
        this.progress = mod.emptyProgress();
        mod.saveProgress(this.progress);
        this.collectedSignature = null;
        this.refreshProgressUi();
        this.refreshRestoreVisibility();
        return;
      }
      this.leaderboard?.forgetPlayer?.();
      this.knownName = undefined;
      this.knownEmailShown = null;
      this.lastProgressSync = 0;
      this.refreshKnownPlayer();
      this.refreshRestoreVisibility();
    }

    /**
     * A player with a remembered email (saved with an earlier score) may have played on another
     * device since, so this asks the server for that email's totals and merges them in. The merge
     * only ever raises numbers (the higher of local and server for every counter, and the union of
     * unlocks), so the server can pull this device up but never down. Runs when the page loads and
     * whenever the tab comes back into view (at most every 30 seconds). Quiet by design: if
     * anything is missing or fails, the player just keeps what is stored here.
     */
    async syncProgressFromServer() {
      const mod = this.progressModule;
      if (!mod || !this.progress || this.progressSyncing) return;
      if (this.lastProgressSync && Date.now() - this.lastProgressSync < 30000) return;
      this.progressSyncing = true;
      try {
        if (!this.leaderboard) {
          const module = await import(moduleUrl('leaderboard'));
          if (module.isLeaderboardConfigured()) this.leaderboard = module;
        }
        const email = this.leaderboard?.getReturningPlayer?.()?.email;
        if (!email || !this.leaderboard?.fetchProgress) return;
        const saved = await this.leaderboard.fetchProgress(email);
        this.lastProgressSync = Date.now();
        if (!saved || !this.progress) return;
        // A run may have finished while the request was in flight; merge into whatever is current.
        const before = JSON.stringify(mod.toSnapshot(this.progress));
        const merged = mod.mergeProgress(this.progress, mod.normalizeProgress({ totals: saved }));
        const { progress } = mod.applyUnlocks(merged, this.unlockRules);
        const changed =
          JSON.stringify(mod.toSnapshot(progress)) !== before ||
          Object.keys(progress.unlocked).length !== Object.keys(this.progress.unlocked).length;
        // The "best score" on this device follows the server's if that is higher.
        const serverBest = Number(progress.totals.bestScore) || 0;
        try {
          if (serverBest > Number(window.localStorage.getItem(HIGH_SCORE_KEY) || 0)) {
            window.localStorage.setItem(HIGH_SCORE_KEY, String(serverBest));
          }
        } catch {
          // storage blocked: nothing to update
        }
        if (!changed) return;
        this.progress = progress;
        mod.saveProgress(progress);
        this.refreshProgressUi();
      } catch {
        // A convenience only; never let it affect the game.
      } finally {
        this.progressSyncing = false;
      }
    }

    /**
     * The "load your progress by email" link shows whenever this device doesn't already know the
     * player's email (then the welcome-back greeting takes its place), so it's also there once
     * items are showing. Loading merges by highest value, so it can't lower what's stored.
     */
    async refreshRestoreVisibility() {
      if (!this.restoreEl) return;
      const ready = Boolean(this.progressModule && this.progress);
      if (ready && !this.leaderboard) {
        try {
          const module = await import(moduleUrl('leaderboard'));
          if (module.isLeaderboardConfigured()) this.leaderboard = module;
        } catch {
          // no leaderboard module: the restore option can't work
        }
      }
      const knownEmail = this.leaderboard?.getReturningPlayer?.()?.email;
      // Loading is for a fresh device: once there is play here (or a known player) it is "Ikke dig?" instead.
      const hasLocal = ready && this.progressModule.hasProgress(this.progress);
      this.restoreEl.hidden = !(ready && this.leaderboard?.fetchProgress) || Boolean(knownEmail) || hasLocal;
    }

    handleRestoreOpen() {
      if (!this.restoreForm) return;
      this.restoreForm.hidden = !this.restoreForm.hidden;
      if (!this.restoreForm.hidden) this.restoreEmail?.focus();
    }

    /** Loads a player's saved totals by email, merges them in, and re-checks every unlock rule. */
    async handleRestoreSubmit(event) {
      event.preventDefault();
      const form = this.restoreForm;
      const mod = this.progressModule;
      if (!form || !mod || !this.leaderboard?.fetchProgress) return;
      if (!this.restoreEmail.reportValidity()) return;

      const email = this.restoreEmail.value.trim();
      this.setStatus(this.restoreStatus, form.dataset.msgLoading);
      let saved;
      try {
        saved = await this.leaderboard.fetchProgress(email);
      } catch (error) {
        console.error('Progress restore failed:', error);
        this.setStatus(this.restoreStatus, form.dataset.msgError, 'error');
        return;
      }
      if (!saved) {
        this.setStatus(this.restoreStatus, form.dataset.msgMissing, 'error');
        return;
      }
      const merged = mod.mergeProgress(this.progress, mod.normalizeProgress({ totals: saved }));
      const { progress } = mod.applyUnlocks(merged, this.unlockRules);
      this.progress = progress;
      mod.saveProgress(progress);
      // They've told us their email, so later runs are backed up under it too.
      this.leaderboard.rememberPlayer?.({ email });
      this.setStatus(this.restoreStatus, form.dataset.msgFound);
      this.refreshProgressUi();
      this.track('progress_restored', {});
    }

    /** Shows the submit form (once per run, score > 0) and loads the top list. */
    async showLeaderboard() {
      // The module import may still be in flight on a very fast first death.
      if (!this.leaderboard) {
        try {
          const module = await import(moduleUrl('leaderboard'));
          if (module.isLeaderboardConfigured()) this.leaderboard = module;
        } catch {
          return;
        }
      }
      if (!this.leaderboard) return;

      this.submittedAlias = null;
      this.lastHighlight = null;
      try {
        this.renderTabs();
      } catch {
        // Tabs are optional; the list and form must still appear.
      }
      if (this.submitForm) {
        // The form stays collapsed until the player chooses to submit.
        const canSubmit = this.runScore > 0;
        this.submitForm.hidden = true;
        if (this.openSubmitButton) this.openSubmitButton.hidden = !canSubmit;
        if (canSubmit) {
          this.submitForm.querySelectorAll('input, button').forEach((control) => {
            control.disabled = false;
          });
          this.submitForm.reset();
          this.syncPlayerIdentity();
          this.setStatus(this.formStatusEl, '');
          if (this.submitButton) this.submitButton.disabled = false;
        }
      }
      this.loadLeaderboard();
    }

    /**
     * Every leaderboard on the page. Normally one, in the game-over card; a page
     * can also carry a separate "Game leaderboard" section, and all of them are
     * kept in step.
     */
    getBoards() {
      return [...document.querySelectorAll('[data-runner-leaderboard]')].map((el) => ({
        el,
        list: el.querySelector('[data-runner-leaderboard-list]'),
        status: el.querySelector('[data-runner-leaderboard-status]'),
        tabs: el.querySelector('[data-runner-leaderboard-tabs]'),
      }));
    }

    /** Fetches and renders the top 10 of the active view. `highlight` marks the player's own row. */
    async loadLeaderboard(highlight = this.lastHighlight) {
      // Lets self-contained boards (the fg-game-page weekly/all-time cards) refresh too.
      document.dispatchEvent(new CustomEvent('runner:leaderboard-refresh'));
      const boards = this.getBoards();
      if (boards.length === 0 || !this.leaderboard) return;
      const views = this.leaderboard.getLeaderboardViews?.() ?? [{ id: 'score', label: '', metric: 'score' }];
      const view = views[this.activeViewIndex] || views[0];
      const itemMeta = this.leaderboard.getItemMeta?.() ?? {};
      const showItems = Boolean(this.leaderboard.shouldShowItems?.());
      const showCity = Boolean((window.__runnerLeaderboardConfig || {}).showCity);
      for (const board of boards) {
        board.el.hidden = false;
        this.setStatus(board.status, board.el.dataset.msgLoading);
        board.list?.replaceChildren();
      }

      try {
        const rows = await this.leaderboard.fetchTopScores(10, view.metric, view.theme, view.period || '');
        // A newer tab click may have replaced this view while the request was in flight.
        if ((views[this.activeViewIndex] || views[0]) !== view) return;

        for (const board of boards) {
          if (!board.list) continue;
          if (rows.length === 0) {
            this.setStatus(board.status, board.el.dataset.msgEmpty);
            continue;
          }
          this.setStatus(board.status, '');
          let highlighted = false;
          rows.forEach((row, index) => {
            const item = document.createElement('li');
            const rank = document.createElement('span');
            rank.className = 'runner-game__rank';
            rank.textContent = String(index + 1);
            const name = document.createElement('span');
            name.className = 'runner-game__entry-name';
            const nameText = document.createElement('span');
            // textContent only: names come from a public table anyone can write to.
            nameText.textContent = row.public_name;
            name.append(nameText);
            // Admin-assigned tags (YouTuber, Medarbejder, "Uge 41 vinder", ...) under the name.
            const tagPills = this.leaderboard.createTagElements?.(row.tags);
            if (tagPills) name.append(tagPills);
            // City is only shown when the player's city is being asked for at all
            // (section setting "Ask for the player's city"; off by default).
            if (showCity && row.city) {
              const city = document.createElement('span');
              city.className = 'runner-game__entry-city';
              city.textContent = row.city;
              name.append(city);
            }

            // What this run collected, for item keys the theme knows about.
            const chips = showItems ? this.buildItemChips(row.items, itemMeta) : null;
            if (chips) name.append(chips);

            const score = document.createElement('span');
            score.className = 'runner-game__entry-score';
            score.textContent = String(row.metric_value ?? row.high_score);
            item.append(rank, name, score);
            if (
              highlight
              && !highlighted
              && row.public_name === highlight.name
              && row.high_score === highlight.score
            ) {
              item.dataset.you = '';
              nameText.textContent = `${row.public_name} (${board.el.dataset.msgYou})`;
              highlighted = true;
            }
            board.list.append(item);
          });
        }
      } catch {
        for (const board of boards) this.setStatus(board.status, board.el.dataset.msgError, 'error');
      }
    }

    /**
     * Small icon + count chips for one leaderboard row. Only keys with a
     * "Tracked item" definition are shown; returns null if there is nothing to show.
     * @param {Record<string, number> | null} items
     * @param {Record<string, { label?: string, icon?: string }>} meta
     */
    buildItemChips(items, meta) {
      if (!items || typeof items !== 'object') return null;
      const wrap = document.createElement('span');
      wrap.className = 'runner-game__entry-items';
      for (const [key, info] of Object.entries(meta)) {
        const count = Number(items[key]);
        if (!count) continue;
        const chip = document.createElement('span');
        chip.title = info.label || key;
        chip.setAttribute('aria-label', `${info.label || key}: ${count}`);
        if (info.icon) {
          const img = document.createElement('img');
          img.src = info.icon;
          img.alt = '';
          img.loading = 'lazy';
          chip.append(img);
        } else {
          chip.append(document.createTextNode(`${info.label || key} `));
        }
        chip.append(document.createTextNode(String(count)));
        wrap.append(chip);
      }
      return wrap.childElementCount > 0 ? wrap : null;
    }

    /** Leaderboard tabs (one per configured view) on every board; hidden when there is only one. */
    renderTabs() {
      if (!this.leaderboard) return;
      const views = this.leaderboard.getLeaderboardViews();
      if (this.activeViewIndex >= views.length) this.activeViewIndex = 0;
      for (const board of this.getBoards()) {
        if (!board.tabs) continue;
        board.tabs.hidden = views.length < 2;
        board.tabs.replaceChildren();
        const fallbackLabel = board.el.querySelector('h2, h3')?.textContent || '';
        views.forEach((view, index) => {
          const tab = document.createElement('button');
          tab.type = 'button';
          tab.className = 'runner-game__tab';
          tab.setAttribute('role', 'tab');
          tab.setAttribute('aria-selected', String(index === this.activeViewIndex));
          tab.textContent = view.label || fallbackLabel;
          tab.addEventListener('click', () => {
            if (index === this.activeViewIndex) return;
            this.activeViewIndex = index;
            this.renderTabs();
            this.loadLeaderboard();
          });
          board.tabs.append(tab);
        });
      }
    }

    /** Builds the HUD item tracker from the configured item registry (no-op when it's switched off). */
    buildTracker() {
      const el = this.trackerEl;
      if (!el) return;
      const meta = (window.__runnerLeaderboardConfig || {}).items || {};
      this.trackerItems = {};
      el.replaceChildren();
      for (const [key, info] of Object.entries(meta)) {
        if (info.tracker === false) continue;
        const li = document.createElement('li');
        li.dataset.zero = '';
        li.title = info.label || key;
        if (info.icon) {
          const img = document.createElement('img');
          img.src = info.icon;
          img.alt = '';
          li.append(img);
        } else {
          li.append(document.createTextNode(`${info.label || key} `));
        }
        const count = document.createElement('span');
        count.textContent = '0';
        li.append(count);
        li.setAttribute('aria-label', `${info.label || key}: 0`);
        el.append(li);
        this.trackerItems[key] = { li, count, label: info.label || key };
      }
    }

    /** @param {Record<string, number>} items per-key counts for the current run */
    updateTracker(items) {
      for (const [key, entry] of Object.entries(this.trackerItems)) {
        const count = Number(items?.[key]) || 0;
        entry.count.textContent = String(count);
        if (count > 0) delete entry.li.dataset.zero;
        else entry.li.dataset.zero = '';
        entry.li.setAttribute('aria-label', `${entry.label}: ${count}`);
      }
    }

    /** Fire-and-forget analytics event; loads the module on first use if it isn't in yet. */
    async track(event, props) {
      try {
        if (!this.analytics) this.analytics = await import(moduleUrl('analytics'));
        this.analytics.setGameTheme(ACTIVE_THEME_ID);
        this.analytics.trackEvent(event, props);
      } catch {
        // analytics must never affect the game
      }
    }

    trackGameLoaded() {
      if (this.gameLoadedSent) return;
      this.gameLoadedSent = true;
      this.track('game_loaded', { character: this.selectedCharacterId });
    }

    /** Each run gets its own run id; every run after the first in a visit is flagged as a rerun. */
    async trackRunStart() {
      try {
        if (!this.analytics) this.analytics = await import(moduleUrl('analytics'));
        this.analytics.setGameTheme(ACTIVE_THEME_ID);
        const { runNumber, rerun } = this.analytics.startRun();
        this.analytics.trackEvent('run_start', {
          character: this.selectedCharacterId,
          rerun,
          run_number: runNumber,
        });
      } catch {
        // analytics must never affect the game
      }
    }

    /**
     * Puts the form on step 1 (email only) for a fresh game over, prefilled with
     * the email of a player who has submitted before on this device.
     */
    syncPlayerIdentity() {
      this.resetFormSteps();
      if (this.runScoreEl) this.runScoreEl.textContent = String(this.runScore);
      const returning = this.leaderboard.getReturningPlayer();
      if (returning && this.emailInput) this.emailInput.value = returning.email;
    }

    /** Back to step 1: just the email field and Continue. */
    resetFormSteps() {
      this.playerState = 'email';
      if (this.detailsEl) this.detailsEl.hidden = true;
      if (this.returningEl) this.returningEl.hidden = true;
      if (this.emailContinueButton) {
        this.emailContinueButton.hidden = false;
        this.emailContinueButton.disabled = false;
      }
      if (this.submitButton) this.submitButton.hidden = true;
      this.setRequired(false);
      this.setStatus(this.formStatusEl, '');
    }

    /** The detail fields are only required once they're visible (hidden required fields block submit). */
    setRequired(on) {
      for (const input of [this.fullnameInput, this.usernameInput, this.consentInput, this.cityInput]) {
        if (input) input.required = on;
      }
    }

    /** Loads the word list lazily (it is only needed once the username field is visible). */
    async loadBlockedWords() {
      if (this.blockedWords) return;
      try {
        this.blockedWords = await import(moduleUrl('blocked-words'));
      } catch {
        // Without it the database check still applies.
        this.blockedWords = null;
      }
    }

    /**
     * First of two username checks (English + Danish word list): instant feedback in
     * the form. The database repeats the same check and is the one that enforces it.
     * @returns {boolean} false if the username is blocked
     */
    validateUsername() {
      const input = this.usernameInput;
      if (!input) return true;
      const blocked = Boolean(this.blockedWords?.isBlockedUsername(input.value));
      input.setCustomValidity(blocked ? this.submitForm?.dataset.msgBlocked || 'Not allowed' : '');
      return !blocked;
    }

    /** Changing the email after a lookup starts the flow over, so a stale result can't be submitted. */
    handleEmailEdit() {
      if (this.playerState !== 'email') this.resetFormSteps();
    }

    /**
     * Step 1 -> 2: looks the email up. A known email gets "welcome back" under their
     * username and skips the details; a new one reveals name, username and consent.
     */
    async handleEmailContinue() {
      const input = this.emailInput;
      const form = this.submitForm;
      if (!input || !form || !this.leaderboard || this.playerState !== 'email') return;
      if (!input.reportValidity()) return;

      if (this.emailContinueButton) this.emailContinueButton.disabled = true;
      this.setStatus(this.formStatusEl, form.dataset.msgChecking);
      let knownName;
      try {
        knownName = await this.leaderboard.lookupPlayer(input.value);
      } catch (error) {
        console.error('Leaderboard email lookup failed:', error);
        this.setStatus(this.formStatusEl, form.dataset.msgEmailError, 'error');
        if (this.emailContinueButton) this.emailContinueButton.disabled = false;
        return;
      }
      this.setStatus(this.formStatusEl, '');
      if (this.emailContinueButton) this.emailContinueButton.hidden = true;
      if (this.submitButton) {
        this.submitButton.hidden = false;
        this.submitButton.disabled = false;
      }

      if (knownName) {
        this.playerState = 'returning';
        if (this.detailsEl) this.detailsEl.hidden = true;
        this.setRequired(false);
        if (this.returningEl) this.returningEl.hidden = false;
        if (this.welcomeEl) {
          this.welcomeEl.textContent = (this.welcomeEl.dataset.msgWelcome || '').replace('__NAME__', knownName);
        }
      } else {
        this.playerState = 'new';
        if (this.returningEl) this.returningEl.hidden = true;
        if (this.detailsEl) this.detailsEl.hidden = false;
        this.setRequired(true);
        this.loadBlockedWords();
        // The generated username stays as the default; the player can change it.
        if (this.usernameInput && !this.usernameInput.value) {
          this.usernameInput.value = this.leaderboard.getPublicName();
        }
        this.loadCities();
      }
    }

    /** Reveals the submit form (the game-over screen starts with just two choices). */
    async handleOpenSubmit() {
      if (!this.submitForm) return;
      this.submitForm.hidden = false;
      this.track('submit_open', { score: this.runScore });
      if (this.openSubmitButton) this.openSubmitButton.hidden = true;
      this.submitForm.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      // A player we remember from this device skips straight to "welcome back".
      if (this.emailInput?.value) this.handleEmailContinue();
    }

    /** Loads the allowed cities once; the list lives in its own module so the game bundle stays small. */
    async loadCities() {
      if (this.cityList || !this.cityInput) return;
      try {
        const module = await import(moduleUrl('cities'));
        this.cityList = [...module.RUNNER_CITIES, module.OTHER_CITY];
        // A saved city (returning player) is only kept if it is an allowed value.
        if (this.cityInput.value) this.validateCity();
      } catch {
        // Without the list the form can't be submitted (city must match it).
        this.cityList = null;
      }
    }

    /** "å" and "aa" are the same letter here (people type Århus; the official name is Aarhus). */
    normalizeCity(value) {
      return value.trim().toLocaleLowerCase('da').replaceAll('å', 'aa');
    }

    /** Opens the search list under the field with the cities matching what was typed. */
    async handleCityInput() {
      await this.loadCities();
      this.validateCity();
      const query = this.normalizeCity(this.cityInput.value);
      // Only list once they've typed something -- a 580-item list is noise on a phone.
      if (!this.cityList || !query) {
        this.closeCityList();
        return;
      }
      const starts = [];
      const contains = [];
      for (const name of this.cityList) {
        const position = this.normalizeCity(name).indexOf(query);
        if (position === 0) starts.push(name);
        else if (position > 0) contains.push(name);
      }
      this.cityMatches = [...starts, ...contains].slice(0, 40);
      this.cityActive = -1;
      this.renderCityList(query);
    }

    renderCityList(query) {
      const list = this.cityListEl;
      if (!list) return;
      list.replaceChildren();
      if (this.cityMatches.length === 0) {
        const empty = document.createElement('li');
        empty.className = 'runner-game__city-empty';
        empty.setAttribute('role', 'presentation');
        empty.textContent = this.cityInput.dataset.msgInvalid || '';
        list.append(empty);
      }
      this.cityMatches.forEach((name, index) => {
        const item = document.createElement('li');
        item.id = `${list.id}-${index}`;
        item.setAttribute('role', 'option');
        item.dataset.city = name;
        item.textContent = name;
        list.append(item);
      });
      list.hidden = false;
      this.cityInput.setAttribute('aria-expanded', 'true');
      // Keep the list in view above the phone keyboard.
      list.scrollIntoView({ block: 'nearest' });
    }

    closeCityList() {
      if (this.cityListEl) this.cityListEl.hidden = true;
      this.cityInput?.setAttribute('aria-expanded', 'false');
      this.cityInput?.removeAttribute('aria-activedescendant');
      this.cityActive = -1;
    }

    setCityActive(index) {
      const items = this.cityListEl ? [...this.cityListEl.querySelectorAll('[role="option"]')] : [];
      if (items.length === 0) return;
      this.cityActive = (index + items.length) % items.length;
      items.forEach((item, i) => item.setAttribute('aria-selected', String(i === this.cityActive)));
      const active = items[this.cityActive];
      this.cityInput.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }

    selectCity(name) {
      this.cityInput.value = name;
      this.validateCity();
      this.closeCityList();
    }

    handleCityKeydown(event) {
      const open = this.cityListEl && !this.cityListEl.hidden;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (open) this.setCityActive(this.cityActive + 1);
        else this.handleCityInput();
      } else if (event.key === 'ArrowUp' && open) {
        event.preventDefault();
        this.setCityActive(this.cityActive - 1);
      } else if (event.key === 'Enter' && open) {
        // Picks the highlighted (or only) match instead of submitting the form.
        const pick = this.cityMatches[this.cityActive >= 0 ? this.cityActive : 0];
        if (pick && (this.cityActive >= 0 || this.cityMatches.length === 1)) {
          event.preventDefault();
          this.selectCity(pick);
        } else {
          event.preventDefault();
        }
      } else if (event.key === 'Escape' && open) {
        event.preventDefault();
        this.closeCityList();
      }
    }

    handleCityBlur() {
      this.closeCityList();
      this.validateCity();
    }

    handleCityListPointerDown(event) {
      event.preventDefault();
    }

    handleCityListClick(event) {
      const item = event.target.closest('[role="option"]');
      if (item) this.selectCity(item.dataset.city);
    }

    /**
     * City must be one of the listed values: snaps a case-insensitive match to
     * its canonical spelling, otherwise marks the field invalid. The database
     * function enforces the same list, this is just the friendly version.
     * @returns {boolean}
     */
    validateCity() {
      const input = this.cityInput;
      if (!input || !this.cityList) return false;
      const typed = this.normalizeCity(input.value);
      const match = typed ? this.cityList.find((name) => this.normalizeCity(name) === typed) : null;
      if (match) {
        if (input.value !== match) input.value = match;
        input.setCustomValidity('');
        return true;
      }
      input.setCustomValidity(input.value.trim() ? input.dataset.msgInvalid || 'Invalid city' : '');
      return false;
    }

    handleReroll() {
      if (!this.leaderboard || !this.usernameInput) return;
      this.usernameInput.value = this.leaderboard.getPublicName(true);
      this.validateUsername();
    }

    /** "Not you?" -- someone else on this device; start over as a new player. */
    handleNotYou() {
      if (!this.leaderboard || !this.submitForm) return;
      this.leaderboard.forgetPlayer();
      this.submitForm.reset();
      this.resetFormSteps();
      this.emailInput?.focus();
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (!this.leaderboard || !this.submitForm || this.submittedAlias) return;
      // Enter in the email field is the same as pressing Continue.
      if (this.playerState === 'email') {
        this.handleEmailContinue();
        return;
      }
      const form = this.submitForm;
      if (this.cityInput) {
        await this.loadCities();
        this.validateCity();
      }
      if (this.playerState === 'new') {
        await this.loadBlockedWords();
        this.validateUsername();
      }
      if (!form.reportValidity()) return;

      const data = new FormData(form);
      const entry = {
        score: this.runScore,
        email: data.get('email'),
        items: this.runItems,
        gameTheme: ACTIVE_THEME_ID,
        runId: this.runId,
      };
      if (this.playerState === 'new') {
        Object.assign(entry, {
          fullnavn: data.get('fullnavn'),
          publicName: data.get('username'),
          city: this.cityInput ? data.get('city') : undefined,
          // The wording the player actually saw and agreed to, stored as proof of consent.
          consentText: form.querySelector('[data-runner-consent-text]')?.textContent?.trim() || '',
        });
      }

      if (this.submitButton) this.submitButton.disabled = true;
      this.setStatus(this.formStatusEl, form.dataset.msgSubmitting);
      let registeredName;
      try {
        registeredName = await this.leaderboard.submitScore(entry);
      } catch (error) {
        console.error('Leaderboard submit failed:', error);
        // The database's own word check can catch what the form's list didn't.
        const blocked = String(error?.message).includes('blocked username');
        this.setStatus(this.formStatusEl, blocked ? form.dataset.msgBlocked : form.dataset.msgError, 'error');
        if (this.submitButton) this.submitButton.disabled = false;
        return;
      }

      // Usernames are unique, so the saved one can differ from what was typed.
      if (this.usernameInput) this.usernameInput.value = registeredName;
      this.submittedAlias = registeredName;
      this.leaderboard.rememberPlayer(entry);
      this.refreshKnownPlayer();
      // New players agreed to the newsletter in the consent text; fire-and-forget.
      if (this.playerState === 'new') this.leaderboard.subscribeToNewsletter(entry);
      this.setStatus(this.formStatusEl, form.dataset.msgSubmitted, 'success');
      form.querySelectorAll('input, button').forEach((control) => {
        control.disabled = true;
      });
      this.track('submit_success', { score: entry.score });
      this.backUpProgress(entry.email);
      this.lastHighlight = { name: registeredName, score: entry.score };
      this.loadLeaderboard();
    }

    setStatus(element, message, state = '') {
      if (!element) return;
      element.textContent = message || '';
      if (state) element.dataset.state = state;
      else delete element.dataset.state;
    }
  }

  customElements.define('runner-game-component', RunnerGameComponent);
}


